// frontend/src/utils/shiftAutoAssign.js
// 月次シフト自動作成（ルールベース・確定的アルゴリズム）
//
// アルゴリズム:
//   Phase 1: 希望と固定パターンを matrix に書き込み、frozen Set に追加（絶対不変）
//   Phase 2: 事前検証 — 希望だけで固定人数を超える日があれば失敗報告
//   Phase 3: 日付ごとに固定人数パターンを埋める（埋まらなければ失敗）
//   Phase 4: 日付ごとに柔軟人数パターンを埋める（minRequired まで）
//   Phase 5: 残り空白に職種別「日勤」マークをデフォルト充填
//   Phase 6: 後続シフト連鎖（post_shift_ids）月跨ぎ DB 書き込み含む
//   Phase 7: 負荷分散スワップ — 希望以外のセル同士で同一パターン回数を均す
//   Phase 8: 検証・conflicts 構築

import dayjs from "dayjs";
import { supabase } from "../supabase";
import solver from "javascript-lp-solver";

// =============================================================================
// 定数
// =============================================================================

const WEEKDAY_KEYS = ["日", "月", "火", "水", "木", "金", "土"];
const OFF_MARK = "休";
const CHAIN_LIMIT = 14;

// =============================================================================
// 小ヘルパー
// =============================================================================

const safeArr = (v) => (Array.isArray(v) ? v : v ? [v] : []);
const keyOf = (empId, date) => `${empId}_${date}`;
const weekdayKey = (date) => WEEKDAY_KEYS[dayjs(date).day()];

// 日付別の柔軟性タイプ判定。
//   pattern.flexibilityByDayOfWeek に曜日上書きがあれば優先、なければデフォルト flexibilityType。
//   返り値: 'fixed' | 'flexible'
const getFlexibilityForDate = (pattern, date) => {
  if (!pattern) return 'flexible';
  const wd = weekdayKey(date);
  const byDay = pattern.flexibilityByDayOfWeek?.[wd];
  if (byDay === 'fixed' || byDay === 'flexible') return byDay;
  return pattern.flexibilityType || 'flexible';
};
const isFixedOnDate = (pattern, date) => getFlexibilityForDate(pattern, date) === 'fixed';

// 🔥 ブラウザの「ページ応答なし」ダイアログを防ぐため、長い処理の途中で
//    JS スレッドを開放して UI イベントを処理する機会を与える。
const yieldToBrowser = () => new Promise((resolve) => setTimeout(resolve, 0));

// 現状の matrix で連勤違反数とサンプルを集計するデバッグユーティリティ
//   各フェーズ後にこれを呼ぶことで「どこで連勤が生まれているか」を特定できる。
//   累計値ベース（寄与度合計）で判定。
const debugCountConsecutiveViolations = (state, phaseLabel) => {
  if (!state?.policy) return;
  const limit = state.policy.maxConsecutiveWorkDays;
  let violations = 0;
  const samples = [];
  Object.values(state.perStaff || {}).forEach((staff) => {
    if (staff.excludeFromAutoAssign) return;
    let sum = 0;
    let start = null;
    state.dates.forEach((date) => {
      const v = state.matrix[`${staff.employeeId}_${date}`];
      if (!v) { sum = 0; start = null; return; }
      const explicit = hasExplicitContribution(v, state.patterns, state.patternByMark);
      const isFollow = !explicit && isChainFollowMark(state, staff.employeeId, date);
      const c = isFollow ? 0 : getConsecutiveContribution(v, state.patterns, state.patternByMark);
      if (c <= 0) {
        sum = 0;
        start = null;
      } else {
        if (sum === 0) start = date;
        sum += c;
        if (sum > limit) {
          violations += 1;
          if (samples.length < 3) {
            samples.push(`${staff.employeeId} ${start}〜${date} (累計${sum.toFixed(1)})`);
          }
        }
      }
    });
  });
  console.log(
    `   📐 [${phaseLabel}] 連勤違反 ${violations} 件 (max=${limit})${samples.length ? ': ' + samples.join(' / ') : ''}`
  );
};

const isWeekendOrHoliday = (date, holidaySet) => {
  if (holidaySet.has(date)) return true;
  const d = dayjs(date).day();
  return d === 0 || d === 6;
};

// あるマークが連勤計算で「何日分」寄与するかを返す（数値）。
//   - 設定値 consecutiveContribution が明示されていれば最優先
//   - 未設定なら従来の自動判定: OFF / request_group ∈ {off,paid,summer} = 0、その他 = 1
//   - 0 を返した場合、連勤は切断される（休み相当）
//   - 0.5 を返した場合、半日勤務として 0.5 日分カウント
const REST_REQUEST_GROUPS = new Set(["off", "paid", "summer"]);
const getConsecutiveContribution = (mark, patterns, patternByMark) => {
  if (!mark) return 0;
  if (mark === OFF_MARK) return 0;
  const p =
    (patternByMark && patternByMark[mark]) ||
    (patterns || []).find((x) => x.displayMark === mark);
  if (!p) return 1; // 不明マークは安全側で 1 日扱い
  // 明示設定があればそれを最優先（0, 0.5, 1 など）
  const explicit = p.consecutiveContribution;
  if (explicit != null && Number.isFinite(Number(explicit))) {
    return Number(explicit);
  }
  // 自動判定: 休み系は 0、その他は 1
  if (REST_REQUEST_GROUPS.has(p.requestGroup)) return 0;
  return 1;
};

// ユーザーが寄与度を明示設定しているか。明示設定があるなら follow 判定で
// 0 上書きしてはならず、その値をそのまま使う（ユーザーの意思を尊重）。
const hasExplicitContribution = (mark, patterns, patternByMark) => {
  if (!mark || mark === OFF_MARK) return false;
  const p =
    (patternByMark && patternByMark[mark]) ||
    (patterns || []).find((x) => x.displayMark === mark);
  if (!p) return false;
  return p.consecutiveContribution != null && Number.isFinite(Number(p.consecutiveContribution));
};

// 後方互換のため isRestMark を残す（寄与度 0 = 連勤を切る相当）
const isRestMark = (mark, patterns, patternByMark) => {
  return getConsecutiveContribution(mark, patterns, patternByMark) === 0;
};

// シード付き整数ハッシュ（タイブレーク用）
const hash = (str, seed = 0) => {
  let h = seed | 0;
  for (let i = 0; i < (str?.length || 0); i++) {
    h = ((h << 5) - h + str.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
};

// seed から決定論的な疑似乱数（mulberry32）
const seededRandom = (seed) => {
  let a = (seed | 0) || 1;
  return () => {
    a |= 0;
    a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

// state.globalSeed に応じて日付順を変える
//   各 seed で異なる順序を試すことで、greedy で詰まる箇所が変わる
const getOrderedDates = (state) => {
  const seed = state.globalSeed || 0;
  const dates = [...state.dates];
  const mode = seed % 4;
  if (mode === 0) return dates; // forward
  if (mode === 1) return dates.reverse(); // backward
  if (mode === 2) {
    // shuffled
    const rnd = seededRandom(seed * 13 + 7);
    for (let i = dates.length - 1; i > 0; i--) {
      const j = Math.floor(rnd() * (i + 1));
      [dates[i], dates[j]] = [dates[j], dates[i]];
    }
    return dates;
  }
  // mode === 3: 中央から外側へ（重要な平均日から先に埋めて両端を後で調整）
  const sorted = [];
  let l = Math.floor(dates.length / 2);
  let r = l + 1;
  sorted.push(dates[l]);
  while (l > 0 || r < dates.length) {
    if (--l >= 0) sorted.push(dates[l]);
    if (r < dates.length) sorted.push(dates[r++]);
  }
  return sorted;
};

const extractMark = (hopeValue) => {
  if (!hopeValue) return null;
  if (typeof hopeValue === "string") return hopeValue.trim() || null;
  if (typeof hopeValue === "object") {
    return (
      hopeValue.value
      || hopeValue.mark
      || hopeValue.shift
      || hopeValue.selection
      || null
    );
  }
  return null;
};

// 希望の値（'off' / 'day' / 'night' / 'paid' / 'half' などのセマンティック種別）
// を実際の matrix に書き込むべき shift_pattern.displayMark に翻訳する。
//   旧 shiftAutoAssign.js の applyAdminHopes / applyStaffHopes が担っていたロジック。
//
// すでにマークとして有効（patternByMark にある）ならそのまま採用。
// ★ スタッフ職種に応じた「日勤マーク」を解決する共通ヘルパー
//   - 候補: request_group="day" / assignment_mode_enabled / 名前に「日勤」を含む（夜勤を除く）
//   - 優先: 職種マッチ → 職種制限なし → priority 小（=優先度高）
//   - 見つからなければ "" を返す（呼び出し側で null フォールバック）
const resolveDayShiftMark = (patterns, jobId) => {
  const candidates = (patterns || [])
    .filter(
      (p) =>
        !p.isChainOnlyPattern &&
        p.displayMark &&
        (
          p.requestGroup === "day" ||
          p.assignmentModeEnabled === true ||
          (p.name?.includes("日勤") && !p.name?.includes("夜勤"))
        )
    )
    .sort((a, b) => (a.priority ?? 99) - (b.priority ?? 99));
  if (candidates.length === 0) return "";
  const idStr = String(jobId || "");
  // 職種マッチを最優先
  const jobMatched = candidates.find(
    (p) => p.targetJobTypes?.length && p.targetJobTypes.includes(idStr)
  );
  if (jobMatched) return jobMatched.displayMark;
  // 職種制限なしのもの
  const noJobLimit = candidates.find((p) => !p.targetJobTypes?.length);
  if (noJobLimit) return noJobLimit.displayMark;
  // 最終フォールバックとして priority 最小のものを返す
  return candidates[0].displayMark;
};

const translateHopeToMark = (rawHope, state, staff) => {
  if (rawHope == null) return null;
  const value = String(rawHope).trim();
  if (!value) return null;
  // 既存パターンと一致するマーク（直接指定）
  if (state.patternByMark[value]) return value;
  const lower = value.toLowerCase();

  // ---- 日勤系の希望（"day"/"none"/"◯"/"○"）は専用ロジックで職種マッチを必須にする ----
  //   "看護師用" と "看護助手用" を取り違えないため、ここは request_group 検索より先に判定。
  if (lower === "none" || lower === "day" || lower === "◯" || value === "○") {
    const jobId = String(staff?.jobTypeId || staff?.job_type_id || "");
    const mark = resolveDayShiftMark(state.patterns, jobId)
      || state.dayShiftMarkByStaff?.[staff?.employeeId]
      || "";
    return mark || null;
  }

  // ---- 休み ----
  if (lower === "off" || lower === "休") return OFF_MARK;

  // ---- 汎用 request_group 検索（職種マッチ優先） ----
  //   提出時の値は基本的に request_group の ID（"morning" / "summer" 等）だが、
  //   過去データや手動入力で requestGroupLabel（「午前」「夏休み希望」等）が直接届く場合もカバーする。
  const groupCandidates = state.patterns
    .filter(
      (p) =>
        !p.isChainOnlyPattern &&
        (
          p.requestGroup === value
          || p.requestGroup === lower
          || (p.requestGroupLabel && (p.requestGroupLabel === value || p.requestGroupLabel === lower))
        )
    )
    .sort((a, b) => (a.priority ?? 99) - (b.priority ?? 99));
  if (groupCandidates.length > 0) {
    const jobId = String(staff?.jobTypeId || staff?.job_type_id || "");
    const jobMatched = groupCandidates.find(
      (p) => p.targetJobTypes?.length && p.targetJobTypes.includes(jobId)
    );
    const noJobLimit = groupCandidates.find((p) => !p.targetJobTypes?.length);
    const chosen = jobMatched || noJobLimit || groupCandidates[0];
    if (chosen?.displayMark) return chosen.displayMark;
  }

  // ---- 夜勤（夜勤希望は applyHopes 側で複数パターンに分散済みだが、念のため） ----
  if (lower === "night" || value === "/") {
    const night = state.patterns
      .filter(
        (p) =>
          !p.isChainOnlyPattern && (
            p.groupName?.includes("夜勤")
            || p.name?.includes("夜勤")
            || p.requestGroup === "night"
            || p.displayMark === "/"
            || p.displayMark?.startsWith("/")
          )
      )
      .sort((a, b) => a.priority - b.priority)[0];
    return night?.displayMark || null;
  }
  if (lower === "paid") {
    const paid = state.patterns.find(
      (p) => p.requestGroup === "paid" || p.name?.includes("有給")
    );
    return paid?.displayMark || null;
  }
  if (lower === "half") {
    const half = state.patterns.find(
      (p) => p.requestGroup === "half" || p.name?.includes("半休")
    );
    return half?.displayMark || null;
  }
  // 最終フォールバック: そのまま採用（不明な mark でも記録）
  return value;
};

// =============================================================================
// パターン正規化
// =============================================================================

const normalizePatterns = (rawPatterns) =>
  (rawPatterns || []).map((p) => {
    const cfg = p.configuration || {};
    return {
      id: p.id,
      name: p.name || p.displayMark || p.short_name || "",
      displayMark: p.displayMark || p.short_name || p.display_mark || "",
      priority: p.priority ?? 50,
      requiredByDayOfWeek:
        p.requiredByDayOfWeek
        || cfg.required_by_day_of_week
        || p.requiredCount
        || p.required_count
        || {},
      flexibilityType:
        p.flexibilityType ?? cfg.flexibility_type ?? "flexible",
      // 曜日ごとの上書き: 例 { 月: 'fixed', 火: 'flexible' }。未設定の曜日は flexibilityType を使う
      flexibilityByDayOfWeek:
        p.flexibilityByDayOfWeek
        ?? cfg.flexibility_by_day_of_week
        ?? cfg.flexibilityByDayOfWeek
        ?? {},
      minRequired: Number(p.minRequired ?? cfg.min_required ?? 0),
      forceAssign: p.forceAssign ?? cfg.force_assign ?? false,
      isChainOnlyPattern:
        p.isChainOnlyPattern ?? cfg.is_chain_only_pattern ?? false,
      maxConsecutive: p.maxConsecutive ?? cfg.max_consecutive ?? 6,
      maxSameConsecutive:
        p.maxSameConsecutive ?? cfg.max_same_consecutive ?? 3,
      holidayAdjustment:
        p.holidayAdjustment ?? cfg.holiday_adjustment ?? "default",
      holidayCustomCount:
        p.holidayCustomCount ?? cfg.holiday_custom_count ?? 0,
      maxMonthlyRequests:
        p.maxMonthlyRequests ?? cfg.max_monthly_requests ?? null,
      // 月毎の詳細上限（集計表示・将来的な制約強制用）
      holidayMonthlyLimitByMonth:
        cfg.holiday_monthly_limit_by_month
        && typeof cfg.holiday_monthly_limit_by_month === 'object'
          ? cfg.holiday_monthly_limit_by_month
          : null,
      maxMonthlyRequestsByGroup:
        p.maxMonthlyRequestsByGroup
        || cfg.max_monthly_requests_by_group
        || [],
      targetJobTypes: (p.targetJobTypes || cfg.target_job_types || []).map(String),
      // スキルグループ制限：このグループに属するスタッフのみ割当可能
      targetGroup: String(p.targetGroup ?? cfg.target_group ?? "").trim(),
      requestGroup: p.requestGroup ?? cfg.request_group ?? "",
      requestGroupLabel: p.requestGroupLabel ?? cfg.request_group_label ?? "",
      // 連勤への寄与度: 0=休み扱い, 0.5=半日, 1=通常勤務, null=自動判定
      consecutiveContribution:
        p.consecutiveContribution
        ?? cfg.consecutive_contribution
        ?? cfg.consecutiveContribution
        ?? null,
      groupName: p.groupName ?? cfg.group_name ?? "",
      postShiftIds: safeArr(p.postShiftIds || cfg.post_shift_ids || []).map(String),
      // 🔥 後続禁止: 「このパターンの翌日には置けないパターン ID 配列」
      //    例: 夜勤に [日勤ID] を設定 → 夜勤の翌日に日勤を置けない
      forbiddenPostShiftIds: safeArr(
        p.forbiddenPostShiftIds
        || cfg.forbidden_post_shift_ids
        || cfg.forbiddenPostShiftIds
        || []
      ).map(String),
      // 🔥 必要人数カウント対象外グループ:
      //   このスキルグループのメンバーがこのパターンに配置されても required にカウントしない。
      //   用途: 新人・研修中スタッフを必要人数に数えないことで戦力的な実数を担保する。
      excludedCountGroups: safeArr(
        p.excludedCountGroups
        || cfg.excluded_count_groups
        || cfg.excludedCountGroups
        || []
      ).map(String),
      assignmentModeEnabled:
        p.assignmentModeEnabled
        ?? cfg.assignmentModeEnabled
        ?? cfg.assignment_mode_enabled
        ?? false,
    };
  });

// =============================================================================
// 補助データ取得
// =============================================================================

const fetchPolicy = async (orgId) => {
  const { data, error } = await supabase
    .from("holiday_policies")
    .select("*")
    .eq("organization_id", orgId)
    .maybeSingle();
  if (error) console.warn("⚠️ holiday_policies:", error);
  return {
    monthlyMin: data?.monthly_min ?? 8,
    monthlyMax: data?.monthly_max ?? 11,
    maxConsecutiveWorkDays: data?.max_consecutive_work_days ?? 6,
  };
};

// スキルグループ取得：
//   - パターンの target_group は「グループ名」で保存
//   - skill_groups.members は staff.id の配列のはずだが、環境によっては employee_id を入れているケースもある
//   - 両方を許容して resolved な staff.id (uuid-text) の Set に正規化する
const fetchSkillGroupMembership = async (orgId, staffList) => {
  const { data, error } = await supabase
    .from("skill_groups")
    .select("id, name, members")
    .eq("organization_id", orgId);
  if (error) {
    console.warn("⚠️ skill_groups:", error);
    return { byName: {}, byId: {} };
  }

  // staffList から id ↔ employee_id のマップを作成
  const staffIdSet = new Set();
  const empIdToStaffId = {};
  (staffList || []).forEach((s) => {
    if (s.id) staffIdSet.add(String(s.id));
    const emp = s.employeeId || s.employee_id;
    if (emp && s.id) empIdToStaffId[String(emp)] = String(s.id);
  });

  const byName = {}; // { groupName: Set<staffId> }
  const byId = {};   // { groupId: groupName }
  (data || []).forEach((g) => {
    // members は jsonb なので、配列以外の場合も対応
    let rawMembers = [];
    if (Array.isArray(g.members)) rawMembers = g.members;
    else if (typeof g.members === "string") {
      try { rawMembers = JSON.parse(g.members); } catch { rawMembers = []; }
    } else if (g.members && typeof g.members === "object") {
      rawMembers = Object.values(g.members);
    }
    const resolved = new Set();
    let resolvedAsStaffId = 0;
    let resolvedAsEmpId = 0;
    let unresolved = 0;
    rawMembers.forEach((m) => {
      const ms = String(m);
      if (staffIdSet.has(ms)) {
        resolved.add(ms);
        resolvedAsStaffId += 1;
      } else if (empIdToStaffId[ms]) {
        resolved.add(empIdToStaffId[ms]);
        resolvedAsEmpId += 1;
      } else {
        unresolved += 1;
      }
    });
    if (g.name) byName[g.name] = resolved;
    if (g.id) byId[g.id] = g.name;
    console.log(
      `  📦 skill_group "${g.name}": members ${rawMembers.length}件 → 解決 ${resolved.size}件 ` +
      `(staffId直接=${resolvedAsStaffId}, empId経由=${resolvedAsEmpId}, 未解決=${unresolved})`
    );
  });
  return { byName, byId };
};

const fetchIncompatibilities = async (orgId) => {
  const { data, error } = await supabase
    .from("shift_incompatibilities")
    .select("*")
    .eq("organization_id", orgId);
  if (error) {
    console.warn("⚠️ shift_incompatibilities:", error);
    return [];
  }
  return (data || []).map((r) => ({
    userAStaffId: String(r.user_a_staff_id),
    userBStaffId: String(r.user_b_staff_id),
    scope: r.scope || "sameShift",
    restrictedRequestGroups: safeArr(r.restricted_request_groups || []),
  }));
};

// 係活動などのイベントを取得
//   戻り値: [{ event_date, event_end_date, target_skill_group, target_staff_ids,
//             target_pattern_id, weekend_holiday_mode, weekend_holiday_pattern_id, name }, ...]
//   範囲モード対応: 期間が当月と重なるイベントを全て取得（=event_date <= 月末 かつ
//                  event_end_date >= 月初 / 単日は event_date == event_end_date 扱い）
const fetchRecurringEvents = async (orgId, firstDate, lastDate) => {
  const { data, error } = await supabase
    .from("recurring_events")
    .select(
      "event_date, event_end_date, event_dates, target_skill_group, target_staff_ids, " +
      "target_pattern_id, weekend_holiday_mode, weekend_holiday_pattern_id, name"
    )
    .eq("organization_id", orgId)
    .lte("event_date", lastDate)
    .order("event_date");
  if (error) {
    console.warn("⚠️ recurring_events:", error.message);
    return [];
  }
  // 月跨ぎ範囲 + 複数日付モードを考慮して月内と重なるものに絞る
  return (data || []).filter((ev) => {
    if (Array.isArray(ev.event_dates) && ev.event_dates.length > 0) {
      return ev.event_dates.some((d) => d >= firstDate && d <= lastDate);
    }
    const end = ev.event_end_date || ev.event_date;
    return end >= firstDate;
  });
};

// 特定日の必要人数オーバーライドを取得
//   戻り値: { "YYYY-MM-DD": { [patternId]: required } }
const fetchDailyOverrides = async (orgId, firstDate, lastDate) => {
  const { data, error } = await supabase
    .from("daily_required_overrides")
    .select("override_date, required_counts")
    .eq("organization_id", orgId)
    .gte("override_date", firstDate)
    .lte("override_date", lastDate);
  if (error) {
    console.warn("⚠️ daily_required_overrides:", error);
    return {};
  }
  const map = {};
  (data || []).forEach((row) => {
    map[row.override_date] = row.required_counts || {};
  });
  return map;
};

const fetchContextMatrices = async (orgId, firstDate, lastDate) => {
  const priorStart = dayjs(firstDate).subtract(14, "day").format("YYYY-MM-DD");
  const priorEnd = dayjs(firstDate).subtract(1, "day").format("YYYY-MM-DD");
  const nextStart = dayjs(lastDate).add(1, "day").format("YYYY-MM-DD");
  const nextEnd = dayjs(lastDate).add(14, "day").format("YYYY-MM-DD");
  const [priorRes, nextRes] = await Promise.all([
    supabase
      .from("monthly_shifts")
      .select("employee_id, shift_date, shift_type")
      .eq("organization_id", orgId)
      .gte("shift_date", priorStart)
      .lte("shift_date", priorEnd),
    supabase
      .from("monthly_shifts")
      .select("employee_id, shift_date, shift_type")
      .eq("organization_id", orgId)
      .gte("shift_date", nextStart)
      .lte("shift_date", nextEnd),
  ]);
  const prior = {};
  (priorRes.data || []).forEach((row) => {
    prior[keyOf(String(row.employee_id), row.shift_date)] = row.shift_type || "";
  });
  const next = {};
  (nextRes.data || []).forEach((row) => {
    next[keyOf(String(row.employee_id), row.shift_date)] = row.shift_type || "";
  });
  return { prior, next };
};

// =============================================================================
// 状態構築
// =============================================================================

const buildState = ({
  staffList,
  patterns,
  dates,
  holidays,
  policy,
  incompatibilities,
  dailyOverrides,
  priorMatrix,
  nextMatrix,
  organizationId,
  staffGroups,
  skillGroupMembership,
}) => {
  const patternByMark = {};
  const patternById = {};
  patterns.forEach((p) => {
    if (p.displayMark) patternByMark[p.displayMark] = p;
    if (p.id) patternById[p.id] = p;
  });

  const perStaff = {};
  const staffByStaffId = {};
  staffList.forEach((s) => {
    const empId = String(s.employeeId);
    perStaff[empId] = {
      employeeId: empId,
      staffId: String(s.id || s.staffId || ""),
      jobTypeId: s.job_type_id || s.jobTypeId || null,
      excludeFromAutoAssign: !!s.excludeFromAutoAssign,
      customShiftPattern: s.customShiftPattern || {},
      // 日勤のみスタッフ（役職者・パート等）: 希望以外は日勤のみ・連続日数超過なら休み
      dayShiftOnly: !!(s.dayShiftOnly || s.day_shift_only),
      // 'all' = 全日日勤、'weekdays' = 平日のみ日勤・土日祝は公休
      dayShiftOnlyMode: s.dayShiftOnlyMode || s.day_shift_only_mode || 'all',
    };
    if (perStaff[empId].staffId) {
      staffByStaffId[perStaff[empId].staffId] = empId;
    }
  });

  // 各 staff の日勤マーク。
  // resolveDayShiftMark に統一して、translateHopeToMark と完全に同じ候補集合・職種マッチロジックを使う。
  // 候補は「request_group="day" / assignment_mode_enabled / 名前に『日勤』を含む（夜勤を除く）」。
  // assignment_mode_enabled が一つも立っていない環境でも、名前ベースで看護師用/看護助手用を切り分けられる。
  const dayShiftMarkByStaff = {};
  staffList.forEach((s) => {
    const empId = String(s.employeeId);
    const jobId = String(s.job_type_id || s.jobTypeId || "");
    dayShiftMarkByStaff[empId] = resolveDayShiftMark(patterns, jobId);
  });

  return {
    matrix: {},
    frozen: new Set(),
    perStaff,
    staffByStaffId,
    patterns,
    patternByMark,
    patternById,
    dayShiftMarkByStaff,
    incompatibilities,
    staffGroups: staffGroups || {},
    skillGroupMembership: skillGroupMembership || { byName: {}, byId: {} },
    dailyOverrides: dailyOverrides || {},
    holidaySet: new Set(holidays || []),
    policy,
    dates,
    organizationId,
    priorMatrix,
    nextMatrix,
    nextMatrixOriginal: { ...nextMatrix },
    crossMonthWrites: new Map(),
    conflicts: {
      off: {},
      night: {},
      hopeOverflow: [], // 希望が固定人数を超えた
      fixedShortage: [], // 固定人数を埋め切れなかった
      flexShortage: [], // 柔軟人数の最低を満たせなかった
      consecutive: [], // 連勤違反
    },
    stats: { totalAssigned: 0, swaps: 0, crossMonthWrites: 0 },
  };
};

// =============================================================================
// セル read / write
// =============================================================================

const getCellAny = (state, empId, date) => {
  if (state.dates.includes(date)) {
    return state.matrix[keyOf(empId, date)] || "";
  }
  return (
    state.priorMatrix[keyOf(empId, date)]
    || state.nextMatrix[keyOf(empId, date)]
    || ""
  );
};

const setCell = (state, empId, date, mark) => {
  if (!state.dates.includes(date)) return;
  state.matrix[keyOf(empId, date)] = mark || "";
};

// チェーン全体を「セット」として配置可能か事前検査し、可能ならまとめて置く。
// 検査で「途中で先約があって置けない」と判明したら、何も置かずに false を返す（原子的）。
// これにより「夜勤を置いたのに明けが後で消えた／別パターンに上書きされた」という破壊を防ぐ。
const setCellWithChain = (state, empId, date, mark) => {
  if (!state.dates.includes(date)) return false;
  const startPattern = state.patternByMark[mark];
  if (!startPattern) {
    setCell(state, empId, date, mark);
    return true;
  }
  // 後続なし → 単純配置
  if (!startPattern.postShiftIds?.length) {
    setCell(state, empId, date, mark);
    return true;
  }
  // 後続あり → チェーン全体を辿って配置可能かチェック
  const plan = [{ date, mark, crossMonth: false }];
  let curMark = mark;
  let curDate = date;
  for (let depth = 0; depth < CHAIN_LIMIT; depth++) {
    const cp = state.patternByMark[curMark];
    if (!cp?.postShiftIds?.length) break;
    const nextDate = dayjs(curDate).add(1, "day").format("YYYY-MM-DD");
    const ref = cp.postShiftIds[0];
    const nextPattern = state.patternById[ref] || state.patternByMark[ref];
    if (!nextPattern?.displayMark) break;
    if (state.dates.includes(nextDate)) {
      // 当月内 — 既に frozen or 別マークが入っていれば配置不可
      if (state.frozen.has(keyOf(empId, nextDate))) return false;
      const existing = state.matrix[keyOf(empId, nextDate)];
      if (existing && existing !== nextPattern.displayMark) return false;
      plan.push({ date: nextDate, mark: nextPattern.displayMark, crossMonth: false });
    } else {
      // 翌月 — DB 既存値があれば配置不可
      if (state.nextMatrixOriginal?.[keyOf(empId, nextDate)]) return false;
      plan.push({ date: nextDate, mark: nextPattern.displayMark, crossMonth: true });
    }
    curMark = nextPattern.displayMark;
    curDate = nextDate;
  }
  // チェーン全体配置 OK → コミット（途中で frozen に追加していくのでアトミック）
  plan.forEach((step, idx) => {
    if (step.crossMonth) {
      state.crossMonthWrites.set(keyOf(empId, step.date), {
        organization_id: state.organizationId,
        employee_id: empId,
        shift_date: step.date,
        shift_type: step.mark || null,
        updated_at: new Date().toISOString(),
      });
      state.nextMatrix[keyOf(empId, step.date)] = step.mark;
    } else {
      setCell(state, empId, step.date, step.mark);
      if (idx > 0) {
        // 後続は freeze（以後どのパスでも変更されない）
        state.frozen.add(keyOf(empId, step.date));
      }
    }
  });
  return true;
};

// このセルがチェーン関連かどうか（チェーン start または follow）。修復で swap させたくないセル。
const isChainRelatedCell = (state, empId, date) => {
  const mark = state.matrix[keyOf(empId, date)];
  if (!mark) return false;
  const p = state.patternByMark[mark];
  if (p?.postShiftIds?.length) return true; // チェーン start
  // 前日が start マークでこの cell がその follow か確認
  const prevDate = dayjs(date).subtract(1, "day").format("YYYY-MM-DD");
  const prevMark = getCellAny(state, empId, prevDate);
  if (!prevMark) return false;
  const pp = state.patternByMark[prevMark];
  if (!pp?.postShiftIds?.length) return false;
  const ref = pp.postShiftIds[0];
  const nextPat = state.patternById[ref] || state.patternByMark[ref];
  return nextPat?.displayMark === mark;
};

const freezeCell = (state, empId, date, mark) => {
  if (!state.dates.includes(date)) return;
  if (!state.perStaff[empId]) return;
  setCell(state, empId, date, mark);
  state.frozen.add(keyOf(empId, date));
};

// =============================================================================
// 必要人数
// =============================================================================

// 🔥 自前のベース計算（チェーン follow 加算なし）
//   _getBaseRequiredCount: dailyOverrides / requiredByDayOfWeek / 祝日調整 のみを参照
//   無限再帰を避けるため、chain follow 加算ロジックを分離。
const _getBaseRequiredCount = (pattern, date, holidaySet, dailyOverrides) => {
  if (dailyOverrides && dailyOverrides[date]) {
    const ov = dailyOverrides[date];
    if (pattern.id && ov[pattern.id] != null) return Number(ov[pattern.id]);
  }
  const wd = weekdayKey(date);
  const base = Number(pattern.requiredByDayOfWeek?.[wd] || 0);
  if (base <= 0) return 0;
  const isHol = isWeekendOrHoliday(date, holidaySet);
  if (!isHol) return base;
  switch (pattern.holidayAdjustment) {
    case "no_assign":
      return 0;
    case "reduce_count":
      return Math.max(0, Math.round(base * 0.7));
    case "increase_count":
      return Math.round(base * 1.3);
    case "custom_count":
      return Number(pattern.holidayCustomCount || 0);
    default:
      return base;
  }
};

const getRequiredCount = (pattern, date, holidaySet, dailyOverrides, state) => {
  // 🔥 連鎖専用パターン (isChainOnlyPattern=true) の required は完全動的算出:
  //   ユーザー指摘: 「後続シフトは前日の chain start の人数で決まる。
  //                曜日ごとに手入力する必要はないはず」
  //   実装: 前日に postShiftIds で自分を指す chain start パターンの required 合計を
  //         そのまま自分の required として返す。dailyOverride も自動反映される。
  //   isChainOnlyPattern=false なら従来通り (手入力曜日値 + override 増分)。
  if (pattern?.isChainOnlyPattern && state?.patterns && pattern?.id) {
    const prevDate = dayjs(date).subtract(1, 'day').format('YYYY-MM-DD');
    let total = 0;
    state.patterns.forEach((startPat) => {
      const list = startPat.postShiftIds || [];
      if (!list.includes(pattern.id)) return;
      // 前日の chain start の required (override 込み)
      total += _getBaseRequiredCount(startPat, prevDate, holidaySet, dailyOverrides);
    });
    return total;
  }

  const base = _getBaseRequiredCount(pattern, date, holidaySet, dailyOverrides);

  // 🔥 chain follow への自動加算 (非連鎖専用パターンで postShiftIds の対象になっているケース):
  //   既存設定との互換性: 手入力 required + override 増分で扱う
  if (!state?.patterns || !pattern?.id) return base;
  const prevDate = dayjs(date).subtract(1, 'day').format('YYYY-MM-DD');
  let chainAddend = 0;
  state.patterns.forEach((startPat) => {
    const list = startPat.postShiftIds || [];
    if (!list.includes(pattern.id)) return;
    const startActual = _getBaseRequiredCount(startPat, prevDate, holidaySet, dailyOverrides);
    const startDefault = _getBaseRequiredCount(startPat, prevDate, holidaySet, null);
    const delta = startActual - startDefault;
    if (delta > 0) chainAddend += delta;
  });
  return base + chainAddend;
};

const countOnDate = (state, date, mark) => {
  let n = 0;
  Object.keys(state.perStaff).forEach((empId) => {
    if (state.matrix[keyOf(empId, date)] === mark) n += 1;
  });
  return n;
};

// 🔥 「必要人数（required）の充足判定」用カウント。
//   pattern.excludedCountGroups に指定されたスキルグループのメンバーは除外する。
//   用途: 新人・研修中スタッフを必要人数に数えない（戦力ベースで判定）。
//   ・pattern が無い / excludedCountGroups が空 → countOnDate + 他パターン分の合算
//   ・excludedCountGroups の各グループに所属するスタッフは除外
//
//   🔥 さらに「他パターンからのカウント (countsTowardPatterns)」も合算する。
//     例: オンコール明け パターンに `countsTowardPatterns: [{ patternId: 日勤.id, dayMode: 'weekdays' }]`
//     が設定されている場合、 countForRequirement(日勤, 平日) は
//     「日勤に居る人 + オンコール明けに居る人」を合算する。
const countForRequirement = (state, date, mark, pattern) => {
  const excluded = pattern?.excludedCountGroups || [];
  const excludedStaffIds = new Set();
  excluded.forEach((groupName) => {
    const memberSet = state.skillGroupMembership?.byName?.[groupName];
    if (memberSet) memberSet.forEach((sid) => excludedStaffIds.add(String(sid)));
  });

  // 「このパターンにカウントされる別マーク」一覧を作る
  //   対象パターンの id を pattern.id で識別。state.patterns を走査して
  //   countsTowardPatterns に pattern.id が含まれていれば、その別パターンの
  //   displayMark を「カウント対象マーク」として扱う。
  const targetMarks = new Set([mark]);
  if (pattern?.id) {
    const isWeekend = state.holidaySet?.has(date) || (() => {
      const d = new Date(date);
      const dow = d.getDay();
      return dow === 0 || dow === 6;
    })();
    state.patterns.forEach((p) => {
      if (!p.displayMark || p.displayMark === mark) return;
      const list = p.countsTowardPatterns || p.counts_toward_patterns
        || p.configuration?.counts_toward_patterns || [];
      const hit = list.find((x) => x?.patternId === pattern.id);
      if (!hit) return;
      const mode = hit.dayMode || 'all';
      if (mode === 'weekdays' && isWeekend) return;
      if (mode === 'weekends_holidays' && !isWeekend) return;
      targetMarks.add(p.displayMark);
    });
  }

  let n = 0;
  Object.values(state.perStaff).forEach((staff) => {
    const empId = staff.employeeId;
    const v = state.matrix[keyOf(empId, date)];
    if (!v || !targetMarks.has(v)) return;
    const sid = String(staff.staffId || '');
    if (excludedStaffIds.has(sid)) return; // 除外対象 → カウントしない
    n += 1;
  });
  return n;
};

// 🔥 そのスタッフが pattern の「カウント対象外グループ」に属するかどうか。
//   修復で「配置しても required にカウントされないスタッフ」を候補から除外するために使う。
//   これがないと「新人を配置 → カウントされない → 不足のまま → また配置」で永久ループ。
const isExcludedForPatternCount = (state, empId, pattern) => {
  const excluded = pattern?.excludedCountGroups || [];
  if (excluded.length === 0) return false;
  const staffId = state.perStaff[empId]?.staffId;
  if (!staffId) return false;
  for (const groupName of excluded) {
    const memberSet = state.skillGroupMembership?.byName?.[groupName];
    if (memberSet && memberSet.has(String(staffId))) return true;
  }
  return false;
};

// 🔥 スタッフの「自由度スコア」を計算する。
//   修復時に「誰を動かすか」を選ぶ際、自由度が高い人を優先的に動かす。
//   ユーザー要件: スキル多重度 / 前後の連勤数 / 希望が前後にない or 少ない / 取れる選択肢が多い。
//
//   高いほど「動かしやすい」= 移動コスト低 → 候補として優先採用すべき。
const calcStaffFreedomScore = (state, empId, date) => {
  const staff = state.perStaff[empId];
  if (!staff) return 0;
  let score = 0;

  // (1) スキル多重度: 配置可能パターン数（職種＋スキルグループ一致）
  let skillVersatility = 0;
  state.patterns.forEach((p) => {
    if (p.isChainOnlyPattern || !p.displayMark) return;
    if (!matchesJobType(staff, p)) return;
    if (!matchesSkillGroup(state, empId, p)) return;
    skillVersatility += 1;
  });
  score += skillVersatility * 2; // 多重スキル = 動かしやすい

  // (2) 月内の希望休数（少ないほど自由度高い）
  let hopeCount = 0;
  state.dates.forEach((d) => {
    if (state.hopes?.[keyOf(empId, d)]) hopeCount += 1;
  });
  score += Math.max(0, 10 - hopeCount); // 希望少ない = +10、多い = 0

  // (3) date 前後の希望密度: 前後 ±2 日に希望がない = 動かしやすい
  let nearbyHopeFree = 0;
  for (let offset = -2; offset <= 2; offset++) {
    if (offset === 0) continue;
    const d = state.dates[state.dates.indexOf(date) + offset];
    if (!d) continue;
    if (!state.hopes?.[keyOf(empId, d)]) nearbyHopeFree += 1;
  }
  score += nearbyHopeFree; // 0-4 加点

  // (4) 前後の連勤端からの距離: 余裕があるほど自由度高い
  //     当日を OFF にしてもしなくても、前後の連勤上限に余裕があれば動かしやすい
  const maxC = state.policy.maxConsecutiveWorkDays ?? 5.5;
  const beforeContrib = (() => {
    let c = 0;
    for (let i = state.dates.indexOf(date) - 1; i >= 0; i--) {
      const v = state.matrix[keyOf(empId, state.dates[i])];
      if (!v) break;
      const contrib = getConsecutiveContribution(v, state.patterns, state.patternByMark);
      if (contrib === 0) break;
      c += contrib;
    }
    return c;
  })();
  const afterContrib = (() => {
    let c = 0;
    for (let i = state.dates.indexOf(date) + 1; i < state.dates.length; i++) {
      const v = state.matrix[keyOf(empId, state.dates[i])];
      if (!v) break;
      const contrib = getConsecutiveContribution(v, state.patterns, state.patternByMark);
      if (contrib === 0) break;
      c += contrib;
    }
    return c;
  })();
  const headroom = Math.max(0, maxC - (beforeContrib + afterContrib));
  score += headroom * 3; // 連勤余裕 = 動かしやすい (高重み)

  return score;
};

const countPerStaffPattern = (state, empId, patternId) => {
  const pattern = state.patternById[patternId];
  if (!pattern) return 0;
  let n = 0;
  state.dates.forEach((date) => {
    if (state.matrix[keyOf(empId, date)] === pattern.displayMark) n += 1;
  });
  return n;
};

const countPerStaffGroup = (state, empId, group) => {
  let n = 0;
  state.dates.forEach((date) => {
    const v = state.matrix[keyOf(empId, date)];
    if (!v) return;
    const p = state.patternByMark[v];
    if (p?.requestGroup === group) n += 1;
  });
  return n;
};

// =============================================================================
// 制約チェック
// =============================================================================

// 当日 (date) の前日にある「ある cell の mark」がチェーン follow（明け等）かを判定
//   ＝前々日の mark が postShiftIds を持ち、その指す mark が当該 cell の mark と一致する
const isChainFollowMark = (state, empId, date) => {
  const mark = getCellAny(state, empId, date);
  if (!mark) return false;
  const prevDate = dayjs(date).subtract(1, "day").format("YYYY-MM-DD");
  const prevMark = getCellAny(state, empId, prevDate);
  if (!prevMark) return false;
  const pp = state.patternByMark[prevMark];
  if (!pp?.postShiftIds?.length) return false;
  const ref = pp.postShiftIds[0];
  const nextPat = state.patternById[ref] || state.patternByMark[ref];
  return nextPat?.displayMark === mark;
};

// 「指定日に勤務マークを置いた場合の累計連勤値」を予測する。
//   寄与度 0（休み/明け）にぶつかったら連勤を切り、累計には含めない。
//   date の前後両方を見るので、空欄を埋めた結果として後方の既存日勤と
//   連結して連勤超過になるケースも捕捉できる。
//   contribution: その date に置こうとしているマークの寄与度（既定 1）
//   例: 日勤×5 + これから置く半休(0.5) → 5.5
const getWouldBeConsecutiveLength = (state, empId, date, contribution = 1) => {
  let backward = 0;
  let d = dayjs(date).subtract(1, "day");
  for (let i = 0; i < 30; i++) {
    const ds = d.format("YYYY-MM-DD");
    const v = getCellAny(state, empId, ds);
    if (!v) break;
    // 明示寄与度設定があれば follow 判定で 0 にしない（ユーザー設定を尊重）。
    if (!hasExplicitContribution(v, state.patterns, state.patternByMark)
        && isChainFollowMark(state, empId, ds)) break;
    const c = getConsecutiveContribution(v, state.patterns, state.patternByMark);
    if (c <= 0) break;
    backward += c;
    d = d.subtract(1, "day");
  }

  let forward = 0;
  let dF = dayjs(date).add(1, "day");
  for (let i = 0; i < 30; i++) {
    const ds = dF.format("YYYY-MM-DD");
    const v = getCellAny(state, empId, ds);
    if (!v) break;
    if (!hasExplicitContribution(v, state.patterns, state.patternByMark)
        && isChainFollowMark(state, empId, ds)) break;
    const c = getConsecutiveContribution(v, state.patterns, state.patternByMark);
    if (c <= 0) break;
    forward += c;
    dF = dF.add(1, "day");
  }

  return backward + (contribution > 0 ? contribution : 0) + forward;
};

const getSameMarkConsecutive = (state, empId, date, mark) => {
  let count = 0;
  let d = dayjs(date).subtract(1, "day");
  for (let i = 0; i < 30; i++) {
    if (getCellAny(state, empId, d.format("YYYY-MM-DD")) !== mark) break;
    count += 1;
    d = d.subtract(1, "day");
  }
  return count;
};

const matchesJobType = (staff, pattern) => {
  if (pattern.targetJobTypes.length === 0) return true;
  if (!staff.jobTypeId) return false;
  return pattern.targetJobTypes.includes(String(staff.jobTypeId));
};

// パターンに target_group が設定されている場合、そのスキルグループに属するスタッフのみ可。
//   ShiftPatternSettings は target_group に「グループ名」を保存しているため、
//   skill_groups.members（staff.id 配列）と直接比較できない。
//   そこで skillGroupMembership.byName[groupName] = Set<staff.id> を使って、
//   このスタッフの staff.id がそのグループに含まれているかをチェックする。
const matchesSkillGroup = (state, empId, pattern) => {
  if (!pattern.targetGroup) return true; // 制限なし
  const memberSet = state.skillGroupMembership?.byName?.[pattern.targetGroup];
  if (!memberSet) {
    // パターンには target_group が設定されているがマスタに見つからない。
    // ⚠️ 以前は「無視（誰でも OK）」にしていたが、これだと設定ミスで全員配置可能になり
    //    「スキルグループのメンバー以外が選出される」現象が起きていた。
    //    ここでは「該当者なし（誰も配置しない）」に変えて、設定ミスに気付きやすくする。
    if (!state._loggedMissingGroups) state._loggedMissingGroups = new Set();
    if (!state._loggedMissingGroups.has(pattern.targetGroup)) {
      console.warn(
        `⚠️ matchesSkillGroup: target_group "${pattern.targetGroup}" (パターン: ${pattern.name}/${pattern.displayMark}) ` +
        `がスキルグループマスタに見つかりません。該当者なしとして扱います。`
      );
      state._loggedMissingGroups.add(pattern.targetGroup);
    }
    return false;
  }
  const myStaffId = state.perStaff[empId]?.staffId;
  if (!myStaffId) return false;
  return memberSet.has(String(myStaffId));
};

const checkMonthlyLimits = (state, empId, pattern) => {
  if (pattern.maxMonthlyRequests != null) {
    const cur = countPerStaffPattern(state, empId, pattern.id);
    if (cur >= pattern.maxMonthlyRequests) return false;
  }
  if (Array.isArray(pattern.maxMonthlyRequestsByGroup)) {
    for (const rule of pattern.maxMonthlyRequestsByGroup) {
      if (!rule?.group || rule.max == null) continue;
      const cur = countPerStaffGroup(state, empId, rule.group);
      if (cur >= rule.max) return false;
    }
  }
  return true;
};

const violatesIncompatibility = (state, empId, date, pattern) => {
  if (!state.incompatibilities.length) return false;
  if (!pattern.requestGroup) return false;
  const myStaffId = state.perStaff[empId]?.staffId;
  if (!myStaffId) return false;

  for (const rule of state.incompatibilities) {
    if (rule.scope !== "sameShift") continue;
    if (rule.userAStaffId !== myStaffId && rule.userBStaffId !== myStaffId)
      continue;
    if (!rule.restrictedRequestGroups.includes(pattern.requestGroup)) continue;
    const otherStaffId =
      rule.userAStaffId === myStaffId ? rule.userBStaffId : rule.userAStaffId;
    const otherEmpId = state.staffByStaffId[otherStaffId];
    if (!otherEmpId) continue;
    const otherMark = getCellAny(state, otherEmpId, date);
    if (!otherMark) continue;
    const otherPattern = state.patternByMark[otherMark];
    if (!otherPattern?.requestGroup) continue;
    if (rule.restrictedRequestGroups.includes(otherPattern.requestGroup)) {
      return true;
    }
  }
  return false;
};

const canAssign = (state, empId, date, pattern) => {
  const staff = state.perStaff[empId];
  if (!staff) return false;
  if (staff.excludeFromAutoAssign) return false;
  if (state.frozen.has(keyOf(empId, date))) return false;
  if (state.matrix[keyOf(empId, date)]) return false;
  // ハード制約：絶対に緩和しない
  if (!matchesJobType(staff, pattern)) return false;
  if (!matchesSkillGroup(state, empId, pattern)) return false;

  // 🔥 連鎖専用パターンは「単体で直接配置」できない:
  //   ユーザー指摘「連鎖専用パターンが脈絡なしに突如配置されてる」現象の対策。
  //   isChainOnlyPattern=true のパターンは、 chain start からの伝播でしか配置されない。
  //   setCellWithChain など chain 伝播経路では直接 matrix を書くので影響なし。
  //   通常の canAssign 経路でこのフラグを弾く。
  if (pattern?.isChainOnlyPattern) return false;
  // ソフト制約：state.relaxation で緩和される可能性
  const relax = state.relaxation || {};
  if (!relax.ignoreMonthly && !checkMonthlyLimits(state, empId, pattern)) return false;

  // 連勤チェックは寄与度ベース: 配置するパターン自身の寄与度を考慮
  const baseLimit = state.policy.maxConsecutiveWorkDays ?? 6;
  const limit = baseLimit + (relax.consecutiveBonus || 0);
  const contribution = getConsecutiveContribution(pattern.displayMark, state.patterns, state.patternByMark);
  if (contribution > 0) {
    // 配置後の累計連勤値（前後の既存セルも考慮）
    const wouldBe = getWouldBeConsecutiveLength(state, empId, date, contribution);
    if (wouldBe > limit) return false;
  }
  if (!relax.ignoreSameConsec && pattern.maxSameConsecutive > 0) {
    const sameRun = getSameMarkConsecutive(state, empId, date, pattern.displayMark);
    if (sameRun + 1 > pattern.maxSameConsecutive) return false;
  }
  // 🔥 後続禁止チェック: 前日のパターンが forbiddenPostShiftIds を持ち、
  //   今日配置しようとしているパターンがその禁止先に該当するなら配置不可。
  //   これはハード制約（緩和段階でも守る）として扱う。
  //   例: 夜勤に「翌日 日勤NG」設定 → 夜勤の翌日に日勤を置こうとすると false。
  const prevDate = dayjs(date).subtract(1, 'day').format('YYYY-MM-DD');
  const prevMark = getCellAny(state, empId, prevDate);
  if (prevMark) {
    const prevPat = state.patternByMark[prevMark];
    const forbidden = prevPat?.forbiddenPostShiftIds || [];
    if (forbidden.length > 0) {
      const forbiddenIncludesThis = forbidden.some((ref) => {
        const t = state.patternById[ref] || state.patternByMark[ref];
        return t?.displayMark === pattern.displayMark;
      });
      if (forbiddenIncludesThis) return false;
    }
  }
  // 逆方向: 今日のパターン自身に forbiddenPostShiftIds があり、翌日のセルがその禁止先なら配置不可。
  //   ※翌日セルがまだ空 (未配置) なら問題なし。配置済みのみチェック。
  if (pattern.forbiddenPostShiftIds?.length) {
    const nextDate = dayjs(date).add(1, 'day').format('YYYY-MM-DD');
    const nextMark = getCellAny(state, empId, nextDate);
    if (nextMark) {
      const forbiddenIncludesNext = pattern.forbiddenPostShiftIds.some((ref) => {
        const t = state.patternById[ref] || state.patternByMark[ref];
        return t?.displayMark === nextMark;
      });
      if (forbiddenIncludesNext) return false;
    }
  }
  if (!relax.ignoreIncompat && violatesIncompatibility(state, empId, date, pattern)) return false;
  return true;
};

// =============================================================================
// 候補ピック（このパターンの月割当が少ない順）
// =============================================================================

// チェーン全体を辿って、各セルが配置可能か事前検査（best-effort配置の判定用）
//   配置不可 = 後続日のどれかが「frozen な別マーク」「別マーク既存」
const canPlaceWholeChain = (state, empId, date, mark) => {
  const startPattern = state.patternByMark[mark];
  if (!startPattern?.postShiftIds?.length) return true;
  let curMark = mark;
  let curDate = date;
  for (let depth = 0; depth < CHAIN_LIMIT; depth++) {
    const cp = state.patternByMark[curMark];
    if (!cp?.postShiftIds?.length) return true;
    const nextDate = dayjs(curDate).add(1, "day").format("YYYY-MM-DD");
    const ref = cp.postShiftIds[0];
    const nextPattern = state.patternById[ref] || state.patternByMark[ref];
    if (!nextPattern?.displayMark) return true;
    if (state.dates.includes(nextDate)) {
      if (state.frozen.has(keyOf(empId, nextDate))) {
        const existing = state.matrix[keyOf(empId, nextDate)];
        if (existing !== nextPattern.displayMark) return false;
      }
      const existing = state.matrix[keyOf(empId, nextDate)];
      if (existing && existing !== nextPattern.displayMark) return false;
    } else {
      if (state.nextMatrixOriginal?.[keyOf(empId, nextDate)]) return false;
    }
    curMark = nextPattern.displayMark;
    curDate = nextDate;
  }
  return true;
};

// あるパターンが「日勤パターン」かを判定するヘルパー。
//   assignment_mode_enabled が立っているのが正規だが、立っていなくても
//   request_group="day" や名前に「日勤」を含む（夜勤を除く）パターンは日勤として扱う。
const isDayShiftLikePattern = (pattern) => {
  if (!pattern) return false;
  if (pattern.assignmentModeEnabled) return true;
  if (pattern.requestGroup === "day") return true;
  if (pattern.name?.includes("日勤") && !pattern.name?.includes("夜勤")) return true;
  return false;
};

// 日付付きの候補選定（チェーン可能な候補を優先、シードで擾乱して異なる解を探索）
const pickCandidatesForDate = (state, eligibleIds, pattern, date, count) => {
  const hasChain = !!pattern.postShiftIds?.length;
  const seed = state.globalSeed || 0;
  // 日勤のみスタッフは「非日勤パターン」の候補からは除外（希望は別経路で既に反映済み）。
  // 加えて、「平日のみ」モードのスタッフは土日祝の日勤パターン候補からも除外（公休にしたいため）。
  const isDayShiftPattern = isDayShiftLikePattern(pattern);
  const isWeekendOrHol = isWeekendOrHoliday(date, state.holidaySet);
  const filtered = eligibleIds.filter((empId) => {
    const meta = state.perStaff[empId];
    if (!meta?.dayShiftOnly) return true;
    // dayShiftOnly スタッフ:
    if (!isDayShiftPattern) return false; // 非日勤パターンには配置しない
    if (isWeekendOrHol && meta.dayShiftOnlyMode === 'weekdays') return false; // 平日のみモードは土日祝出さない
    return true;
  });
  const scored = filtered.map((empId) => ({
    empId,
    cnt: countPerStaffPattern(state, empId, pattern.id),
    chainable: hasChain ? canPlaceWholeChain(state, empId, date, pattern.displayMark) : true,
    // シードによる擾乱（同じ cnt 内での順序を変える）
    noise: ((hash(empId, seed * 31 + 17) % 1000) / 1000),
  }));
  scored.sort((a, b) => {
    if (a.chainable !== b.chainable) return a.chainable ? -1 : 1;
    if (a.cnt !== b.cnt) return a.cnt - b.cnt;
    if (Math.abs(a.noise - b.noise) > 0.001) return a.noise - b.noise;
    return a.empId.localeCompare(b.empId);
  });
  return scored.slice(0, count).map((s) => s.empId);
};

// =============================================================================
// Phase 1: 希望と固定パターンの反映
// =============================================================================

const isNightHopeMark = (rawMark) => {
  if (!rawMark) return false;
  const v = String(rawMark).trim().toLowerCase();
  return v === "night" || v === "/";
};

const findNightPatterns = (state) =>
  state.patterns
    .filter(
      (p) =>
        !p.isChainOnlyPattern && (
          p.groupName?.includes("夜勤")
          || p.name?.includes("夜勤")
          || p.requestGroup === "night"
          || (p.displayMark && p.displayMark.startsWith("/"))
        )
    )
    .sort((a, b) => a.priority - b.priority);

const applyHopes = (state, hopes) => {
  // ⚙️ バージョンタグ: 修正がブラウザに反映されているかを確認するため
  console.log('🏷️ shiftAutoAssign version: 2026-06-10-r3 (resolveDayShiftMark + requestGroupLabel)');
  const reasons = { noMark: 0, badKey: 0, notInMonth: 0, unknownStaff: 0, noTranslation: 0 };
  const reasonSamples = { noMark: [], badKey: [], notInMonth: [], unknownStaff: [], noTranslation: [] };
  const translationLog = {};
  const translationFailures = []; // 翻訳失敗の詳細
  const KEY_PATTERN = /^(.+)_(\d{4}-\d{2}-\d{2})$/;
  const totalIn = Object.keys(hopes || {}).length;

  // Step 1: パース → 構造化
  const parsed = [];
  Object.entries(hopes || {}).forEach(([key, raw]) => {
    const rawMark = extractMark(raw);
    if (!rawMark) {
      reasons.noMark += 1;
      if (reasonSamples.noMark.length < 5) reasonSamples.noMark.push({ key, raw });
      return;
    }
    const m = key.match(KEY_PATTERN);
    if (!m) {
      reasons.badKey += 1;
      if (reasonSamples.badKey.length < 5) reasonSamples.badKey.push({ key, raw });
      return;
    }
    const empId = m[1];
    const date = m[2];
    if (!state.dates.includes(date)) {
      reasons.notInMonth += 1;
      if (reasonSamples.notInMonth.length < 5) reasonSamples.notInMonth.push({ key, date });
      return;
    }
    if (!state.perStaff[empId]) {
      reasons.unknownStaff += 1;
      if (reasonSamples.unknownStaff.length < 5) reasonSamples.unknownStaff.push({ key, empId });
      return;
    }
    parsed.push({ empId, date, rawMark });
  });

  // Step 2: 夜勤希望と他に分離
  const nightHopesByDate = {};
  const otherHopes = [];
  parsed.forEach((h) => {
    // 直接マーク一致（[/1 等）は specific として通常扱い
    if (state.patternByMark[h.rawMark]) {
      otherHopes.push(h);
    } else if (isNightHopeMark(h.rawMark)) {
      if (!nightHopesByDate[h.date]) nightHopesByDate[h.date] = [];
      nightHopesByDate[h.date].push(h);
    } else {
      otherHopes.push(h);
    }
  });

  // 希望反映前のスキルグループメンバーシップチェック。
  //   target_group 制限のあるパターンに対し、希望者がメンバーでない場合は反映しない。
  //   こうしないと「非メンバーが希望を出した→そのまま配置」で skillGroup 違反になる。
  const checkSkillGroupForHope = (empId, mark) => {
    const p = state.patternByMark[mark];
    if (!p || !p.targetGroup) return true;
    const memberSet = state.skillGroupMembership?.byName?.[p.targetGroup];
    if (!memberSet) return false;
    const myStaffId = state.perStaff[empId]?.staffId;
    if (!myStaffId) return false;
    return memberSet.has(String(myStaffId));
  };
  const skillGroupRejected = [];

  // Step 3: 非夜勤の希望を 1対1 で翻訳・固定（後続シフトがあれば即時適用）
  let applied = 0;
  otherHopes.forEach((h) => {
    const mark = translateHopeToMark(h.rawMark, state, state.perStaff[h.empId]);
    if (mark == null) {
      reasons.noTranslation += 1;
      if (reasonSamples.noTranslation.length < 5) {
        reasonSamples.noTranslation.push({ empId: h.empId, date: h.date, rawMark: h.rawMark });
      }
      translationFailures.push({ empId: h.empId, date: h.date, rawMark: h.rawMark, reason: 'null' });
      return;
    }
    // ★ スキルグループ非メンバーは希望反映しない
    //    ただし管理者希望は「最優先の意思」として skill group check をスキップする。
    const hopeKey = `${h.empId}_${h.date}`;
    const isAdmin = state.adminHopeKeys?.has(hopeKey);
    if (!isAdmin && !checkSkillGroupForHope(h.empId, mark)) {
      skillGroupRejected.push({ empId: h.empId, date: h.date, mark, rawMark: h.rawMark });
      return;
    }
    if (isAdmin) {
      console.log(`👑 管理者希望: ${h.empId} ${h.date} → ${mark} (skill group check スキップ)`);
    }
    // 翻訳結果が patternByMark に存在しない → matrix に書いても他ロジックが扱えないため、警告として記録
    if (!state.patternByMark[mark]) {
      translationFailures.push({ empId: h.empId, date: h.date, rawMark: h.rawMark, mark, reason: 'mark_not_in_patterns' });
    }
    if (!translationLog[h.rawMark]) translationLog[h.rawMark] = { count: 0, mark };
    translationLog[h.rawMark].count += 1;
    freezeCell(state, h.empId, h.date, mark);
    // チェーン即時適用：希望マークが後続を持つ場合（例: 直接 [/1 を希望）、明けも frozen で予約
    const p = state.patternByMark[mark];
    if (p?.postShiftIds?.length) {
      applyChain(state, h.empId, h.date, mark, 0);
    }
    applied += 1;
  });

  // Step 4: 夜勤希望を複数の夜勤パターンに分散
  //   日ごとに、必要人数のあるパターンに順次割当（同一パターンの overflow を防ぐ）
  const nightPatterns = findNightPatterns(state);
  let nightApplied = 0;
  let nightOverflow = 0;
  Object.entries(nightHopesByDate).forEach(([date, dateHopes]) => {
    const slots = nightPatterns.map((p) => ({
      pattern: p,
      remaining: getRequiredCount(p, date, state.holidaySet, state.dailyOverrides, state),
    }));
    dateHopes.forEach((h) => {
      // ★ スキルグループ非メンバーは配置せず希望スキップ
      //   slot を選ぶ際にも skill group チェック。
      const slot = slots.find(
        (s) => s.remaining > 0 && checkSkillGroupForHope(h.empId, s.pattern.displayMark)
      );
      if (!slot && !nightPatterns.some((p) => checkSkillGroupForHope(h.empId, p.displayMark))) {
        // どの夜勤パターンのメンバーでもない → 希望を反映しない
        skillGroupRejected.push({ empId: h.empId, date, rawMark: 'night', reason: 'no night skill group' });
        return;
      }
      if (slot) {
        freezeCell(state, h.empId, date, slot.pattern.displayMark);
        // チェーン即時適用：明け等の後続を翌日にも frozen で配置
        if (slot.pattern.postShiftIds?.length) {
          applyChain(state, h.empId, date, slot.pattern.displayMark, 0);
        }
        slot.remaining -= 1;
        nightApplied += 1;
        const key = `night→${slot.pattern.displayMark}`;
        if (!translationLog[key]) translationLog[key] = { count: 0, mark: slot.pattern.displayMark };
        translationLog[key].count += 1;
      } else if (nightPatterns.length > 0) {
        // overflow: slot 不足だが希望に応える。スキルグループにマッチするパターンを選ぶ
        const fallback = nightPatterns.find((p) => checkSkillGroupForHope(h.empId, p.displayMark));
        if (!fallback) {
          skillGroupRejected.push({ empId: h.empId, date, rawMark: 'night', reason: 'no matching night pattern' });
          return;
        }
        freezeCell(state, h.empId, date, fallback.displayMark);
        if (fallback.postShiftIds?.length) {
          applyChain(state, h.empId, date, fallback.displayMark, 0);
        }
        nightOverflow += 1;
      } else {
        freezeCell(state, h.empId, date, "/");
        nightOverflow += 1;
      }
    });
  });

  console.log(
    `  ➕ 確定希望 ${applied + nightApplied} 件を固定（受信 ${totalIn} 件、skipped: ${JSON.stringify(reasons)}）`
  );
  if (nightApplied > 0 || nightOverflow > 0) {
    console.log(
      `  🌙 夜勤希望を ${nightPatterns.length} パターンに分散: ${nightApplied}件 / 溢れ ${nightOverflow}件`
    );
  }
  if (skillGroupRejected.length > 0) {
    console.warn(
      `⚠️ スキルグループ非メンバーの希望を ${skillGroupRejected.length} 件却下しました（先頭5件）:`,
      skillGroupRejected.slice(0, 5)
    );
  }

  // ===== デバッグログ強化（希望が反映されない原因の特定用） =====
  console.group('🔍 希望反映デバッグ詳細');

  // 翻訳マップ（rawHope → mark）の一覧
  if (Object.keys(translationLog).length > 0) {
    console.log('📋 希望→マーク 翻訳一覧:');
    console.table(
      Object.entries(translationLog).map(([rawHope, info]) => ({
        rawHope,
        translatedMark: info.mark,
        count: info.count,
        markExistsInPatterns: !!state.patternByMark[info.mark],
      }))
    );
  }

  // スキップ理由のサンプル
  Object.entries(reasonSamples).forEach(([cat, samples]) => {
    if (samples.length > 0) {
      console.warn(`⚠️ skipped (${cat}) サンプル ${samples.length}件:`, samples);
    }
  });

  // 翻訳失敗の詳細
  if (translationFailures.length > 0) {
    const byReason = {};
    translationFailures.forEach((f) => {
      const k = f.reason;
      if (!byReason[k]) byReason[k] = [];
      byReason[k].push(f);
    });
    Object.entries(byReason).forEach(([reason, list]) => {
      console.warn(`⚠️ 翻訳失敗 (${reason}) ${list.length}件:`, list.slice(0, 10));
    });
  }

  // どの request_group / displayMark が登録されているか
  const knownGroups = [...new Set(state.patterns.map(p => p.requestGroup).filter(Boolean))];
  const knownMarks = Object.keys(state.patternByMark);
  console.log('📚 登録済み request_group:', knownGroups);
  console.log('📚 登録済み displayMark:', knownMarks);

  console.groupEnd();
  console.log("  📝 希望種別 → 採用マーク:", translationLog);
};

const applyCustomShiftPatterns = (state) => {
  let applied = 0;
  state.dates.forEach((date) => {
    const wd = weekdayKey(date);
    Object.values(state.perStaff).forEach((staff) => {
      if (state.frozen.has(keyOf(staff.employeeId, date))) return;
      const mark = staff.customShiftPattern?.[wd];
      if (!mark) return;
      freezeCell(state, staff.employeeId, date, mark);
      applied += 1;
    });
  });
  console.log(`  ➕ スタッフ個別固定 ${applied} 件を固定`);
};

// 各スタッフの月公休 min 日数を月内に均等配置する事前 OFF 散布
//   目的: 固定パスが進む間に連勤上限で全員が枯渇する cascading を防ぐ
//   方針: hopes/custom/prior chain で既に置かれた休を考慮、残り必要数を等間隔で freeze
const preScatterOff = (state) => {
  const monthlyMin = state.policy.monthlyMin ?? 8;
  // 🔥 「事前散布は monthlyMin の半分まで」に縮小:
  //   ユーザー方針: 「monthlyMin は最低限の守り。不要な OFF は付けない」
  //   先に散布する量を減らし、残りは smartDefaultFill / 事後 OFF 補充で
  //   「固定不足に影響しない位置」に置くことで OFF の無駄使いを防ぐ。
  const PRE_SCATTER_RATIO = 0.5;
  const preTarget = Math.floor(monthlyMin * PRE_SCATTER_RATIO);
  let scattered = 0;
  Object.values(state.perStaff).forEach((staff) => {
    if (staff.excludeFromAutoAssign) return;
    const empId = staff.employeeId;
    // 既存の OFF 数を数える
    let existing = 0;
    state.dates.forEach((d) => {
      if (state.matrix[keyOf(empId, d)] === OFF_MARK) existing += 1;
    });
    const need = preTarget - existing;
    if (need <= 0) return;
    // 非固定の候補日（freezeされていない＆値もない＆チェーンに関与しない）
    //   チェーン関連日に 休 を置くと、後続パスでチェーンが破壊される
    const candidates = state.dates.filter(
      (d) =>
        !state.frozen.has(keyOf(empId, d))
        && !state.matrix[keyOf(empId, d)]
        && !isChainRelatedCell(state, empId, d)
    );
    if (candidates.length < need) return; // 余裕がない
    // 等間隔で散布（シードによりオフセット変化、毎試行で異なる位置に休が入る）
    const seed = state.globalSeed || 0;
    const stride = Math.max(1, candidates.length / need);
    const offset = (hash(empId, seed) % Math.max(Math.floor(stride), 1));
    let placed = 0;
    for (let i = offset + stride / 2; i < candidates.length && placed < need; i += stride) {
      const d = candidates[Math.floor(i)];
      if (!d) continue;
      // 🔥 freezeCell ではなく setCell を使う:
      //   散布した OFF は「monthlyMin 確保」の初期配置にすぎず、
      //   後の修復で固定不足が発生した場合に勤務化できるよう touch 可能にする。
      //   smartDefaultFill は既存値ありセルを触らないので、OFF は維持される。
      //   ユーザー希望休 (hopes) は別経路で frozen 化されるため安全。
      setCell(state, empId, d, OFF_MARK);
      placed += 1;
      scattered += 1;
    }
  });
  console.log(`  💤 事前 OFF 散布 ${scattered} 件（縮小モード: 目標 ${preTarget}/${monthlyMin}日 - 残りは事後補充）`);
};

// 🔥 修復完了後に「monthlyMin に達してないスタッフ」だけ OFF を追加で配置する。
//   ユーザー方針: 「monthlyMin は最低限。不要な OFF を散布しない」
//   - preScatterOff で半分しか散布してない → ここで残りを「影響の少ない位置」に補充
//   - 候補日の優先順位: 余剰がある柔軟パターン > 余剰がある固定パターン > 既に値があってもよい場所
//   - 連勤違反 / 固定不足 を **増やさない** 場所だけに置く（既に達成した精度を壊さない）
const runEnsureMonthlyMinOff = (state) => {
  const monthlyMin = state.policy.monthlyMin ?? 8;
  let added = 0;
  let staffNeedingMore = 0;
  let staffStillShort = 0;

  Object.values(state.perStaff).forEach((staff) => {
    if (staff.excludeFromAutoAssign) return;
    const empId = staff.employeeId;
    let offCount = 0;
    state.dates.forEach((d) => {
      if (state.matrix[keyOf(empId, d)] === OFF_MARK) offCount += 1;
    });
    if (offCount >= monthlyMin) return;
    staffNeedingMore += 1;
    let need = monthlyMin - offCount;

    // 候補日: frozen でない、 hopes でない、 チェーン関連でない
    //         かつ「現在の配置を OFF に変えても固定不足を悪化させない」
    const dateCandidates = state.dates.filter((d) => {
      const k = keyOf(empId, d);
      if (state.frozen.has(k)) return false;
      if (state.hopes?.[k]) return false;
      if (isChainRelatedCell(state, empId, d)) return false;
      const v = state.matrix[k];
      if (!v) return true; // 空セル: 安全
      if (v === OFF_MARK) return false; // 既に OFF
      const pat = state.patternByMark[v];
      if (!pat) return false;
      if (pat.postShiftIds?.length) return false;     // chain start 触らない
      if (isChainFollowMark(state, empId, d)) return false;
      // 配置されているパターンが minRequired/required を割らないか
      if (isFixedOnDate(pat, d)) {
        const req = getRequiredCount(pat, d, state.holidaySet, state.dailyOverrides, state);
        const cur = countForRequirement(state, d, pat.displayMark, pat);
        if (cur - 1 < req) return false; // required を割るので不可
      } else {
        const cur = countOnDate(state, d, pat.displayMark);
        const min = pat.minRequired || 0;
        if (cur - 1 < min) return false;
      }
      return true;
    });

    // 自由度スコア降順: 動かしやすい日 (≒ 影響少ない日) を優先
    dateCandidates.sort((a, b) =>
      calcStaffFreedomScore(state, empId, b) - calcStaffFreedomScore(state, empId, a));

    for (const d of dateCandidates) {
      if (need <= 0) break;
      const k = keyOf(empId, d);
      const orig = state.matrix[k];
      state.matrix[k] = OFF_MARK;
      // 連勤違反を増やしてないか確認 (OFF 化は基本連勤を減らす方向だが安全のため)
      if (!canAssign(state, empId, d, null)) {
        // canAssign(null) は特殊。 OFF にしても問題ない（連勤を切る効果）。 通常 true 返す
        state.matrix[k] = orig;
        continue;
      }
      added += 1;
      need -= 1;
    }
    if (need > 0) staffStillShort += 1;
  });

  if (staffNeedingMore > 0) {
    console.log(
      `🛌 事後 OFF 補充: ${added} 件追加 / 不足スタッフ ${staffNeedingMore}名 ` +
      `(うち ${staffStillShort}名 はまだ monthlyMin 未達 - 連勤/固定優先で OFF 増やせず)`
    );
  }
};

// 前月末からの後続シフト連鎖を当月へ引き継ぐ
//   例: 6/30 に夜勤[/1] が入っていて、その後続が 明け[X] の場合、
//       7/1 に明けが自動で入る。さらに後続が連鎖していれば 7/2 以降にも伝播。
const applyPriorMonthCarryover = (state) => {
  let carried = 0;
  const firstDate = state.dates[0];
  const lastPrior = dayjs(firstDate).subtract(1, "day").format("YYYY-MM-DD");
  Object.keys(state.perStaff).forEach((empId) => {
    const priorMark = state.priorMatrix[keyOf(empId, lastPrior)];
    if (!priorMark) return;
    const pattern = state.patternByMark[priorMark];
    if (!pattern?.postShiftIds?.length) return;
    const beforeFrozen = state.frozen.size;
    applyChain(state, empId, lastPrior, priorMark, 0);
    if (state.frozen.size > beforeFrozen) {
      carried += state.frozen.size - beforeFrozen;
    }
  });
  console.log(`  ➕ 前月末からの連鎖継承 ${carried} 件を反映`);
};

// =============================================================================
// 係活動などのイベント反映
//   recurring_events テーブルから当月と重なるイベントを反映する。
//   イベントの形式:
//     {
//       event_date, event_end_date (nullなら単日),
//       target_skill_group (グループモード) | target_staff_ids[] (個別モード),
//       target_pattern_id (主たる勤務パターン),
//       weekend_holiday_mode: 'continue' | 'off' | 'custom',
//       weekend_holiday_pattern_id (mode='custom' のとき),
//       name
//     }
//   対象は管理者意思として最優先 → 希望 / frozen でも強制上書き + frozen に追加。
// =============================================================================
const applyRecurringEvents = (state, events) => {
  if (!events || events.length === 0) {
    console.log('🎪 イベント反映: 0 件');
    return;
  }
  let placed = 0;
  let skipped = 0;
  const samples = [];

  // 対象 empId を解決するヘルパー
  const resolveTargetEmpIds = (ev) => {
    const empIds = new Set();
    // スタッフ個別モード（優先）
    if (Array.isArray(ev.target_staff_ids) && ev.target_staff_ids.length > 0) {
      ev.target_staff_ids.forEach((staffId) => {
        const empId = state.staffByStaffId[String(staffId)];
        if (empId) empIds.add(empId);
      });
      return empIds;
    }
    // スキルグループモード
    if (ev.target_skill_group) {
      const memberSet = state.skillGroupMembership?.byName?.[ev.target_skill_group];
      if (memberSet) {
        Array.from(memberSet).forEach((staffId) => {
          const empId = state.staffByStaffId[String(staffId)];
          if (empId) empIds.add(empId);
        });
      }
    }
    return empIds;
  };

  // 1セルを「強制配置 or 休 or 空き」で書き込み
  const placeOne = (empId, date, mark, evName) => {
    const k = keyOf(empId, date);
    state.matrix[k] = mark;
    state.frozen.add(k);
    placed += 1;
    if (samples.length < 8) {
      samples.push(`${empId} ${date}: ${mark || '(空)'} (${evName})`);
    }
    // チェーン適用（夜勤→明けなど）
    const pat = state.patternByMark[mark];
    if (pat?.postShiftIds?.length) {
      applyChain(state, empId, date, mark, 0);
    }
  };

  events.forEach((ev) => {
    const { event_date: startDate, event_end_date: endDateRaw, target_pattern_id: patternId, name } = ev;
    const endDate = endDateRaw || startDate;
    const weekendMode = ev.weekend_holiday_mode || 'continue';

    // 主パターン解決
    const mainPattern = state.patternById[patternId] || state.patterns.find((p) => p.id === patternId);
    if (!mainPattern?.displayMark) {
      skipped += 1;
      console.warn(`🎪 イベント「${name}」: パターン ${patternId} が見つからない`);
      return;
    }
    // 土日祝用パターン（custom モードのみ）
    let weekendPattern = null;
    if (weekendMode === 'custom') {
      const wid = ev.weekend_holiday_pattern_id;
      weekendPattern = state.patternById[wid] || state.patterns.find((p) => p.id === wid);
      if (!weekendPattern?.displayMark) {
        skipped += 1;
        console.warn(`🎪 イベント「${name}」: 土日祝パターン ${wid} が見つからない`);
        return;
      }
    }

    // 対象 empId
    const empIds = resolveTargetEmpIds(ev);
    if (empIds.size === 0) {
      skipped += 1;
      console.warn(`🎪 イベント「${name}」: 対象メンバーなし (group=${ev.target_skill_group}, staff=${(ev.target_staff_ids || []).length})`);
      return;
    }

    // 対象日の集合を計算: event_dates (複数モード) > 期間 (event_date..event_end_date)
    const targetDateStrs = [];
    if (Array.isArray(ev.event_dates) && ev.event_dates.length > 0) {
      ev.event_dates.forEach((d) => { if (typeof d === 'string') targetDateStrs.push(d); });
    } else {
      let d = dayjs(startDate);
      const end = dayjs(endDate);
      for (let i = 0; i < 366 && (d.isBefore(end) || d.isSame(end, 'day')); i++) {
        targetDateStrs.push(d.format('YYYY-MM-DD'));
        d = d.add(1, 'day');
      }
    }

    targetDateStrs.forEach((dateStr) => {
      if (!state.dates.includes(dateStr)) return;
      const d2 = dayjs(dateStr);
      const dow = d2.day();
      const isWeekend = dow === 0 || dow === 6;
      const isHoliday = state.holidaySet.has(dateStr);
      const isWeekendOrHoliday = isWeekend || isHoliday;

      let dayMark;
      if (isWeekendOrHoliday) {
        if (weekendMode === 'off') {
          dayMark = OFF_MARK;
        } else if (weekendMode === 'custom') {
          dayMark = weekendPattern.displayMark;
        } else {
          dayMark = mainPattern.displayMark;
        }
      } else {
        dayMark = mainPattern.displayMark;
      }

      empIds.forEach((empId) => placeOne(empId, dateStr, dayMark, name));
    });
  });

  console.log(
    `🎪 イベント反映: ${placed} セル配置 / ${skipped} 件スキップ`,
    samples
  );
};

// =============================================================================
// Phase 2: 事前検証
// =============================================================================

const preflightValidation = (state) => {
  let overCount = 0;
  state.dates.forEach((date) => {
    state.patterns.forEach((pattern) => {
      if (pattern.isChainOnlyPattern) return;
      const required = getRequiredCount(pattern, date, state.holidaySet, state.dailyOverrides, state);
      if (required <= 0) return;
      // この日に frozen で入っているこのマークの数
      let frozenCount = 0;
      Object.keys(state.perStaff).forEach((empId) => {
        if (!state.frozen.has(keyOf(empId, date))) return;
        if (state.matrix[keyOf(empId, date)] === pattern.displayMark) frozenCount += 1;
      });
      if (
        isFixedOnDate(pattern, date)
        && frozenCount > required
      ) {
        state.conflicts.hopeOverflow.push({
          date,
          patternName: pattern.name,
          mark: pattern.displayMark,
          frozenCount,
          required,
        });
        overCount += 1;
      }
    });
  });
  if (overCount > 0) {
    console.warn(`⚠️ 希望が固定人数を超える日が ${overCount} 件あります`);
  }
};

// =============================================================================
// Phase 3 & 4: 固定 → 柔軟 の埋め
// =============================================================================

// 固定/柔軟が満たせなかった時に、なぜ eligible が少なかったかを集計
const analyzeIneligibility = (state, date, pattern) => {
  const r = {
    excluded: 0,
    frozen: 0,
    alreadyAssigned: 0,
    jobType: 0,
    skillGroup: 0,
    monthlyLimit: 0,
    consecutive: 0,
    sameConsec: 0,
    incompat: 0,
  };
  Object.keys(state.perStaff).forEach((empId) => {
    const s = state.perStaff[empId];
    if (s.excludeFromAutoAssign) { r.excluded += 1; return; }
    if (state.frozen.has(keyOf(empId, date))) { r.frozen += 1; return; }
    if (state.matrix[keyOf(empId, date)]) { r.alreadyAssigned += 1; return; }
    if (!matchesJobType(s, pattern)) { r.jobType += 1; return; }
    if (!matchesSkillGroup(state, empId, pattern)) { r.skillGroup += 1; return; }
    if (!checkMonthlyLimits(state, empId, pattern)) { r.monthlyLimit += 1; return; }
    if (pattern.displayMark !== OFF_MARK) {
      // 寄与度ベース + 前後両方向で配置後の累計連勤値を予測
      const contribution = getConsecutiveContribution(
        pattern.displayMark, state.patterns, state.patternByMark
      );
      if (contribution > 0) {
        const wouldBe = getWouldBeConsecutiveLength(state, empId, date, contribution);
        const limit = state.policy.maxConsecutiveWorkDays ?? 6;
        if (wouldBe > limit) { r.consecutive += 1; return; }
      }
    }
    if (pattern.maxSameConsecutive > 0) {
      const sameRun = getSameMarkConsecutive(state, empId, date, pattern.displayMark);
      if (sameRun + 1 > pattern.maxSameConsecutive) { r.sameConsec += 1; return; }
    }
    if (violatesIncompatibility(state, empId, date, pattern)) { r.incompat += 1; return; }
  });
  return r;
};

const fillPatternsForDay = (state, date, patterns, mode /* 'fixed' | 'flex' */) => {
  patterns.forEach((pattern) => {
    if (pattern.isChainOnlyPattern) return;
    // この日にこのパターンの override があれば flexibility に関わらず「固定」扱い
    const hasOverride =
      state.dailyOverrides?.[date]?.[pattern.id] != null;
    if (!hasOverride) {
      // ⭐ 曜日別の柔軟性タイプを判定（pattern.flexibilityByDayOfWeek が優先、なければ flexibilityType）
      const flexForDate = getFlexibilityForDate(pattern, date);
      if (mode === "fixed" && flexForDate !== "fixed") return;
      if (mode === "flex" && flexForDate === "fixed") return;
    } else {
      // override の日は fixed pass のみで処理（flex pass では skip）
      if (mode !== "fixed") return;
    }

    const required = getRequiredCount(pattern, date, state.holidaySet, state.dailyOverrides, state);
    if (required <= 0) return;

    // 🔥 required の充足は countForRequirement（excludedCountGroups 除外）で判定
    let currentCount = countForRequirement(state, date, pattern.displayMark, pattern);
    // override または fixed mode は exact target、flex は minRequired
    const treatAsFixed = mode === "fixed" || hasOverride;
    const target = treatAsFixed ? required : Math.max(pattern.minRequired, 0);
    let need = target - currentCount;
    if (need <= 0) return;

    const eligible = Object.keys(state.perStaff).filter((empId) =>
      canAssign(state, empId, date, pattern)
    );
    if (eligible.length < need) {
      const reasons = analyzeIneligibility(state, date, pattern);
      if (treatAsFixed) {
        state.conflicts.fixedShortage.push({
          date,
          patternName: pattern.name,
          mark: pattern.displayMark,
          have: currentCount + eligible.length,
          required,
          reasons,
          isOverride: hasOverride,
        });
        console.warn(
          `  ⚠️ 固定不足 ${date} ${pattern.name}(${pattern.displayMark}) ${currentCount + eligible.length}/${required}人${hasOverride ? '（override）' : ''}`,
          reasons
        );
      } else {
        state.conflicts.flexShortage.push({
          date,
          patternName: pattern.name,
          mark: pattern.displayMark,
          have: currentCount + eligible.length,
          minRequired: target,
          reasons,
        });
      }
    }
    // pickCandidatesForDate はその日にチェーン全体が配置可能な候補を優先する
    const picks = pickCandidatesForDate(state, eligible, pattern, date, need);
    picks.forEach((empId) => {
      setCell(state, empId, date, pattern.displayMark);
      state.stats.totalAssigned += 1;
      // チェーン即時適用（best-effort：先約がある日はスキップ）。
      // 最終的に runChainPass でも保険として再走するが、ここで早期に置いて
      // 後続パスから follow セルを守る。
      if (pattern.postShiftIds?.length) {
        applyChain(state, empId, date, pattern.displayMark, 0);
      }
    });
  });
};

// 固定人数が完全に満たされるまで、ソフト制約を段階的に緩和して再試行する
//   ⚠️ 連勤上限（max_consecutive_work_days）は労働時間管理上の必須条件として
//      ハードキャップ扱い。緩和段階のいかなる段階でも超過しない（設定値そのまま）。
//   先に同パターン連勤・月間上限・相性ルールを緩めて穴埋めを試み、
//   それでも穴が残れば shortage として記録（ユーザーが手動で補正）。
//   緩和段階:
//     0: 全制約厳守
//     1: max_same_consecutive 無視（連勤上限は守る）
//     2: max_same_consecutive 無視 + 月間上限無視（連勤上限は守る）
//     3: max_same_consecutive 無視 + 月間上限無視 + 相性無視（最後の救済）
const runIterativeFixedPass = (state) => {
  const RELAX_STAGES = [
    { consecutiveBonus: 0, ignoreSameConsec: false, ignoreMonthly: false, ignoreIncompat: false },
    { consecutiveBonus: 0, ignoreSameConsec: true,  ignoreMonthly: false, ignoreIncompat: false },
    { consecutiveBonus: 0, ignoreSameConsec: true,  ignoreMonthly: true,  ignoreIncompat: false },
    { consecutiveBonus: 0, ignoreSameConsec: true,  ignoreMonthly: true,  ignoreIncompat: true  },
  ];

  // 反復のために frozen までの初期状態をスナップショット
  const snapshot = {
    matrix: { ...state.matrix },
    frozen: new Set(state.frozen),
    crossMonthWrites: new Map(state.crossMonthWrites),
    nextMatrix: { ...state.nextMatrix },
  };

  let best = null;
  for (let i = 0; i < RELAX_STAGES.length; i++) {
    // 各段階で初期状態に戻す
    state.matrix = { ...snapshot.matrix };
    state.frozen = new Set(snapshot.frozen);
    state.crossMonthWrites = new Map(snapshot.crossMonthWrites);
    state.nextMatrix = { ...snapshot.nextMatrix };
    state.conflicts.fixedShortage = [];
    state.conflicts.flexShortage = [];
    state.relaxation = RELAX_STAGES[i];

    runFixedPass(state);
    runFlexPass(state);

    const shortage = state.conflicts.fixedShortage.length;
    console.log(
      `🔁 反復 ${i + 1}/${RELAX_STAGES.length} (連勤 +${RELAX_STAGES[i].consecutiveBonus}, ` +
      `sameConsec=${RELAX_STAGES[i].ignoreSameConsec ? '無視' : '守'}, ` +
      `monthly=${RELAX_STAGES[i].ignoreMonthly ? '無視' : '守'}, ` +
      `incompat=${RELAX_STAGES[i].ignoreIncompat ? '無視' : '守'}): 固定不足 ${shortage} 件`
    );

    if (!best || shortage < best.shortage) {
      best = {
        shortage,
        matrix: { ...state.matrix },
        frozen: new Set(state.frozen),
        crossMonthWrites: new Map(state.crossMonthWrites),
        nextMatrix: { ...state.nextMatrix },
        fixedShortage: [...state.conflicts.fixedShortage],
        flexShortage: [...state.conflicts.flexShortage],
        relaxation: RELAX_STAGES[i],
      };
    }
    if (shortage === 0) {
      console.log(`  ✅ 固定人数を全て満たす配置に到達（段階 ${i + 1}）`);
      break;
    }
  }

  // 最良解を採用
  state.matrix = best.matrix;
  state.frozen = best.frozen;
  state.crossMonthWrites = best.crossMonthWrites;
  state.nextMatrix = best.nextMatrix;
  state.conflicts.fixedShortage = best.fixedShortage;
  state.conflicts.flexShortage = best.flexShortage;
  state.relaxation = best.relaxation;
  if (best.shortage > 0) {
    console.warn(
      `⚠️ 全段階でも固定不足 ${best.shortage} 件が残りました。最良結果を採用。`
    );
  }
};

// shortage 残った固定パターンの空きを、近傍の非固定セルを 休 にスワップして連勤を切る方式で修復
//   - frozen は触らない
//   - fixed 既配置のセルも触らない（他の fixed shortage を作らない）
//   - 候補は skill_group / job_type にマッチする staff のみ
const tryRepairOneShortage = (state, date, pattern) => {
  const fixedMarks = new Set(
    state.patterns.filter((p) => p.flexibilityType === "fixed").map((p) => p.displayMark)
  );

  const candidates = Object.keys(state.perStaff).filter((empId) => {
    const s = state.perStaff[empId];
    if (s.excludeFromAutoAssign) return false;
    if (state.frozen.has(keyOf(empId, date))) return false;
    if (state.matrix[keyOf(empId, date)]) return false; // 既に当日他配置済み
    if (!matchesJobType(s, pattern)) return false;
    if (!matchesSkillGroup(state, empId, pattern)) return false;
    if (violatesIncompatibility(state, empId, date, pattern)) return false;
    return true;
  });

  // 配置を試す共通ロジック：チェーンが置けなければ start のみ置く（best-effort）
  const tryPlace = (empId) => {
    if (setCellWithChain(state, empId, date, pattern.displayMark)) return true;
    // チェーン全配置不可だが、start のみ置きたい場合（後で runChainPass で部分回復試行）
    setCell(state, empId, date, pattern.displayMark);
    if (pattern.postShiftIds?.length) applyChain(state, empId, date, pattern.displayMark, 0);
    return true;
  };

  // 配置するパターン自身の寄与度（0.5/1 など）
  const placeContrib = getConsecutiveContribution(
    pattern.displayMark, state.patterns, state.patternByMark
  );

  // 戦略1: 連勤OKで即配置できる候補
  for (const empId of candidates) {
    const limit = state.policy.maxConsecutiveWorkDays ?? 6;
    const wouldBe = getWouldBeConsecutiveLength(state, empId, date, placeContrib);
    if (wouldBe <= limit) {
      if (tryPlace(empId)) return true;
    }
  }

  // 戦略2: 連勤超過 → 近傍の非固定/非チェーンセルを 休 にスワップして連勤を切る
  for (const empId of candidates) {
    const limit = state.policy.maxConsecutiveWorkDays ?? 6;
    let wouldBe = getWouldBeConsecutiveLength(state, empId, date, placeContrib);
    if (wouldBe <= limit) continue;
    let d = dayjs(date).subtract(1, "day");
    let swapDate = null;
    let swappedFromMark = null;
    for (let i = 0; i < limit + 2 && i < 30; i++) {
      const ds = d.format("YYYY-MM-DD");
      if (!state.dates.includes(ds)) break;
      if (!state.frozen.has(keyOf(empId, ds))) {
        const v = state.matrix[keyOf(empId, ds)];
        if (
          v && v !== OFF_MARK
          && !fixedMarks.has(v)
          && !isChainRelatedCell(state, empId, ds)
        ) {
          swapDate = ds;
          swappedFromMark = v;
          break;
        }
      }
      d = d.subtract(1, "day");
    }
    if (!swapDate) continue;
    state.matrix[keyOf(empId, swapDate)] = OFF_MARK;
    wouldBe = getWouldBeConsecutiveLength(state, empId, date, placeContrib);
    if (wouldBe > limit) {
      state.matrix[keyOf(empId, swapDate)] = swappedFromMark;
      continue;
    }
    if (tryPlace(empId)) return true;
    state.matrix[keyOf(empId, swapDate)] = swappedFromMark;
  }

  // 戦略3: 別スタッフを displace する（候補が他の非固定パターン上にいる場合、
  //   その候補のセルをまず空にしてから assign する）
  const displaceCandidates = Object.keys(state.perStaff).filter((empId) => {
    const s = state.perStaff[empId];
    if (s.excludeFromAutoAssign) return false;
    if (state.frozen.has(keyOf(empId, date))) return false;
    if (!matchesJobType(s, pattern)) return false;
    if (!matchesSkillGroup(state, empId, pattern)) return false;
    if (violatesIncompatibility(state, empId, date, pattern)) return false;
    const cur = state.matrix[keyOf(empId, date)];
    if (!cur) return false;
    if (fixedMarks.has(cur)) return false;
    if (isChainRelatedCell(state, empId, date)) return false;
    return true;
  });
  for (const empId of displaceCandidates) {
    const prev = state.matrix[keyOf(empId, date)];
    state.matrix[keyOf(empId, date)] = "";
    const limit = state.policy.maxConsecutiveWorkDays ?? 6;
    const wouldBe = getWouldBeConsecutiveLength(state, empId, date, placeContrib);
    if (wouldBe > limit) {
      state.matrix[keyOf(empId, date)] = prev;
      continue;
    }
    if (tryPlace(empId)) return true;
    state.matrix[keyOf(empId, date)] = prev;
  }

  // 戦略4: 「fixed 同パターンクロススワップ」
  //   既に別の日に同 pattern を持っているスタッフ A を date に移し、A の旧位置を別候補 B で埋める。
  //   A は本来 (date) で fixed の役を担うべきだったが、greedy で別日に行ってしまっただけ。
  //   - 候補 A: 別の日 D' で pattern.displayMark を持ち、その日が非 frozen、非チェーン
  //   - 候補 B: 当日 date で skill_group マッチで pattern を埋められる
  //   - 移動: A の D' を空にし、A を date に置き、B を D' に置く
  const allDates = state.dates;
  for (const candA of candidates) {
    for (const altDate of allDates) {
      if (altDate === date) continue;
      if (state.matrix[keyOf(candA, altDate)] !== pattern.displayMark) continue;
      if (state.frozen.has(keyOf(candA, altDate))) continue;
      if (isChainRelatedCell(state, candA, altDate)) continue;

      // この A は (altDate, pattern) から取り外せる
      // しかし、altDate の pattern も required を満たす必要がある → B を探す
      const requiredOnAlt = getRequiredCount(pattern, altDate, state.holidaySet, state.dailyOverrides, state);
      const currentOnAlt = countOnDate(state, altDate, pattern.displayMark);
      // altDate から A を抜くと currentOnAlt - 1 になる。required を満たすには代替 B が必要
      // 単純化: A を移しても altDate の充足が崩れないように、altDate に余裕があるか確認
      if (currentOnAlt - 1 >= requiredOnAlt) {
        // 充足には影響なし → 単純に A を移す
        const aPrevAlt = state.matrix[keyOf(candA, altDate)];
        state.matrix[keyOf(candA, altDate)] = "";
        if (tryPlace(candA)) return true;
        state.matrix[keyOf(candA, altDate)] = aPrevAlt;
        continue;
      }
      // altDate で B を補充する必要 → B 候補（A 以外で altDate に pattern を埋められる）を探す
      const aPrevAlt = state.matrix[keyOf(candA, altDate)];
      state.matrix[keyOf(candA, altDate)] = "";
      const bCandidates = Object.keys(state.perStaff).filter((empB) => {
        if (empB === candA) return false;
        if (state.frozen.has(keyOf(empB, altDate))) return false;
        if (state.matrix[keyOf(empB, altDate)]) return false;
        if (!matchesJobType(state.perStaff[empB], pattern)) return false;
        if (!matchesSkillGroup(state, empB, pattern)) return false;
        if (violatesIncompatibility(state, empB, altDate, pattern)) return false;
        return true;
      });
      let placedB = false;
      for (const candB of bCandidates) {
        const limit2 = state.policy.maxConsecutiveWorkDays ?? 6;
        const wouldBe2 = getWouldBeConsecutiveLength(state, candB, altDate, placeContrib);
        if (wouldBe2 > limit2) continue;
        // B を altDate に置く
        if (setCellWithChain(state, candB, altDate, pattern.displayMark)) {
          placedB = true;
          break;
        }
      }
      if (!placedB) {
        state.matrix[keyOf(candA, altDate)] = aPrevAlt;
        continue;
      }
      // A を date に置く
      if (tryPlace(candA)) return true;
      // A の配置失敗 → 全部 revert
      state.matrix[keyOf(candA, altDate)] = aPrevAlt;
      // B の配置は revert したいが setCellWithChain で frozen を増やしている可能性
      // 単純化として B の altDate も空に戻す
      state.matrix[keyOf(state.perStaff[bCandidates[0]]?.employeeId || "", altDate)] = "";
    }
  }

  return false;
};

// 柔軟人数の不足も含めて両方を修復対象にする
const collectShortages = (state) => {
  const list = [];
  state.dates.forEach((date) => {
    state.patterns.forEach((pattern) => {
      if (pattern.isChainOnlyPattern || !pattern.displayMark) return;
      const required = getRequiredCount(pattern, date, state.holidaySet, state.dailyOverrides, state);
      if (required <= 0) return;
      const cur = countOnDate(state, date, pattern.displayMark);
      const hasOverride = state.dailyOverrides?.[date]?.[pattern.id] != null;
      const isFixed = isFixedOnDate(pattern, date) || hasOverride;
      const target = isFixed ? required : Math.max(pattern.minRequired || 0, 0);
      if (target <= 0) return;
      if (cur < target) {
        list.push({
          date,
          pattern,
          need: target - cur,
          isFixed,
          isOverride: hasOverride,
        });
      }
    });
  });
  return list;
};

const runRepairPass = (state) => {
  let totalRepaired = 0;
  for (let attempt = 0; attempt < 50; attempt++) {
    const remaining = collectShortages(state);
    if (remaining.length === 0) {
      console.log(`✅ 修復パス完了：不足ゼロ（${attempt} 回反復、合計 ${totalRepaired} 件修復）`);
      state.conflicts.fixedShortage = [];
      state.conflicts.flexShortage = [];
      return;
    }
    // 固定不足を優先、次に need の多いもの順
    remaining.sort((a, b) => {
      if (a.isFixed !== b.isFixed) return a.isFixed ? -1 : 1;
      return b.need - a.need;
    });
    let progress = false;
    for (const sh of remaining) {
      for (let k = 0; k < sh.need; k++) {
        if (tryRepairOneShortage(state, sh.date, sh.pattern)) {
          totalRepaired += 1;
          progress = true;
        } else {
          break;
        }
      }
    }
    if (!progress) break;
  }

  // 最終再計算
  const finalRemaining = collectShortages(state);
  state.conflicts.fixedShortage = finalRemaining.filter((r) => r.isFixed).map((r) => ({
    date: r.date,
    patternName: r.pattern.name,
    mark: r.pattern.displayMark,
    have: countOnDate(state, r.date, r.pattern.displayMark),
    required: getRequiredCount(r.pattern, r.date, state.holidaySet, state.dailyOverrides, state),
    reasons: analyzeIneligibility(state, r.date, r.pattern),
  }));
  state.conflicts.flexShortage = finalRemaining.filter((r) => !r.isFixed).map((r) => ({
    date: r.date,
    patternName: r.pattern.name,
    mark: r.pattern.displayMark,
    have: countOnDate(state, r.date, r.pattern.displayMark),
    minRequired: Math.max(r.pattern.minRequired || 0, 0),
    reasons: analyzeIneligibility(state, r.date, r.pattern),
  }));
  if (state.conflicts.fixedShortage.length > 0 || state.conflicts.flexShortage.length > 0) {
    console.warn(
      `❌ 修復パス完了後 固定不足 ${state.conflicts.fixedShortage.length}件 / 柔軟不足 ${state.conflicts.flexShortage.length}件 残ります`
    );
  } else {
    console.log(`✅ 修復パス完了：全不足ゼロ（合計 ${totalRepaired} 件修復）`);
  }
};

const runFixedPass = (state) => {
  const fixed = state.patterns
    .filter(
      (p) =>
        p.flexibilityType === "fixed"
        && !p.isChainOnlyPattern
        && p.displayMark
    )
    .sort((a, b) => a.priority - b.priority);
  console.log(`  🔒 固定人数パターン ${fixed.length}件:`, fixed.map((p) => `${p.name}(${p.displayMark})`));
  getOrderedDates(state).forEach((date) => fillPatternsForDay(state, date, fixed, "fixed"));
  console.log(`  ➕ 固定人数パターン埋め完了`);
};

const runFlexPass = (state) => {
  const flex = state.patterns
    .filter(
      (p) =>
        p.flexibilityType !== "fixed"
        && !p.isChainOnlyPattern
        && p.displayMark
    )
    .sort((a, b) => a.priority - b.priority);
  console.log(`  📐 柔軟人数パターン ${flex.length}件:`, flex.map((p) => `${p.name}(${p.displayMark})`));
  getOrderedDates(state).forEach((date) => fillPatternsForDay(state, date, flex, "flex"));
  console.log(`  ➕ 柔軟人数パターン埋め完了`);
};

// =============================================================================
// Phase 4.7: 日勤のみスタッフの埋め
//   役職者・パート等の dayShiftOnly スタッフについて、希望や固定パターンで
//   未確定の空欄セルを「日勤マーク」で埋める。
//   ただし連続勤務日数の上限を超える日は休み (OFF_MARK) を入れる。
//   - 日勤マークが解決できないスタッフはスキップ（通常の default fill に委ねる）
//   - frozen セル / 既に値があるセルは触らない
//   - pickCandidatesForDate 側で「非日勤パターン」候補からは既に除外済み
// =============================================================================
const runDayShiftOnlyFill = (state) => {
  const maxCons = state.policy?.maxConsecutiveWorkDays || 6;
  let filled = 0;
  let restAdded = 0;
  let weekendOff = 0;
  let skipped = 0;

  Object.entries(state.perStaff).forEach(([empId, meta]) => {
    if (!meta?.dayShiftOnly) return;
    const mode = meta.dayShiftOnlyMode || 'all'; // 'all' or 'weekdays'
    const dayMark = state.dayShiftMarkByStaff?.[empId];
    if (!dayMark) {
      skipped += 1;
      return;
    }
    const dayPattern = state.patternByMark[dayMark];
    const patternMaxCons = dayPattern?.maxConsecutive || maxCons;
    const limit = Math.min(maxCons, patternMaxCons);

    const dayContribOnly = getConsecutiveContribution(dayMark, state.patterns, state.patternByMark);
    state.dates.forEach((date) => {
      const key = keyOf(empId, date);
      if (state.matrix[key]) return;       // 既に何か入っている
      if (state.frozen.has(key)) return;   // 固定セル

      // 'weekdays' モード: 土日祝は公休
      if (mode === 'weekdays' && isWeekendOrHoliday(date, state.holidaySet)) {
        state.matrix[key] = OFF_MARK;
        weekendOff += 1;
        return;
      }

      // パターンの曜日別 required が 0 ならその日にそのマークは配置しない（→ 公休）
      if (dayPattern) {
        const requiredForDay = getRequiredCount(
          dayPattern, date, state.holidaySet, state.dailyOverrides, state
        );
        if (requiredForDay <= 0) {
          state.matrix[key] = OFF_MARK;
          restAdded += 1;
          return;
        }
      }

      // 配置後の連勤累計値（前後の既存セル + これから置く日勤の寄与度）
      const wouldBeLen = getWouldBeConsecutiveLength(state, empId, date, dayContribOnly);
      if (wouldBeLen > limit) {
        state.matrix[key] = OFF_MARK;
        restAdded += 1;
      } else {
        state.matrix[key] = dayMark;
        filled += 1;
        const lenAfter = getWouldBeConsecutiveLength(state, empId, date, dayContribOnly);
        if (lenAfter > limit) {
          console.warn(
            `[dayShiftOnlyFill 違反生成] ${empId} ${date}: 日勤配置後の累計=${lenAfter} > limit=${limit}`
          );
        }
      }
    });
  });

  console.log(
    `☀️ runDayShiftOnlyFill: 日勤埋め ${filled} 件 / 休み追加 ${restAdded} 件 / 平日のみモード 土日祝公休 ${weekendOff} 件` +
    (skipped ? ` / 日勤マーク未解決 ${skipped}名スキップ` : '')
  );
};

// =============================================================================
// Phase 5: スマート default fill
//   残空白セルに「公休 (休)」と「職種別日勤マーク」を、以下の制約を満たすよう充填:
//     - 連続勤務日数 ≤ max_consecutive_work_days
//     - 月公休 ∈ [monthly_min, monthly_max]
//     - 公休が必要な分は均等に散らす
// =============================================================================

const runSmartDefaultFill = (state) => {
  const { monthlyMin, monthlyMax, maxConsecutiveWorkDays } = state.policy;
  let placedOff = 0;
  let placedDay = 0;
  let missingDayMark = 0;

  Object.values(state.perStaff).forEach((staff) => {
    if (staff.excludeFromAutoAssign) return;
    const empId = staff.employeeId;
    const dayMark = state.dayShiftMarkByStaff[empId];
    if (!dayMark) {
      missingDayMark += 1;
      return;
    }

    let offCount = 0;
    state.dates.forEach((d) => {
      if (state.matrix[keyOf(empId, d)] === OFF_MARK) offCount += 1;
    });

    const dayPattern = state.patternByMark[dayMark];
    state.dates.forEach((date, idx) => {
      const key = keyOf(empId, date);
      const v = state.matrix[key];
      if (v) return; // 既存値あり: スキップ
      if (state.frozen.has(key)) return;

      // ★ パターンの曜日別 required が 0 ならその日にそのマークは配置しない（→ 公休）。
      //    例: 「土日のみ 0 人固定」のパターンが日勤系の場合、土日も公休になる。
      if (dayPattern) {
        const requiredForDay = getRequiredCount(
          dayPattern, date, state.holidaySet, state.dailyOverrides, state
        );
        if (requiredForDay <= 0) {
          state.matrix[key] = OFF_MARK;
          offCount += 1;
          placedOff += 1;
          return;
        }
      }

      const remainingDates = state.dates.length - idx;
      const remainingOffNeeded = Math.max(0, monthlyMin - offCount);

      // ★ 配置後の連勤累計値を予測（前後両方向の既存セル + これから置く日勤マークの寄与度）
      const dayContrib = getConsecutiveContribution(dayMark, state.patterns, state.patternByMark);
      const wouldBeLen = getWouldBeConsecutiveLength(state, empId, date, dayContrib);

      let placeOff = false;
      // ① 連勤上限チェック: 配置後の連勤長が limit を超えるなら休にする。
      //    例: limit=5.5, wouldBeLen=6 → 休配置
      if (wouldBeLen > maxConsecutiveWorkDays) {
        placeOff = true;
      }
      // ② 残日数で休 min を満たせない → 強制で休
      else if (remainingOffNeeded >= remainingDates) {
        placeOff = true;
      }
      // ③ 残量プレッシャーや連勤近接で休を入れる
      else if (offCount < monthlyMin) {
        const pressure = remainingOffNeeded / remainingDates;
        if (wouldBeLen >= maxConsecutiveWorkDays - 1 && pressure > 0.15) {
          placeOff = true;
        } else if (pressure > 0.45) {
          placeOff = true;
        }
      }
      // ④ 公休が monthly_max に到達したら以降は強制で日勤
      //    ただし連勤上限超過になる場合は ① を優先（連勤厳守）。
      if (offCount >= monthlyMax && wouldBeLen <= maxConsecutiveWorkDays) {
        placeOff = false;
      }

      if (placeOff) {
        setCell(state, empId, date, OFF_MARK);
        offCount += 1;
        placedOff += 1;
      } else {
        setCell(state, empId, date, dayMark);
        placedDay += 1;
        // 配置後の検証: 念のため両方向再計算してログ
        const lenAfter = getWouldBeConsecutiveLength(state, empId, date);
        if (lenAfter > maxConsecutiveWorkDays) {
          console.warn(
            `[smartFill 違反生成] ${empId} ${date}: 日勤配置後の連勤=${lenAfter} > limit=${maxConsecutiveWorkDays}`
          );
        }
      }
    });
  });

  console.log(
    `  ➕ スマート default fill: 公休 ${placedOff}件 / 日勤 ${placedDay}件 / 日勤マーク未設定 ${missingDayMark}名`
  );
};

// =============================================================================
// Phase 6: 後続シフト連鎖
// =============================================================================

const applyChain = (state, empId, fromDate, fromMark, depth = 0) => {
  if (depth >= CHAIN_LIMIT) return;
  const pattern = state.patternByMark[fromMark];
  if (!pattern?.postShiftIds?.length) return;
  const nextDate = dayjs(fromDate).add(1, "day").format("YYYY-MM-DD");
  const ref = pattern.postShiftIds[0];
  const nextPattern = state.patternById[ref] || state.patternByMark[ref];
  if (!nextPattern?.displayMark) return;

  if (state.dates.includes(nextDate)) {
    if (state.frozen.has(keyOf(empId, nextDate))) return;
    // 既に入っている値が異なる場合は上書きしてロック
    setCell(state, empId, nextDate, nextPattern.displayMark);
    state.frozen.add(keyOf(empId, nextDate));
  } else {
    if (state.nextMatrixOriginal[keyOf(empId, nextDate)]) return; // 既存値を尊重
    state.crossMonthWrites.set(keyOf(empId, nextDate), {
      organization_id: state.organizationId,
      employee_id: empId,
      shift_date: nextDate,
      shift_type: nextPattern.displayMark || null,
      updated_at: new Date().toISOString(),
    });
    state.nextMatrix[keyOf(empId, nextDate)] = nextPattern.displayMark;
  }
  applyChain(state, empId, nextDate, nextPattern.displayMark, depth + 1);
};

const runChainPass = (state) => {
  state.dates.forEach((date) => {
    Object.keys(state.perStaff).forEach((empId) => {
      const mark = state.matrix[keyOf(empId, date)];
      if (!mark) return;
      const p = state.patternByMark[mark];
      if (!p?.postShiftIds?.length) return;
      applyChain(state, empId, date, mark, 0);
    });
  });
  console.log(`  ➕ 連鎖適用完了（翌月キュー: ${state.crossMonthWrites.size}件）`);
};

// =============================================================================
// Phase 6.5: 連勤違反の連鎖修復
//   違反スタッフの「触れる日」のシフトを別スタッフに引き受けてもらう（=勤務交換）。
//   1対1で代替が見つからない時は、代替候補が「自分を退ければ違反になる」場合でも、
//   さらにその候補のシフトを別の人に振り替える、という連鎖で全体最適化する。
//   失敗時は changes を巻き戻して原状復帰。
// =============================================================================

// 1スタッフの現在の matrix から連勤情報を計算（最大連勤値とその range）
const computeConsecutiveRuns = (state, empId) => {
  const runs = [];
  let run = 0;
  let startDate = null;
  const runDates = [];
  state.dates.forEach((date) => {
    const v = state.matrix[keyOf(empId, date)];
    const explicit = v ? hasExplicitContribution(v, state.patterns, state.patternByMark) : false;
    const isFollow = v && !explicit ? isChainFollowMark(state, empId, date) : false;
    const c = v
      ? (isFollow ? 0 : getConsecutiveContribution(v, state.patterns, state.patternByMark))
      : 0;
    if (c > 0) {
      if (run === 0) { startDate = date; runDates.length = 0; }
      run += c;
      runDates.push(date);
    } else {
      if (run > 0) runs.push({ start: startDate, end: runDates[runDates.length - 1], length: run, dates: [...runDates] });
      run = 0;
      startDate = null;
      runDates.length = 0;
    }
  });
  if (run > 0) runs.push({ start: startDate, end: runDates[runDates.length - 1], length: run, dates: [...runDates] });
  return runs;
};

// 1スタッフが現在違反している連勤 run（最大長）を返す。違反なしなら null。
const detectMaxViolation = (state, empId) => {
  const limit = state.policy.maxConsecutiveWorkDays;
  const runs = computeConsecutiveRuns(state, empId);
  let worst = null;
  runs.forEach((r) => {
    if (r.length > limit && (!worst || r.length > worst.length)) worst = r;
  });
  if (!worst) return null;
  return { empId, ...worst };
};

// 全スタッフの違反を収集
const collectConsecutiveViolationsForRepair = (state) => {
  const out = [];
  Object.values(state.perStaff || {}).forEach((s) => {
    if (s.excludeFromAutoAssign) return;
    const v = detectMaxViolation(state, s.employeeId);
    if (v) out.push(v);
  });
  // 長い違反から先に直す（深刻なものを優先）
  out.sort((a, b) => b.length - a.length);
  return out;
};

// セルを「触れる」か（修復のために自由に変更してよいか）
//   - frozen でない
//   - 希望由来でない（hopes/adminHopes に該当しない）
//   - 既に OFF_MARK ではない
//   ※固定モードのセルでも、代替が見つかれば動かして良い（findReplacementForChain で代替確保）。
//   ※chain follow セルは identifyChainSegment が head を取り出してから動かす。
const isCellSwappableForRepair = (state, empId, date) => {
  const k = keyOf(empId, date);
  if (state.frozen.has(k)) return false;
  const v = state.matrix[k];
  if (!v || v === OFF_MARK) return false;
  if (state.hopes && state.hopes[k] != null && state.hopes[k] !== "") return false;
  return true;
};

// 指定セル (empId, date) が含まれるチェーンセグメント（連続シフト群）を返す。
//   - そのセルが head なら、後続 (postShiftIds 連鎖) を多段で追う
//   - そのセルが follow なら、前の head まで遡って head から再構築
//   - 多段（夜勤→明け→明け休 など）に対応
//   返り値: [{ date, mark }, ...] 時系列順
const identifyChainSegment = (state, empId, date) => {
  const cells = [];
  // まず head を特定（このセル自体 or 遡って head）
  let headDate = date;
  for (let i = 0; i < 30; i++) {
    if (!isChainFollowMark(state, empId, headDate)) break;
    const prevDate = dayjs(headDate).subtract(1, 'day').format('YYYY-MM-DD');
    if (!state.dates.includes(prevDate)) break;
    headDate = prevDate;
  }

  // head から後続を多段で辿る
  let cur = headDate;
  for (let i = 0; i < 30; i++) {
    const mark = state.matrix[keyOf(empId, cur)];
    if (!mark) break;
    if (state.frozen.has(keyOf(empId, cur)) && cur !== date) {
      // 範囲外の frozen に当たったら停止
      break;
    }
    cells.push({ date: cur, mark });

    const nextDate = dayjs(cur).add(1, 'day').format('YYYY-MM-DD');
    if (!state.dates.includes(nextDate)) break;
    // 次の日が follow か判定（=今日のパターンが postShiftIds を持ち、それに合致）
    const pattern = state.patternByMark[mark];
    const postIds = pattern?.postShiftIds || [];
    if (postIds.length === 0) break;
    const nextMark = state.matrix[keyOf(empId, nextDate)];
    if (!nextMark) break;
    const nextIsFollow = postIds.some((ref) => {
      const target = state.patternById[ref] || state.patternByMark[ref];
      return target?.displayMark === nextMark;
    });
    if (!nextIsFollow) break;
    cur = nextDate;
  }
  return cells;
};

// チェーン全体を引き受け可能なスタッフを優先度順で列挙
//   - 各セルが空 or 触れる（hope/frozen でない）
//   - 各セルで canAssign（職種・スキル・月間上限・相性・連勤）を満たす
const findReplacementForChain = (state, chainCells, excludeEmpId) => {
  const limit = state.policy.maxConsecutiveWorkDays;
  const candidates = [];
  Object.keys(state.perStaff).forEach((empId) => {
    if (empId === excludeEmpId) return;
    const s = state.perStaff[empId];
    if (s.excludeFromAutoAssign) return;

    // 各セルの既存値で許容するもの:
    //   - 空 / OFF_MARK → そのまま置ける（コスト 0）
    //   - 別マークだが触れる（hope/frozen でない、chain follow でない、
    //     チェーン開始でない、明示寄与度設定が無い単独勤務） → 玉突きで動かす（コスト +）
    //   - 同マーク / 触れない → 候補から除外
    let displaceCost = 0;
    let feasibleStruct = true;
    for (const cell of chainCells) {
      const k = keyOf(empId, cell.date);
      if (state.frozen.has(k)) { feasibleStruct = false; break; }
      const existing = state.matrix[k];
      if (!existing || existing === OFF_MARK) continue; // 動かす必要なし
      if (existing === cell.mark) { feasibleStruct = false; break; } // 既に同じ
      if (state.hopes && state.hopes[k]) { feasibleStruct = false; break; }
      if (isChainFollowMark(state, empId, cell.date)) { feasibleStruct = false; break; }
      const existingPat = state.patternByMark[existing];
      // 既存セル自身がチェーン start → 別チェーンを壊さないため動かさない
      if (existingPat?.postShiftIds?.length) { feasibleStruct = false; break; }
      // ※固定モードのセルでも動かして良い。canAssign が必要人数を維持できるか判定する。
      //   ここで弾くと「固定パターンに居る人を一切玉突きできない」という致命的制約になり、
      //   多くの違反が swappable=0 で諦められる原因になる。
      // 固定モードのセルを動かす場合、コストを高めに（=他候補を優先）
      displaceCost += existingPat && isFixedOnDate(existingPat, cell.date) ? 5 : 1;
    }
    if (!feasibleStruct) return;

    // 各セルで canAssign を通せるか（一時的にクリアしてチェック）
    const snapshot = chainCells.map((c) => state.matrix[keyOf(empId, c.date)]);
    chainCells.forEach((c) => { state.matrix[keyOf(empId, c.date)] = ''; });
    let canAll = true;
    for (const cell of chainCells) {
      const pattern = state.patternByMark[cell.mark];
      if (!pattern || !canAssign(state, empId, cell.date, pattern)) {
        canAll = false;
        break;
      }
      state.matrix[keyOf(empId, cell.date)] = cell.mark;
    }
    chainCells.forEach((c, i) => { state.matrix[keyOf(empId, c.date)] = snapshot[i]; });
    if (!canAll) return;

    const slack = limit - getWouldBeConsecutiveLength(state, empId, chainCells[0].date, 0);
    candidates.push({ empId, displaceCost, slack });
  });
  // 玉突きコスト小 → 連勤余裕大 の順
  candidates.sort((a, b) => {
    if (a.displaceCost !== b.displaceCost) return a.displaceCost - b.displaceCost;
    return b.slack - a.slack;
  });
  return candidates.map((c) => c.empId);
};

// 1つの違反を「チェーン単位」の連鎖交換で修復する。
//   - 違反日のセルが含まれるチェーン全体（夜勤→明け→…）を1単位として
//     違反者から取り外す → 代替スタッフにチェーン全体を渡す
//   - これにより「夜勤を消して明けだけ残る」というチェーン崩壊を防ぐ
//   - 必要人数が満たせない代替は採用しない（=穴は作らない）
//   - 代替が見つからない場合は、その変更経路を諦めて別経路を試す
const tryRepairConsecutiveViolation = (state, violation, depth, maxDepth, changes, visited) => {
  if (depth >= maxDepth) return false;
  const vKey = `${violation.empId}_${violation.start}_${violation.end}_${depth}`;
  if (visited.has(vKey)) return false;
  visited.add(vKey);

  // 違反 run の中で、中央寄り（=切れば前後ともに limit 内に収まりやすい）の日を優先
  const swappable = violation.dates
    .map((d, i) => ({ date: d, distFromCenter: Math.abs(i - (violation.dates.length - 1) / 2) }))
    .filter((x) => isCellSwappableForRepair(state, violation.empId, x.date))
    .sort((a, b) => a.distFromCenter - b.distFromCenter)
    .map((x) => x.date);

  const processed = new Set(); // 同じチェーン head を二度試さない
  for (const date of swappable) {
    // この日が含まれるチェーンセグメント全体を取り出す
    const chainCells = identifyChainSegment(state, violation.empId, date);
    if (chainCells.length === 0) continue;
    const headKey = `${chainCells[0].date}_${chainCells[0].mark}`;
    if (processed.has(headKey)) continue;
    processed.add(headKey);

    // チェーン全体に「触れる」状態か（最低でも head が hope/frozen でないこと）
    const headSwappable = chainCells.every((c) =>
      !state.frozen.has(keyOf(violation.empId, c.date))
      && !(state.hopes && state.hopes[keyOf(violation.empId, c.date)])
    );
    if (!headSwappable) continue;

    const localChanges = [];
    const apply = (k, newVal) => {
      localChanges.push({ key: k, before: state.matrix[k] });
      state.matrix[k] = newVal == null ? '' : newVal;
    };
    const revertLocal = () => {
      for (let i = localChanges.length - 1; i >= 0; i--) {
        state.matrix[localChanges[i].key] = localChanges[i].before;
      }
    };

    // 🔥 monthly_max ガード: 違反者の OFF カウントが上限を超えるなら、この経路はスキップ
    const monthlyMax = state.policy.monthlyMax ?? Infinity;
    let currentOffSelf = 0;
    state.dates.forEach((d) => {
      if (state.matrix[keyOf(violation.empId, d)] === OFF_MARK) currentOffSelf += 1;
    });
    // 既に OFF だったセルを除いて、新たに OFF になる数を数える
    const newOffsForSelf = chainCells.filter(
      (c) => state.matrix[keyOf(violation.empId, c.date)] !== OFF_MARK
    ).length;
    if (currentOffSelf + newOffsForSelf > monthlyMax) {
      // 月間最大休日数を超えてしまう → この日では諦める
      continue;
    }

    // (1) 違反者からチェーン全体を OFF にする
    chainCells.forEach((c) => apply(keyOf(violation.empId, c.date), OFF_MARK));

    // (2) 違反解消したか？
    const afterSelf = detectMaxViolation(state, violation.empId);

    // (3) 各チェーンセルの必要人数チェック
    const shortageCells = chainCells.filter((c) => {
      const pat = state.patternByMark[c.mark];
      if (!pat) return false;
      const req = getRequiredCount(pat, c.date, state.holidaySet, state.dailyOverrides, state);
      const cur = countOnDate(state, c.date, c.mark);
      return cur < req;
    });

    // (4) 必要人数 OK ＆ 違反解消 → 代替不要、完了
    if (shortageCells.length === 0 && !afterSelf) {
      changes.push(...localChanges);
      return true;
    }

    // (5) 必要人数 OK だが自分はまだ違反 → 再帰修復のみ
    if (shortageCells.length === 0 && afterSelf) {
      if (tryRepairConsecutiveViolation(state, afterSelf, depth + 1, maxDepth, localChanges, visited)) {
        changes.push(...localChanges);
        return true;
      }
      revertLocal();
      continue;
    }

    // (6) 必要人数不足 → チェーン全体を引き受け可能な代替を探す
    const replacements = findReplacementForChain(state, chainCells, violation.empId);
    if (replacements.length === 0) {
      // 代替なし → 穴は作らない方針なので revert して次の日
      revertLocal();
      continue;
    }

    let succeeded = false;
    for (const repId of replacements) {
      // 代替にチェーン全体を配置
      const repChanges = [];
      chainCells.forEach((c) => {
        const k = keyOf(repId, c.date);
        repChanges.push({ key: k, before: state.matrix[k] });
        localChanges.push({ key: k, before: state.matrix[k] });
        state.matrix[k] = c.mark;
      });

      const repViolation = detectMaxViolation(state, repId);
      const selfStill = detectMaxViolation(state, violation.empId);

      if (!repViolation && !selfStill) {
        succeeded = true;
        break;
      }

      const nextViolation = repViolation || selfStill;
      if (tryRepairConsecutiveViolation(state, nextViolation, depth + 1, maxDepth, localChanges, visited)) {
        const a = detectMaxViolation(state, repId);
        const b = detectMaxViolation(state, violation.empId);
        if (!a && !b) {
          succeeded = true;
          break;
        }
      }

      // この代替では駄目 → rep の配置のみ revert（自分の OFF はそのまま）
      while (
        localChanges.length > 0
        && repChanges.some((rc) => rc.key === localChanges[localChanges.length - 1].key)
      ) {
        const last = localChanges.pop();
        state.matrix[last.key] = last.before;
      }
    }

    if (succeeded) {
      changes.push(...localChanges);
      return true;
    }

    // どの代替でも駄目 → 全 revert して次の日
    revertLocal();
  }

  return false;
};

const runConsecutiveRepairPass = async (state, opts = {}) => {
  const maxDepth = opts.maxDepth || 10;
  // 🔥 maxPasses と noProgressLimit を緩和: 「100% まで粘る」方針で、
  //   実質はタイムアウトのみ効くように大きく取る。
  const maxPasses = opts.maxPasses || 2000;
  const timeoutMs = opts.timeoutMs || 180_000;
  const noProgressLimit = opts.noProgressLimit || 50;
  // 🔥 進捗 callback: progressStart 〜 progressEnd の範囲で経過時間に応じて更新。
  const onProgress = opts.onProgress || (() => {});
  const progressStart = opts.progressStart ?? 90;
  const progressEnd = opts.progressEnd ?? 93;

  const startTime = (typeof performance !== 'undefined' ? performance.now() : Date.now());
  const initialViolations = collectConsecutiveViolationsForRepair(state);
  if (initialViolations.length === 0) {
    console.log('✅ 連勤違反なし（修復スキップ）');
    return;
  }
  console.log(
    `🔧 連勤違反 ${initialViolations.length} 件を連鎖交換で修復試行 ` +
    `(maxDepth=${maxDepth}, maxPasses=${maxPasses}, timeout=${Math.round(timeoutMs / 1000)}s)...`
  );

  let totalResolved = 0;
  let pass = 0;
  let noProgressStreak = 0;
  let timedOut = false;

  // eslint-disable-next-line no-await-in-loop
  while (pass < maxPasses) {
    pass += 1;
    // 🔥 UI スレッドに制御を返す（"ページ応答なし" ダイアログ防止）
    // eslint-disable-next-line no-await-in-loop
    await yieldToBrowser();
    const elapsed = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - startTime;
    if (elapsed > timeoutMs) {
      timedOut = true;
      console.warn(`🔧 連鎖修復: タイムアウト (${Math.round(elapsed)}ms > ${timeoutMs}ms)`);
      break;
    }

    const violations = collectConsecutiveViolationsForRepair(state);
    if (violations.length === 0) {
      console.log(`  📍 Pass ${pass}: 違反 0 → 収束`);
      break;
    }
    console.log(`  📍 Pass ${pass}: 残り違反 ${violations.length} 件 (経過 ${Math.round(elapsed / 1000)}s)`);
    // 🔥 進捗バー更新: 経過時間ベースで progressStart → progressEnd を補間
    const progressRange = progressEnd - progressStart;
    const elapsedRatio = Math.min(1, elapsed / timeoutMs);
    onProgress(progressStart + Math.floor(elapsedRatio * progressRange));

    // 🔥 全違反が swappable=0 なら、これ以上 pass を回しても無駄なので即終了
    //   （これがないと 1秒で 50 pass を空回りする）
    const anySwappable = violations.some(
      (v) => v.dates.some((d) => isCellSwappableForRepair(state, v.empId, d))
    );
    if (!anySwappable) {
      console.log(`  📍 Pass ${pass}: 全違反が swappable=0（触れる日なし） → 即終了`);
      // 諦め違反として state に記録（次回以降スキップ）
      if (!state.impossibleConsecViolations) state.impossibleConsecViolations = new Set();
      violations.forEach((v) => {
        state.impossibleConsecViolations.add(`${v.empId}_${v.start}_${v.end}`);
      });
      break;
    }

    let resolvedThisPass = 0;
    for (const v of violations) {
      // タイムアウト中チェック
      const t = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - startTime;
      if (t > timeoutMs) { timedOut = true; break; }

      const re = detectMaxViolation(state, v.empId);
      if (!re) { resolvedThisPass += 1; continue; }

      const swappableDates = re.dates.filter((d) => isCellSwappableForRepair(state, re.empId, d));
      // この違反が触れない & 過去に諦め登録済みなら skip
      if (swappableDates.length === 0) continue;

      if (pass <= 3) {
        const cellSamples = re.dates.map((d) => `${d.slice(5)}:${state.matrix[keyOf(re.empId, d)]}`).join('|');
        console.log(`    🔍 ${re.empId} ${re.start}〜${re.end} (len=${re.length}) cells=[${cellSamples}] swappable=${swappableDates.length}`);
      }

      const changes = [];
      const visited = new Set();
      if (tryRepairConsecutiveViolation(state, re, 0, maxDepth, changes, visited)) {
        resolvedThisPass += 1;
        if (pass <= 3) console.log(`    ✅ ${re.empId}: 違反解消 (変更 ${changes.length} 件)`);
      } else {
        for (let i = changes.length - 1; i >= 0; i--) {
          state.matrix[changes[i].key] = changes[i].before;
        }
      }
    }
    totalResolved += resolvedThisPass;

    if (timedOut) break;
    if (resolvedThisPass === 0) {
      noProgressStreak += 1;
      if (noProgressStreak >= noProgressLimit) {
        console.log(`  📍 Pass ${pass}: 改善ゼロが ${noProgressStreak} 回連続 → 打ち切り`);
        break;
      }
    } else {
      noProgressStreak = 0;
    }
  }

  const elapsedMs = Math.round(
    (typeof performance !== 'undefined' ? performance.now() : Date.now()) - startTime
  );
  const remaining = collectConsecutiveViolationsForRepair(state);
  console.log(
    `🔧 連鎖修復完了: ${totalResolved} 件 解消 / 残り ${remaining.length} 件 ` +
    `(${pass} passes, ${elapsedMs}ms${timedOut ? ', タイムアウト' : ''})`
  );
};

// =============================================================================
// Phase 6.6: 柔軟パターンの過剰割り当て削減（保守的版）
//   設計方針:
//   - 柔軟は「ぴったり required」じゃなくてよい（ユーザー: 「無理に休みにしなくて良い」）
//   - monthly_max は単なる上限。重要なのは monthly_min が満たされること
//   - したがって過剰セルを削るのは「最低休日数(monthly_min)を満たしていない」スタッフだけ
//   - 連勤違反スタッフは別フェーズ（Phase 7.5）で対応するのでここでは削らない
//   - 監視 = 削減 ではない: 必要に応じて削るだけ
// =============================================================================
const runReduceOverAssignedFlex = (state) => {
  const { monthlyMin, monthlyMax } = state.policy;
  let removed = 0;
  const removedByPattern = {};

  // スタッフごとの公休カウントを事前計算
  const offCountByStaff = {};
  Object.keys(state.perStaff).forEach((empId) => {
    let c = 0;
    state.dates.forEach((d) => {
      if (state.matrix[keyOf(empId, d)] === OFF_MARK) c += 1;
    });
    offCountByStaff[empId] = c;
  });

  state.patterns.forEach((pattern) => {
    if (pattern.isChainOnlyPattern) return;
    const mark = pattern.displayMark;
    if (!mark || mark === OFF_MARK) return;

    state.dates.forEach((date) => {
      if (isFixedOnDate(pattern, date)) return;
      if (state.dailyOverrides?.[date]?.[pattern.id] != null) return;

      const required = getRequiredCount(pattern, date, state.holidaySet, state.dailyOverrides, state);
      const current = countOnDate(state, date, mark);
      let surplus = current - required;
      if (surplus <= 0) return;

      // 候補: このパターンに入ってる中で「monthly_min 未達」のスタッフのみ
      //   → 無理に休に変えない。monthly_min 達成済みの人はそのまま勤務で残す
      const candidates = Object.keys(state.perStaff)
        .filter((empId) => {
          if (state.matrix[keyOf(empId, date)] !== mark) return false;
          if (state.frozen.has(keyOf(empId, date))) return false;
          if (state.perStaff[empId].excludeFromAutoAssign) return false;
          if (state.hopes && state.hopes[`${empId}_${date}`]) return false;
          if (pattern.postShiftIds?.length) return false;
          if (isChainFollowMark(state, empId, date)) return false;
          if (offCountByStaff[empId] >= monthlyMax) return false;
          // 🔥 monthly_min 未達のスタッフのみ削減対象（無理に休にしない方針）
          if (offCountByStaff[empId] >= monthlyMin) return false;
          return true;
        });

      // 公休が少ない人（=今すぐ休が必要な人）を優先
      candidates.sort((a, b) => offCountByStaff[a] - offCountByStaff[b]);

      for (const empId of candidates) {
        if (surplus <= 0) break;
        state.matrix[keyOf(empId, date)] = OFF_MARK;
        offCountByStaff[empId] += 1;
        surplus -= 1;
        removed += 1;
        removedByPattern[mark] = (removedByPattern[mark] || 0) + 1;
      }
    });
  });

  if (removed > 0) {
    console.log(`✂️ 柔軟過剰削減（monthly_min 未達者のみ）: ${removed} セル → 休`, removedByPattern);
  } else {
    console.log(`✂️ 柔軟過剰削減: なし（全員 monthly_min 達成 or 余剰なし）`);
  }
};

// =============================================================================
// Phase 7.6: 固定不足のクロスパターン交換修復
//   既存ロジックの致命的欠陥: 連勤修復は「空 / OFF」のスタッフだけを代替候補と
//   していたが、現実には全員何かのシフトに入っている。固定不足も同じ問題。
//   実務的アプローチ:
//   - 不足パターン X の日 D に「別のパターン Y に入ってるスタッフ Z」を移す
//   - Y が「柔軟 or 設定なし」なら 1 人抜けても OK（Y は最低人数を維持できればOK）
//   - Y が「固定」or「最低人数すれすれ」なら、別の人 W を別パターンから Y に動かす（連鎖）
//   これにより、希望取り下げなしで全員のシフトを再配置できる。
// =============================================================================

// =============================================================================
// Phase 7.4: シンプル順次修復（1日目から月末まで素直に過不足を直接振替）
//   方針: 複雑な玉突きや揺さぶりに走る前に、最も素直な修正を順番に試す。
//
//   各日について：
//     1. 固定不足パターン X を列挙
//     2. その日に「触れる候補」プールを作る:
//        a) 柔軟パターンに余裕がある人（current > minRequired）
//        b) OFF だが希望休みでない人
//     3. 不足分だけプールから X に振り替える（canAssign + ハード制約チェック付き）
//     4. 固定過剰パターン Y を列挙し、超過分を OFF or 不足パターンに振替
//
//   触らないもの: frozen / hope / 固定モード / chain start / chain follow / イベント
// =============================================================================

// =============================================================================
// Phase 7.46: 時間軸 swap 修復（積極配置版）
//   方針: 「即配置可能」な候補を見つけたら必ず配置する。UI で確認させず、
//        ロジック側で全部解消する。
//   想定: A は X 日に OFF（希望じゃない）+ 別の日 Y に勤務 → 月公休数が monthlyMin に達してる
//        → 「A を X 日に勤務に、Y 日を OFF に」入れ替えれば月公休数は不変、
//          X 日の不足が解消される。
//   ただし配置によって他のルール違反を生まないよう canAssign で確認する。
// =============================================================================

// 時間軸 swap の swap 先 (other date for OFF) を探す
const findTimeAxisSwapTarget = (state, empId, excludeDate) => {
  for (const otherDate of state.dates) {
    if (otherDate === excludeDate) continue;
    const oK = keyOf(empId, otherDate);
    if (state.frozen.has(oK)) continue;
    if (state.hopes && state.hopes[oK]) continue;
    const v = state.matrix[oK];
    if (!v || v === OFF_MARK) continue;
    const vPat = state.patternByMark[v];
    if (!vPat) continue;
    if (vPat.postShiftIds?.length) continue;
    if (isChainFollowMark(state, empId, otherDate)) continue;
    if (isFixedOnDate(vPat, otherDate)) {
      const oReq = getRequiredCount(vPat, otherDate, state.holidaySet, state.dailyOverrides, state);
      const oCur = countOnDate(state, otherDate, v);
      if (oCur - 1 < oReq) continue;
    } else {
      const oMin = vPat.minRequired || 0;
      const oCur = countOnDate(state, otherDate, v);
      if (oCur - 1 < oMin) continue;
    }
    return otherDate;
  }
  return null;
};

// 「なぜ配置できないか」を診断する
const diagnoseWhyCantAssign = (state, empId, date, pattern) => {
  const reasons = [];
  const s = state.perStaff[empId];
  if (!s) return ['unknown_staff'];
  if (s.excludeFromAutoAssign) reasons.push('対象外');
  if (state.frozen.has(keyOf(empId, date))) reasons.push('frozen');
  if (state.hopes && state.hopes[keyOf(empId, date)]) reasons.push('希望由来');
  if (!matchesJobType(s, pattern)) reasons.push(`職種不一致(staff.job=${s.jobTypeId} vs pattern.targets=${(pattern.targetJobTypes||[]).join('/')})`);
  if (!matchesSkillGroup(state, empId, pattern)) reasons.push(`スキルグループ不一致(${pattern.targetGroup})`);

  // 一時的にセルをクリアして残りの制約をチェック
  const k = keyOf(empId, date);
  const orig = state.matrix[k];
  state.matrix[k] = '';

  if (!checkMonthlyLimits(state, empId, pattern)) reasons.push('月間上限');

  const contribution = getConsecutiveContribution(pattern.displayMark, state.patterns, state.patternByMark);
  if (contribution > 0) {
    const wouldBe = getWouldBeConsecutiveLength(state, empId, date, contribution);
    const limit = state.policy.maxConsecutiveWorkDays ?? 6;
    if (wouldBe > limit) reasons.push(`連勤超過(${wouldBe.toFixed(1)}>${limit})`);
  }

  if (pattern.maxSameConsecutive > 0) {
    const sameRun = getSameMarkConsecutive(state, empId, date, pattern.displayMark);
    if (sameRun + 1 > pattern.maxSameConsecutive) {
      reasons.push(`同マーク連続(${sameRun + 1}>${pattern.maxSameConsecutive})`);
    }
  }

  // 後続禁止: 前日のパターンが forbiddenPostShiftIds を持つかチェック
  const prevDate = dayjs(date).subtract(1, 'day').format('YYYY-MM-DD');
  const prevMark = getCellAny(state, empId, prevDate);
  if (prevMark) {
    const prevPat = state.patternByMark[prevMark];
    const forbidden = prevPat?.forbiddenPostShiftIds || [];
    if (forbidden.some((ref) => {
      const t = state.patternById[ref] || state.patternByMark[ref];
      return t?.displayMark === pattern.displayMark;
    })) {
      reasons.push(`前日「${prevMark}」の後続禁止`);
    }
  }
  if (pattern.forbiddenPostShiftIds?.length) {
    const nextDate = dayjs(date).add(1, 'day').format('YYYY-MM-DD');
    const nextMark = getCellAny(state, empId, nextDate);
    if (nextMark && pattern.forbiddenPostShiftIds.some((ref) => {
      const t = state.patternById[ref] || state.patternByMark[ref];
      return t?.displayMark === nextMark;
    })) {
      reasons.push(`翌日「${nextMark}」が後続禁止`);
    }
  }

  if (violatesIncompatibility(state, empId, date, pattern)) reasons.push('相性ルール');

  state.matrix[k] = orig;
  return reasons.length > 0 ? reasons : ['配置可能 (ロジックバグ?)'];
};

const runTimeAxisSwapRepair = (state, opts = {}) => {
  const verbose = opts.verbose !== false;
  const { monthlyMin } = state.policy;
  let placed = 0;
  let canAssignFail = 0;
  let noSwapTarget = 0;
  let monthlyMaxBlocked = 0;
  const samples = [];
  const failReasons = {};

  // 🔥 真因修正: 固定パターンだけでなく、柔軟パターンも required 未達なら補充対象。
  //   UI の「3/5」表示は固定/柔軟関係なく required ベース。ロジックもそれに合わせる。
  //   これがないと「柔軟 ◯N が 3/5」のような不足が永久に放置される。
  const shortages = [];
  state.patterns.forEach((p) => {
    if (p.isChainOnlyPattern || !p.displayMark) return;
    state.dates.forEach((date) => {
      const required = getRequiredCount(p, date, state.holidaySet, state.dailyOverrides, state);
      if (required <= 0) return;
      // 🔥 required 充足判定は countForRequirement
      const current = countForRequirement(state, date, p.displayMark, p);
      if (current < required) {
        shortages.push({
          date, pattern: p, mark: p.displayMark,
          required, current, deficit: required - current,
          isFixed: isFixedOnDate(p, date) || (state.dailyOverrides?.[date]?.[p.id] != null),
        });
      }
    });
  });
  shortages.sort((a, b) => b.deficit - a.deficit);

  if (shortages.length === 0) {
    if (verbose) console.log(`⏱️ 時間軸 swap: 不足なし (固定/柔軟ともに required 達成)`);
    return;
  }
  if (verbose) console.log(`⏱️ 時間軸 swap 開始: 不足 ${shortages.length} 件 (固定/柔軟込み)`);

  shortages.forEach((sh) => {
    let need = sh.deficit;
    if (need <= 0) return;

    // 候補: 当日 OFF（希望でない、frozen でない）で職種/スキル OK のスタッフ
    //   + 🔥 当日「別パターンに居る人」（そのパターンが required を下回らない範囲で抜ける）も候補に
    //     「その日 OFF のスタッフなし」で諦めていた問題を解消するため。
    const offCandidates = [];
    const otherPatternCandidates = []; // 🔥 別パターンから取れる候補
    const offCandidatesRejected = [];
    Object.values(state.perStaff).forEach((s) => {
      if (s.excludeFromAutoAssign) return;
      const empId = s.employeeId;
      const k = keyOf(empId, sh.date);
      if (state.frozen.has(k)) return;
      if (state.hopes && state.hopes[k]) return;
      // 職種・スキル・除外グループの早期フィルタ（OFF/別パターン共通）
      if (!matchesJobType(s, sh.pattern)) {
        offCandidatesRejected.push({ empId, reason: 'jobType' });
        return;
      }
      if (!matchesSkillGroup(state, empId, sh.pattern)) {
        offCandidatesRejected.push({ empId, reason: 'skillGroup' });
        return;
      }
      if (isExcludedForPatternCount(state, empId, sh.pattern)) {
        offCandidatesRejected.push({ empId, reason: 'excludedCountGroup' });
        return;
      }
      const v = state.matrix[k];
      if (v === OFF_MARK) {
        offCandidates.push(s);
        return;
      }
      if (!v) return; // 空セルは触らない
      if (v === sh.pattern.displayMark) return; // 既に同じパターン
      const fromPat = state.patternByMark[v];
      if (!fromPat) return;
      if (fromPat.postShiftIds?.length) return;           // chain start 触らない
      if (isChainFollowMark(state, empId, sh.date)) return; // follow セル触らない
      // 元パターンを下回らないか: required を下回らない範囲で抜ける
      //   柔軟は minRequired、固定は required + countForRequirement で判定
      const isFromFixed = isFixedOnDate(fromPat, sh.date);
      if (isFromFixed) {
        const origReq = getRequiredCount(fromPat, sh.date, state.holidaySet, state.dailyOverrides, state);
        const origCount = countForRequirement(state, sh.date, fromPat.displayMark, fromPat);
        if (origCount - 1 < origReq) return;
      } else {
        const origCount = countOnDate(state, sh.date, fromPat.displayMark);
        const origMin = fromPat.minRequired || 0;
        if (origCount - 1 < origMin) return;
      }
      otherPatternCandidates.push({ s, fromPattern: fromPat });
    });

    if (offCandidates.length === 0 && otherPatternCandidates.length === 0 && offCandidatesRejected.length === 0) {
      if (verbose) console.log(`  ${sh.date} ${sh.mark} ${sh.current}/${sh.required}: その日 OFF / 別パターン候補なし`);
      return;
    }
    if (verbose) {
      console.log(
        `  ${sh.date} ${sh.mark} ${sh.current}/${sh.required}: ` +
        `OFF候補 ${offCandidates.length}名 / 別パターン候補 ${otherPatternCandidates.length}名 ` +
        `(職種/スキル不一致除外 ${offCandidatesRejected.length}名)`
      );
    }

    // 🔥 自由度スコア降順ソート: 「動かしやすい人」を先に使う。
    //   これがないと「選択肢が少ない人」を先に動かしてしまい、
    //   後で「選択肢の少ない人しか残ってないので動かせない」状況に陥る。
    //   ユーザー指摘の核心: 「選択肢が多い人を無駄使い」を防ぐ。
    offCandidates.sort((a, b) => {
      return calcStaffFreedomScore(state, b.employeeId, sh.date)
        - calcStaffFreedomScore(state, a.employeeId, sh.date);
    });
    otherPatternCandidates.sort((a, b) => {
      return calcStaffFreedomScore(state, b.s.employeeId, sh.date)
        - calcStaffFreedomScore(state, a.s.employeeId, sh.date);
    });

    // 🔥 別パターンからの振替を先に試す（公休数が変わらない＝月公休 swap 不要）
    for (const { s, fromPattern } of otherPatternCandidates) {
      if (need <= 0) break;
      const empId = s.employeeId;
      const k = keyOf(empId, sh.date);
      const origValue = state.matrix[k];
      state.matrix[k] = '';
      const ok = canAssign(state, empId, sh.date, sh.pattern);
      if (!ok) {
        state.matrix[k] = origValue;
        canAssignFail += 1;
        const reasons = diagnoseWhyCantAssign(state, empId, sh.date, sh.pattern);
        reasons.forEach((r) => { failReasons[r] = (failReasons[r] || 0) + 1; });
        continue;
      }
      state.matrix[k] = sh.pattern.displayMark;
      placed += 1;
      need -= 1;
      if (samples.length < 10) samples.push(`${empId} ${sh.date}: ${fromPattern.displayMark}→${sh.pattern.displayMark}`);
    }

    for (const s of offCandidates) {
      if (need <= 0) break;
      const empId = s.employeeId;
      const k = keyOf(empId, sh.date);

      let offCount = 0;
      state.dates.forEach((d) => {
        if (state.matrix[keyOf(empId, d)] === OFF_MARK) offCount += 1;
      });

      state.matrix[k] = '';
      const ok = canAssign(state, empId, sh.date, sh.pattern);
      if (!ok) {
        state.matrix[k] = OFF_MARK;
        canAssignFail += 1;
        // なぜダメか診断
        const reasons = diagnoseWhyCantAssign(state, empId, sh.date, sh.pattern);
        reasons.forEach((r) => { failReasons[r] = (failReasons[r] || 0) + 1; });
        if (verbose && samples.length < 20) {
          console.log(`    ❌ ${empId}: ${reasons.join(', ')}`);
        }
        continue;
      }

      // (A) 月公休に余裕あり → そのまま配置
      if (offCount - 1 >= monthlyMin) {
        state.matrix[k] = sh.pattern.displayMark;
        placed += 1;
        need -= 1;
        if (samples.length < 10) samples.push(`${empId} ${sh.date}: 公→${sh.pattern.displayMark}`);
        continue;
      }

      // 🔥 (A') monthlyMin ソフト化: 連勤違反なし & 固定不足あり時は monthlyMin を下回ってもよい
      //   ユーザー方針: 「絶対守るのは連勤違反のみ。固定不足時は公休よりも勤務者確保を優先」
      //   opts.allowOffBelowMin で許可。canAssign は既に通っているので連勤違反は起きない。
      if (opts.allowOffBelowMin) {
        state.matrix[k] = sh.pattern.displayMark;
        placed += 1;
        need -= 1;
        if (samples.length < 10) samples.push(`${empId} ${sh.date}: 公→${sh.pattern.displayMark} (公休${offCount-1}<${monthlyMin}, ソフト緩和)`);
        continue;
      }

      // (B) 月公休ぎりぎり → 別日の勤務を OFF にして時間軸 swap
      state.matrix[k] = sh.pattern.displayMark;
      const swapTarget = findTimeAxisSwapTarget(state, empId, sh.date);
      if (swapTarget) {
        state.matrix[keyOf(empId, swapTarget)] = OFF_MARK;
        placed += 1;
        need -= 1;
        if (samples.length < 10) samples.push(`${empId} ${sh.date}↔${swapTarget}`);
      } else {
        state.matrix[k] = OFF_MARK;
        noSwapTarget += 1;
        if (verbose) console.log(`    ⚠️ ${empId}: 月公休ぎりぎり(${offCount}) かつ swap 先なし`);
      }
    }
  });

  console.log(
    `⏱️ 時間軸 swap 完了: ${placed} 件配置 / canAssign失敗 ${canAssignFail} / swap先なし ${noSwapTarget}` +
    (monthlyMaxBlocked > 0 ? ` / 月公休上限 ${monthlyMaxBlocked}` : '')
  );
  if (samples.length > 0) console.log('  ✅ 配置例:', samples);
  if (Object.keys(failReasons).length > 0) {
    console.log('  ❌ 失敗理由集計:', failReasons);
  }
};

// 🔥 同マーク連続違反を「同日内 swap」で解消する。
//   例: 山田くんが D1, D2, D3 すべて ◯N (maxSameConsecutive=3 → D3 が違反)
//        → D3 の ◯N を「同日に別パターン or OFF に居て、 ◯N に配置可能な人」と交換
//   ユーザー指摘の核心: 「同マーク連続違反は他の人とチェンジで解決できる場面が多い」
const runSameMarkConsecSwap = (state) => {
  let resolved = 0;
  let attempted = 0;

  state.patterns.forEach((pattern) => {
    if (!pattern.displayMark) return;
    if (!pattern.maxSameConsecutive || pattern.maxSameConsecutive <= 0) return;
    const limit = pattern.maxSameConsecutive;

    Object.values(state.perStaff).forEach((staff) => {
      if (staff.excludeFromAutoAssign) return;
      const empId = staff.employeeId;

      // 連続を追跡しながら、 limit を超えた位置 (= 違反箇所) を検出
      let run = 0;
      for (let i = 0; i < state.dates.length; i++) {
        const date = state.dates[i];
        const k = keyOf(empId, date);
        const v = state.matrix[k];
        if (v === pattern.displayMark) {
          run += 1;
          if (run <= limit) continue;
          // ここから違反箇所。 動かせない条件をスキップ
          if (state.frozen.has(k)) continue;
          if (state.hopes?.[k]) continue;
          if (pattern.postShiftIds?.length) continue;             // chain start 触らない
          if (isChainFollowMark(state, empId, date)) continue;    // follow セル触らない
          attempted += 1;

          // 代替候補: 同日 (date) に「OFF or 別パターン」に居て、 pattern に配置可能な人
          const replacements = [];
          Object.values(state.perStaff).forEach((rs) => {
            if (rs.excludeFromAutoAssign) return;
            const rEmpId = rs.employeeId;
            if (rEmpId === empId) return;
            const rk = keyOf(rEmpId, date);
            if (state.frozen.has(rk)) return;
            if (state.hopes?.[rk]) return;
            if (!matchesJobType(rs, pattern)) return;
            if (!matchesSkillGroup(state, rEmpId, pattern)) return;
            if (isExcludedForPatternCount(state, rEmpId, pattern)) return;
            const rv = state.matrix[rk];
            if (rv === pattern.displayMark) return; // 既に同パターン
            // OFF または 別パターン(柔軟/固定で過剰でない場合は不可)
            if (rv && rv !== OFF_MARK) {
              const rPat = state.patternByMark[rv];
              if (!rPat) return;
              if (rPat.postShiftIds?.length) return;
              if (isChainFollowMark(state, rEmpId, date)) return;
              if (isFixedOnDate(rPat, date)) {
                const origReq = getRequiredCount(rPat, date, state.holidaySet, state.dailyOverrides, state);
                const origCount = countForRequirement(state, date, rPat.displayMark, rPat);
                if (origCount - 1 < origReq) return;
              } else {
                const origCount = countOnDate(state, date, rPat.displayMark);
                const origMin = rPat.minRequired || 0;
                if (origCount - 1 < origMin) return;
              }
            }
            replacements.push(rs);
          });

          // 自由度スコア降順で並び替え (動かしやすい人を優先)
          replacements.sort((a, b) =>
            calcStaffFreedomScore(state, b.employeeId, date)
            - calcStaffFreedomScore(state, a.employeeId, date));

          // 試行: 違反者 → OFF (一時)、 代替者 → pattern。 両方 canAssign 通れば確定
          let swapped = false;
          for (const rs of replacements) {
            const rEmpId = rs.employeeId;
            const rk = keyOf(rEmpId, date);
            const origViolator = state.matrix[k];
            const origRep = state.matrix[rk];
            // 違反者の代替先: 元の代替者が居た場所のマーク（OFFや別パターン）
            state.matrix[k] = origRep;
            state.matrix[rk] = pattern.displayMark;
            const okViolator = canAssign(state, empId, date, state.patternByMark[origRep] || null)
              || origRep === OFF_MARK || !origRep;
            const okRep = canAssign(state, rEmpId, date, pattern);
            if (okViolator && okRep) {
              swapped = true;
              resolved += 1;
              run = 0; // 連続切れた
              break;
            }
            // ロールバック
            state.matrix[k] = origViolator;
            state.matrix[rk] = origRep;
          }
          if (!swapped) {
            // 解消できなかった: run はそのまま (次の日も違反継続)
          }
        } else {
          run = 0;
        }
      }
    });
  });

  if (attempted > 0) {
    console.log(`🔀 同マーク連続違反 同日swap: ${resolved}/${attempted} 件解消`);
  }
  return { resolved, attempted };
};

const runSequentialRepair = (state) => {
  let fixedShortageResolved = 0;
  let fixedExcessResolved = 0;
  let placedFromOff = 0;
  let placedFromFlex = 0;

  state.dates.forEach((date) => {
    // この日の各パターンの過不足を計算
    const patternStatus = state.patterns
      .filter((p) => !p.isChainOnlyPattern && p.displayMark)
      .map((p) => {
        const required = getRequiredCount(p, date, state.holidaySet, state.dailyOverrides, state);
        // 🔥 required 充足判定は countForRequirement
        const current = countForRequirement(state, date, p.displayMark, p);
        const isFixed = isFixedOnDate(p, date)
          || (state.dailyOverrides?.[date]?.[p.id] != null);
        return { pattern: p, required, current, isFixed, diff: current - required };
      });

    // 不足のパターン（🔥 固定だけでなく柔軟も required 未達なら対象に）
    const shortages = patternStatus.filter((x) => x.diff < 0 && x.required > 0);
    // 固定過剰のパターン（柔軟は超過 OK なので対象外）
    const excesses = patternStatus.filter((x) => x.isFixed && x.diff > 0);

    // 候補プール作成
    //   - flexSource: 柔軟パターン or 固定で余裕がある人
    //   - offSource: 休みだが希望休みでない人
    const flexSource = [];
    const offSource = [];
    Object.keys(state.perStaff).forEach((empId) => {
      const k = keyOf(empId, date);
      if (state.frozen.has(k)) return;
      if (state.hopes && state.hopes[k]) return;
      const s = state.perStaff[empId];
      if (s.excludeFromAutoAssign) return;
      const v = state.matrix[k];

      if (!v || v === OFF_MARK) {
        offSource.push(empId);
        return;
      }
      const pat = state.patternByMark[v];
      if (!pat) return;
      if (pat.postShiftIds?.length) return;        // chain start 触らない
      if (isChainFollowMark(state, empId, date)) return; // follow セル触らない
      if (isFixedOnDate(pat, date)) return;        // 固定パターン触らない
      // 柔軟 or 設定なし → minRequired を下回らない範囲で抜ける
      flexSource.push({ empId, currentPattern: pat });
    });

    // 1. 固定不足を埋める
    shortages.forEach((sh) => {
      const need = -sh.diff;
      let added = 0;

      // 1-a. 柔軟パターンから振り替え（過剰削減を兼ねる）
      for (let i = flexSource.length - 1; i >= 0 && added < need; i--) {
        const { empId, currentPattern } = flexSource[i];
        // 🔥 配置してもカウント対象外のスタッフはスキップ（永久ループ防止）
        if (isExcludedForPatternCount(state, empId, sh.pattern)) continue;
        // 元パターンの minRequired を下回らないか
        const origCount = countOnDate(state, date, currentPattern.displayMark);
        const origMin = currentPattern.minRequired || 0;
        if (origCount - 1 < origMin) continue;
        // canAssign チェック（一時的にクリアして判定）
        const k = keyOf(empId, date);
        const origValue = state.matrix[k];
        state.matrix[k] = '';
        const ok = canAssign(state, empId, date, sh.pattern);
        if (!ok) { state.matrix[k] = origValue; continue; }
        // 振り替え確定
        state.matrix[k] = sh.pattern.displayMark;
        flexSource.splice(i, 1);
        added += 1;
        placedFromFlex += 1;
      }

      // 1-b. OFF（希望休みでない）から配置
      for (let i = offSource.length - 1; i >= 0 && added < need; i--) {
        const empId = offSource[i];
        // 🔥 カウント対象外スタッフは入れても無意味なのでスキップ
        if (isExcludedForPatternCount(state, empId, sh.pattern)) continue;
        const k = keyOf(empId, date);
        const origValue = state.matrix[k];
        state.matrix[k] = '';
        const ok = canAssign(state, empId, date, sh.pattern);
        if (!ok) { state.matrix[k] = origValue; continue; }
        state.matrix[k] = sh.pattern.displayMark;
        offSource.splice(i, 1);
        added += 1;
        placedFromOff += 1;
      }

      if (added > 0) {
        fixedShortageResolved += added;
        console.log(`  ✅ ${date} ${sh.pattern.name}(${sh.pattern.displayMark}): -${need} → -${need - added} (flex 振替+OFF採用 ${added}名)`);
      }
    });

    // 2. 固定過剰を OFF に削る or 不足の別パターンへ振替
    excesses.forEach((ex) => {
      const over = ex.diff;
      let removed = 0;
      // 過剰パターンに居るスタッフを集める
      const holders = Object.keys(state.perStaff).filter((empId) => {
        const k = keyOf(empId, date);
        if (state.matrix[k] !== ex.pattern.displayMark) return false;
        if (state.frozen.has(k)) return false;
        if (state.hopes && state.hopes[k]) return false;
        if (state.perStaff[empId].excludeFromAutoAssign) return false;
        if (ex.pattern.postShiftIds?.length) return false;
        if (isChainFollowMark(state, empId, date)) return false;
        return true;
      });

      for (let i = 0; i < holders.length && removed < over; i++) {
        const empId = holders[i];
        const k = keyOf(empId, date);
        // 連勤中なら OFF 化（連勤違反軽減効果）
        state.matrix[k] = OFF_MARK;
        removed += 1;
        fixedExcessResolved += 1;
      }
      if (removed > 0) {
        console.log(`  ✂️ ${date} ${ex.pattern.name}(${ex.pattern.displayMark}): +${over} → +${over - removed} (${removed}名 OFF 化)`);
      }
    });
  });

  console.log(
    `🔧 順次修復完了: 固定不足解消 ${fixedShortageResolved} ` +
    `(柔軟振替 ${placedFromFlex}, OFF採用 ${placedFromOff}) / ` +
    `固定過剰削減 ${fixedExcessResolved}`
  );

  // 🐛 デバッグ: runSequentialRepair 直後の「実際の matrix」と「countForRequirement」の不整合検出
  //   ログでは「-5 → -0 (5名配置)」と言っているのに直後の time axis swap で 42/47 と表示される
  //   現象を追跡するため、 実際の値をその場で出力する。
  let mismatchCount = 0;
  state.patterns.forEach((p) => {
    if (!p.displayMark || p.isChainOnlyPattern) return;
    state.dates.forEach((date) => {
      const required = getRequiredCount(p, date, state.holidaySet, state.dailyOverrides, state);
      if (required <= 0) return;
      const actualMatrix = countOnDate(state, date, p.displayMark);
      const requirementCount = countForRequirement(state, date, p.displayMark, p);
      const excludedDiff = actualMatrix - requirementCount;
      if (requirementCount < required && excludedDiff > 0) {
        mismatchCount += 1;
        if (mismatchCount <= 5) {
          console.warn(
            `  🐛 [不整合検出] ${date} ${p.displayMark}: matrix配置=${actualMatrix} / ` +
            `required=${required} / countForRequirement=${requirementCount} / ` +
            `除外人数=${excludedDiff} → ${excludedDiff}名の除外グループメンバーが配置されている`
          );
        }
      }
    });
  });
  if (mismatchCount > 0) {
    console.warn(
      `  🐛 [不整合検出 合計] ${mismatchCount} 件のパターン×日付で「matrix に配置済みだが ` +
      `excludedCountGroups で除外され required 未達」が発生 → 除外グループメンバーが配置されている可能性`
    );
  }
};

const collectFixedShortages = (state) => {
  // 🔥 名前は collectFixedShortages のままだが、実態は「全パターンで required 未達」を返す。
  //   UI の「3/5」表示が固定/柔軟関係なく required ベースで出るので、
  //   ロジックもそれに合わせる。柔軟も「目標 required」未達なら補充対象。
  const out = [];
  state.patterns.forEach((pattern) => {
    if (pattern.isChainOnlyPattern) return;
    if (!pattern.displayMark) return;
    state.dates.forEach((date) => {
      const required = getRequiredCount(pattern, date, state.holidaySet, state.dailyOverrides, state);
      if (required <= 0) return; // required=0 の日は対象外
      // 🔥 必要人数判定は countForRequirement (excludedCountGroups 除外)
      const current = countForRequirement(state, date, pattern.displayMark, pattern);
      if (current < required) {
        out.push({
          date,
          pattern,
          mark: pattern.displayMark,
          required,
          current,
          deficit: required - current,
          isFixed: isFixedOnDate(pattern, date)
            || (state.dailyOverrides?.[date]?.[pattern.id] != null),
        });
      }
    });
  });
  // 大きい不足から先に直す（小さい不足の方が連鎖が短い → 後回しでも解ける）
  out.sort((a, b) => b.deficit - a.deficit);
  return out;
};

// 1つの不足を解消するために「別パターンに居る誰か」を交換で動かす。
// 連鎖深さ depth、共通の changes / visited を持つ。
const tryRepairFixedShortage = (state, shortage, depth, maxDepth, changes, visited) => {
  if (depth >= maxDepth) return false;
  const { date, pattern, required } = shortage;
  const visitedKey = `${date}_${pattern.displayMark}_${depth}`;
  if (visited.has(visitedKey)) return false;
  visited.add(visitedKey);

  // 🔥 required 充足判定は countForRequirement
  let current = countForRequirement(state, date, pattern.displayMark, pattern);
  if (current >= required) return true;

  const apply = (k, newVal) => {
    changes.push({ key: k, before: state.matrix[k] });
    state.matrix[k] = newVal == null ? '' : newVal;
  };

  // 候補: その日に別のパターンに入っているスタッフ
  const candidates = Object.keys(state.perStaff).filter((empId) => {
    const k = keyOf(empId, date);
    const cur = state.matrix[k];
    if (!cur || cur === OFF_MARK) return false;
    if (cur === pattern.displayMark) return false;
    if (state.frozen.has(k)) return false;
    if (state.hopes && state.hopes[k]) return false;
    const curPat = state.patternByMark[cur];
    if (curPat && isFixedOnDate(curPat, date)) return false;     // 元が固定は触らない
    if (curPat?.postShiftIds?.length) return false;              // 元がチェーン start は触らない
    if (isChainFollowMark(state, empId, date)) return false;     // 元が follow セルは触らない
    if (state.perStaff[empId].excludeFromAutoAssign) return false;
    // pattern にハード制約として置けるか
    if (!matchesJobType(state.perStaff[empId], pattern)) return false;
    if (!matchesSkillGroup(state, empId, pattern)) return false;
    // 🔥 配置してもカウント対象外のスタッフは候補から除外（永久ループ防止）
    if (isExcludedForPatternCount(state, empId, pattern)) return false;
    return true;
  });

  // 優先度: 元パターンに「余裕」がある人から（=元から1人抜いてもminを切らない）
  candidates.sort((a, b) => {
    const aCur = state.matrix[keyOf(a, date)];
    const bCur = state.matrix[keyOf(b, date)];
    const aPat = state.patternByMark[aCur];
    const bPat = state.patternByMark[bCur];
    const aMin = aPat?.minRequired || 0;
    const bMin = bPat?.minRequired || 0;
    const aCount = countOnDate(state, date, aCur);
    const bCount = countOnDate(state, date, bCur);
    return (bCount - bMin) - (aCount - aMin); // slack 大きい順
  });

  const need = required - current;
  let added = 0;

  // 🔥 chain-aware: pattern がチェーン開始マーク（postShiftIds 持ち）の場合、
  //   配置先候補の翌日セルが follow と整合的でなければ skip する（チェーン破壊防止）。
  //   これがないと、配置→チェーン整合チェックで取り消し→再配置の永久ループになる。
  const expectedFollowMark = (() => {
    if (!pattern.postShiftIds?.length) return null;
    const ref = pattern.postShiftIds[0];
    const t = state.patternById[ref] || state.patternByMark[ref];
    return t?.displayMark || null;
  })();

  for (const empId of candidates) {
    if (added >= need) break;
    const k = keyOf(empId, date);
    const orig = state.matrix[k];
    if (!orig) continue;
    const origPat = state.patternByMark[orig];

    // 🔥 chain-aware: 翌日のセル状態を確認
    let nextDateInfo = null;
    if (expectedFollowMark) {
      const nextDate = dayjs(date).add(1, 'day').format('YYYY-MM-DD');
      if (state.dates.includes(nextDate)) {
        const nextK = keyOf(empId, nextDate);
        const nextOrig = state.matrix[nextK];
        // 翌日が「空 / OFF / 既に正しい follow」でなければ、この候補ではチェーン破壊になる
        const compatible = !nextOrig || nextOrig === OFF_MARK || nextOrig === expectedFollowMark;
        if (!compatible) {
          // この候補は次日に別マークが入ってる → チェーン破壊 → skip
          continue;
        }
        // 翌日が frozen / hope ならここを書き換えられない → skip
        if (state.frozen.has(nextK)) continue;
        if (state.hopes && state.hopes[nextK]) continue;
        nextDateInfo = { nextK, nextOrig };
      }
      // 月末で翌日が当月外 → 翌月キューに任せる（既存ロジック）
    }

    // 一時的にクリアして canAssign チェック
    apply(k, '');
    if (!canAssign(state, empId, date, pattern)) {
      // revert
      const last = changes.pop();
      state.matrix[last.key] = last.before;
      continue;
    }
    // ターゲットパターン配置
    apply(k, pattern.displayMark);
    // 🔥 chain start なら follow もセットで置く
    if (nextDateInfo && expectedFollowMark) {
      apply(nextDateInfo.nextK, expectedFollowMark);
    }
    added += 1;
    current += 1;

    // 元パターンに新たな不足が生まれていないか
    if (origPat && !origPat.isChainOnlyPattern) {
      // 🔥 required 充足判定は countForRequirement
      const origCurrent = countForRequirement(state, date, orig, origPat);
      const origIsFixed = isFixedOnDate(origPat, date)
        || (state.dailyOverrides?.[date]?.[origPat.id] != null);
      if (origIsFixed) {
        const origRequired = getRequiredCount(origPat, date, state.holidaySet, state.dailyOverrides, state);
        if (origCurrent < origRequired) {
          // 連鎖修復
          const origShortage = {
            date,
            pattern: origPat,
            mark: orig,
            required: origRequired,
            current: origCurrent,
            deficit: origRequired - origCurrent,
          };
          if (!tryRepairFixedShortage(state, origShortage, depth + 1, maxDepth, changes, visited)) {
            // 連鎖失敗 → この交換は revert
            const last1 = changes.pop();
            const last2 = changes.pop();
            state.matrix[last1.key] = last1.before;
            state.matrix[last2.key] = last2.before;
            added -= 1;
            current -= 1;
            continue;
          }
        }
      } else {
        // 柔軟: minRequired を下回ったら警告だが採用
        // (ユーザー方針: 柔軟は無理にピッタリじゃなくていい)
      }
    }
  }

  return current >= required;
};

// =============================================================================
// Phase 7.47: 深さ優先連鎖修復 (Deep Chain Repair)
//   ユーザー要求の核心:
//     「変更対象を同日内に限定せず、スキル上配置可能な全スタッフを候補にする。
//      そのスタッフを配置することで起きる別の違反を連鎖的に修復し、
//      最終的に全違反が解消するなら採用。最小限の修正で済む候補を優先。」
//
//   既存の tryRepairFixedShortage との違い:
//     - スキル上候補を「同日に居る人」から「全月間で配置可能な人」に拡張
//     - 月公休制約による直接配置不可も「別日 OFF への波及」で解決
//     - その波及で生まれる別日不足も連鎖的に修復
//
//   無限ループ防止:
//     - visited (フィンガープリント) で同じ違反を再訪しない
//     - maxDepth で深さ制限
//     - 失敗時は確実に revert
// =============================================================================

const findAllSkillCompatibleStaff = (state, pattern) => {
  return Object.values(state.perStaff).filter((s) => {
    if (s.excludeFromAutoAssign) return false;
    if (!matchesJobType(s, pattern)) return false;
    if (!matchesSkillGroup(state, s.employeeId, pattern)) return false;
    // 🔥 配置してもカウント対象外のスタッフは候補から除外（永久ループ防止）
    if (isExcludedForPatternCount(state, s.employeeId, pattern)) return false;
    return true;
  });
};

const tryDeepResolveShortage = (state, shortage, depth, maxDepth, changes, visited) => {
  if (depth >= maxDepth) return false;
  const fp = `${shortage.date}_${shortage.mark}`;
  if (visited.has(fp)) return false; // 同じ違反を再帰でループしない
  visited.add(fp);

  const { date, pattern, required } = shortage;
  let current = countForRequirement(state, date, pattern.displayMark, pattern);
  if (current >= required) {
    visited.delete(fp); // 解消済みなら visited から外して別経路で再訪可能に
    return true;
  }

  // スキル上配置可能な全スタッフを候補に（既にこのパターンに居る人を除く）
  const candidates = findAllSkillCompatibleStaff(state, pattern)
    .filter((s) => state.matrix[keyOf(s.employeeId, date)] !== pattern.displayMark);

  // 優先度ソート: 「波及の少ない人」=「現在 OFF」「希望じゃない」「frozen じゃない」を優先
  candidates.sort((a, b) => {
    const ak = keyOf(a.employeeId, date);
    const bk = keyOf(b.employeeId, date);
    const av = state.matrix[ak];
    const bv = state.matrix[bk];
    const aScore = (state.frozen.has(ak) ? 100 : 0)
      + (state.hopes?.[ak] ? 100 : 0)
      + (av && av !== OFF_MARK ? 10 : 0);
    const bScore = (state.frozen.has(bk) ? 100 : 0)
      + (state.hopes?.[bk] ? 100 : 0)
      + (bv && bv !== OFF_MARK ? 10 : 0);
    return aScore - bScore;
  });

  for (const s of candidates) {
    if (current >= required) break;
    const empId = s.employeeId;
    const k = keyOf(empId, date);
    if (state.frozen.has(k)) continue;
    if (state.hopes && state.hopes[k]) continue;
    const origMark = state.matrix[k];
    const origPat = origMark ? state.patternByMark[origMark] : null;
    // chain 関連は触らない
    if (origPat?.postShiftIds?.length) continue;
    if (origMark && isChainFollowMark(state, empId, date)) continue;

    // canAssign チェック（連勤・同マーク連続・後続禁止・月間上限・相性）
    state.matrix[k] = '';
    if (!canAssign(state, empId, date, pattern)) {
      state.matrix[k] = origMark;
      continue;
    }

    const localChanges = [{ key: k, before: origMark }];
    state.matrix[k] = pattern.displayMark;

    // === 波及の追跡 ===
    const cascadedFail = (() => {
      // (1) origMark を抜いたことで、その origPat に新たな不足が出るか
      if (origPat && !origPat.isChainOnlyPattern && origMark !== OFF_MARK) {
        const origReq = getRequiredCount(origPat, date, state.holidaySet, state.dailyOverrides, state);
        const origCur = countForRequirement(state, date, origMark, origPat);
        if (origCur < origReq) {
          // 同日に origPat の不足が生まれた → 連鎖修復
          const sub = {
            date,
            pattern: origPat,
            mark: origMark,
            required: origReq,
            current: origCur,
            deficit: origReq - origCur,
          };
          if (!tryDeepResolveShortage(state, sub, depth + 1, maxDepth, localChanges, visited)) {
            return true; // 連鎖修復失敗
          }
        }
      }

      // (2) 月公休 min を切るか確認（origMark が OFF だった場合に勤務 1 日増 = OFF 1 日減）
      if (origMark === OFF_MARK || !origMark) {
        let offCount = 0;
        state.dates.forEach((d) => {
          if (state.matrix[keyOf(empId, d)] === OFF_MARK) offCount += 1;
        });
        if (offCount < state.policy.monthlyMin) {
          // 別の日を OFF にして補う → 時間軸 swap
          const swapTarget = findTimeAxisSwapTarget(state, empId, date);
          if (!swapTarget) return true; // swap 先なし → 連鎖修復失敗
          const oldVal = state.matrix[keyOf(empId, swapTarget)];
          const oldPat = state.patternByMark[oldVal];
          localChanges.push({ key: keyOf(empId, swapTarget), before: oldVal });
          state.matrix[keyOf(empId, swapTarget)] = OFF_MARK;

          // swapTarget で新たな不足が出るかチェック
          if (oldPat && !oldPat.isChainOnlyPattern) {
            const swapReq = getRequiredCount(oldPat, swapTarget, state.holidaySet, state.dailyOverrides, state);
            const swapCur = countForRequirement(state, swapTarget, oldVal, oldPat);
            if (swapCur < swapReq) {
              const sub = {
                date: swapTarget,
                pattern: oldPat,
                mark: oldVal,
                required: swapReq,
                current: swapCur,
                deficit: swapReq - swapCur,
              };
              if (!tryDeepResolveShortage(state, sub, depth + 1, maxDepth, localChanges, visited)) {
                return true; // 連鎖修復失敗
              }
            }
          }
        }
      }

      return false; // 波及修復成功
    })();

    if (cascadedFail) {
      // revert
      for (let i = localChanges.length - 1; i >= 0; i--) {
        state.matrix[localChanges[i].key] = localChanges[i].before;
      }
      continue;
    }

    // 採用
    changes.push(...localChanges);
    current = countForRequirement(state, date, pattern.displayMark, pattern);
  }

  visited.delete(fp); // 別経路で再訪可能に戻す
  return current >= required;
};

const runDeepChainRepair = async (state, opts = {}) => {
  const maxDepth = opts.maxDepth || 6;
  const timeoutMs = opts.timeoutMs || 60_000;
  const startTime = (typeof performance !== 'undefined' ? performance.now() : Date.now());

  const initial = collectFixedShortages(state);
  if (initial.length === 0) {
    console.log('🌳 深さ優先連鎖修復: 不足なし');
    return;
  }
  console.log(`🌳 深さ優先連鎖修復開始: 不足 ${initial.length} 件`);

  let resolved = 0;
  let attempted = 0;
  for (const sh of initial) {
    const elapsed = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - startTime;
    if (elapsed > timeoutMs) {
      console.warn(`🌳 深さ優先連鎖修復: タイムアウト`);
      break;
    }
    // eslint-disable-next-line no-await-in-loop
    await yieldToBrowser();
    // 現状確認
    const cur = countForRequirement(state, sh.date, sh.mark, sh.pattern);
    if (cur >= sh.required) { resolved += 1; continue; }
    attempted += 1;

    const changes = [];
    const visited = new Set();
    if (tryDeepResolveShortage(state, sh, 0, maxDepth, changes, visited)) {
      resolved += 1;
      console.log(`  ✅ ${sh.date} ${sh.mark}: 連鎖修復成功 (変更 ${changes.length} 件)`);
    } else {
      // revert
      for (let i = changes.length - 1; i >= 0; i--) {
        state.matrix[changes[i].key] = changes[i].before;
      }
    }
  }
  console.log(`🌳 深さ優先連鎖修復完了: ${resolved}/${attempted} 件 解消`);
};

// =============================================================================
// Phase 7.48: 同パターン他日交換修復 (Same-Mark Other-Date Swap)
//   不足: D1 で X パターン
//   候補: 他日 D2 で X パターンに居るスタッフ A（A は D1 で OFF or 触れる柔軟）
//   操作: A の D1 = X に置く + A の D2 = OFF に切り替え（時間軸の振り替え）
//   検証: D2 の X が不足にならないか確認、なれば別候補
//   = 「同じパターンを担う人を時間軸でシャッフルして配置を均す」
// =============================================================================
const runSameMarkOtherDateSwap = async (state) => {
  const shortages = collectFixedShortages(state);
  if (shortages.length === 0) return;
  let placed = 0;
  const samples = [];

  for (const sh of shortages) {
    // eslint-disable-next-line no-await-in-loop
    await yieldToBrowser();
    let need = sh.deficit;
    if (need <= 0) continue;

    // 他日にこのパターンで配置されているスタッフを抽出
    const candidates = Object.values(state.perStaff).filter((s) => {
      if (s.excludeFromAutoAssign) return false;
      if (!matchesJobType(s, sh.pattern)) return false;
      if (!matchesSkillGroup(state, s.employeeId, sh.pattern)) return false;
      // 🔥 カウント対象外スタッフは配置しても無意味（永久ループ防止）
      if (isExcludedForPatternCount(state, s.employeeId, sh.pattern)) return false;
      const k = keyOf(s.employeeId, sh.date);
      if (state.matrix[k] === sh.pattern.displayMark) return false; // 既に sh.date に居る
      // sh.date は OFF or 空 or 触れる柔軟である必要
      const v = state.matrix[k];
      if (state.frozen.has(k)) return false;
      if (state.hopes && state.hopes[k]) return false;
      if (v && v !== OFF_MARK) {
        const vp = state.patternByMark[v];
        if (!vp) return false;
        if (vp.postShiftIds?.length) return false;
        if (isChainFollowMark(state, s.employeeId, sh.date)) return false;
        if (isFixedOnDate(vp, sh.date)) return false; // 固定なら抜けない
      }
      // 他日に sh.pattern.displayMark がある？
      return state.dates.some((d) => {
        if (d === sh.date) return false;
        if (state.matrix[keyOf(s.employeeId, d)] !== sh.pattern.displayMark) return false;
        if (state.frozen.has(keyOf(s.employeeId, d))) return false;
        if (state.hopes && state.hopes[keyOf(s.employeeId, d)]) return false;
        if (isChainFollowMark(state, s.employeeId, d)) return false;
        return true;
      });
    });

    for (const s of candidates) {
      if (need <= 0) break;
      const empId = s.employeeId;
      const k = keyOf(empId, sh.date);
      const origMark = state.matrix[k];

      // canAssign チェック
      state.matrix[k] = '';
      if (!canAssign(state, empId, sh.date, sh.pattern)) {
        state.matrix[k] = origMark;
        continue;
      }
      state.matrix[k] = sh.pattern.displayMark;

      // donor 日（同パターンで居る他日）を選ぶ
      let donorDate = null;
      for (const d of state.dates) {
        if (d === sh.date) continue;
        if (state.matrix[keyOf(empId, d)] !== sh.pattern.displayMark) continue;
        if (state.frozen.has(keyOf(empId, d))) continue;
        if (state.hopes && state.hopes[keyOf(empId, d)]) continue;
        if (isChainFollowMark(state, empId, d)) continue;
        // donor 日でこのパターンを抜くと不足にならないか
        const donorReq = getRequiredCount(sh.pattern, d, state.holidaySet, state.dailyOverrides, state);
        const donorCur = countForRequirement(state, d, sh.pattern.displayMark, sh.pattern);
        if (donorCur - 1 < donorReq) continue; // 抜くと不足 → 別の日
        donorDate = d;
        break;
      }

      if (!donorDate) {
        state.matrix[k] = origMark;
        continue;
      }
      // donor 日を OFF に
      state.matrix[keyOf(empId, donorDate)] = OFF_MARK;
      placed += 1;
      need -= 1;
      if (samples.length < 10) {
        samples.push(`${empId} ${sh.date}↔${donorDate} (${sh.pattern.displayMark} 同パターン振替)`);
      }
    }
  }

  if (placed > 0) {
    console.log(`🔁 同パターン他日交換: ${placed} 件配置`, samples);
  }
};

// =============================================================================
// Phase 7.49: 席空け連鎖修復 (Open Seat Cascade)
//   不足: D1 で X
//   スキル A は D1 に 別パターン Y で居る → A を Y → X に振り替え
//   Y の不足を「Y のスキルだけで OK な人 B」で補充（B は別パターン Z に居るかも）
//   = 「スキル候補のために席を空ける連鎖」
// =============================================================================
const runOpenSeatCascade = async (state) => {
  const shortages = collectFixedShortages(state);
  if (shortages.length === 0) return;
  let placed = 0;
  const samples = [];

  for (const sh of shortages) {
    // eslint-disable-next-line no-await-in-loop
    await yieldToBrowser();
    let need = sh.deficit;
    if (need <= 0) continue;

    // X スキル候補 (sh.pattern にマッチ) で、同日 sh.date に別パターンに居る人
    const skillCandidates = findAllSkillCompatibleStaff(state, sh.pattern).filter((s) => {
      const k = keyOf(s.employeeId, sh.date);
      if (state.matrix[k] === sh.pattern.displayMark) return false;
      if (state.frozen.has(k)) return false;
      if (state.hopes && state.hopes[k]) return false;
      const v = state.matrix[k];
      if (!v || v === OFF_MARK) return false; // OFF は別ロジック対象
      const vp = state.patternByMark[v];
      if (!vp || vp.postShiftIds?.length) return false;
      if (isChainFollowMark(state, s.employeeId, sh.date)) return false;
      return true;
    });

    for (const sk of skillCandidates) {
      if (need <= 0) break;
      const empId = sk.employeeId;
      const k = keyOf(empId, sh.date);
      const origMark = state.matrix[k];
      const origPat = state.patternByMark[origMark];

      // canAssign チェック
      state.matrix[k] = '';
      if (!canAssign(state, empId, sh.date, sh.pattern)) {
        state.matrix[k] = origMark;
        continue;
      }
      state.matrix[k] = sh.pattern.displayMark;

      // origPat に不足が出るか確認
      const origReq = getRequiredCount(origPat, sh.date, state.holidaySet, state.dailyOverrides, state);
      const origCur = countForRequirement(state, sh.date, origMark, origPat);

      if (origCur >= origReq) {
        // 不足出ない → そのまま採用
        placed += 1;
        need -= 1;
        if (samples.length < 10) samples.push(`${empId} ${sh.date}: ${origMark}→${sh.pattern.displayMark}`);
        continue;
      }

      // 不足が出る → 席空け補充候補 (origPat にマッチする人) を探す
      let filled = false;
      const seatFillers = Object.values(state.perStaff).filter((s) => {
        if (s.excludeFromAutoAssign) return false;
        if (s.employeeId === empId) return false;
        const k2 = keyOf(s.employeeId, sh.date);
        if (state.matrix[k2] === origMark) return false;
        if (state.frozen.has(k2)) return false;
        if (state.hopes && state.hopes[k2]) return false;
        if (!matchesJobType(s, origPat)) return false;
        if (!matchesSkillGroup(state, s.employeeId, origPat)) return false;
        return true;
      });

      for (const filler of seatFillers) {
        const fk = keyOf(filler.employeeId, sh.date);
        const fOrig = state.matrix[fk];
        const fOrigPat = fOrig ? state.patternByMark[fOrig] : null;
        // 触れない場所はスキップ
        if (fOrigPat?.postShiftIds?.length) continue;
        if (fOrig && isChainFollowMark(state, filler.employeeId, sh.date)) continue;

        state.matrix[fk] = '';
        if (!canAssign(state, filler.employeeId, sh.date, origPat)) {
          state.matrix[fk] = fOrig;
          continue;
        }
        state.matrix[fk] = origMark;

        // filler の元場所 (fOrig) で不足が出るか?
        if (fOrig && fOrig !== OFF_MARK && fOrigPat) {
          const fOrigReq = getRequiredCount(fOrigPat, sh.date, state.holidaySet, state.dailyOverrides, state);
          const fOrigCur = countForRequirement(state, sh.date, fOrig, fOrigPat);
          if (fOrigCur < fOrigReq) {
            // さらに連鎖必要 → ここでは諦め（深さ 1 までで止める、別 cycle で対応）
            state.matrix[fk] = fOrig;
            continue;
          }
        }

        filled = true;
        if (samples.length < 10) {
          samples.push(`${empId}:${origMark}→${sh.pattern.displayMark} + ${filler.employeeId}:${fOrig || '空'}→${origMark}`);
        }
        break;
      }

      if (!filled) {
        // revert
        state.matrix[k] = origMark;
        continue;
      }
      placed += 1;
      need -= 1;
    }
  }

  if (placed > 0) {
    console.log(`🪑 席空け連鎖修復: ${placed} 件配置`, samples);
  }
};

// =============================================================================
// Phase 7.6: ILP (整数計画法) 修復
//   ルールベース修復で解けなかった残違反を javascript-lp-solver で最適化。
//
//   モデル化:
//     - 変数: x[empId][date][mark] ∈ {0,1}
//     - 制約:
//       1) 各セル: Σ_m x[s,d,m] = 1  (1セル1パターン)
//       2) 固定パターン required: Σ_s x[s,d,m] = required
//       3) 柔軟パターン required: Σ_s x[s,d,m] ≥ minRequired
//       4) 既存固定セル (frozen/hope/event): x[s,d,frozenMark] = 1
//       5) 月公休: monthlyMin ≤ Σ_d x[s,d,OFF] ≤ monthlyMax
//       6) 職種・スキル不一致: x[s,d,m] = 0
//     - 目的関数: 既存解からの変更コスト最小化（最小破壊）
//
//   局所 ILP: 「不足のあるパターンにスキルマッチするスタッフ × 当月」だけ変数化。
// =============================================================================
const runILPRepair = (state, opts = {}) => {
  const verbose = opts.verbose !== false;
  // 🔥 制約フラグ: 段階的緩和に使う
  const useConsec = opts.useConsec !== false;
  const useSameConsec = opts.useSameConsec !== false;
  const useChain = opts.useChain !== false;
  const useForbidden = opts.useForbidden !== false;
  const useMonthlyOff = opts.useMonthlyOff !== false;

  const shortages = collectFixedShortages(state);
  if (shortages.length === 0) {
    if (verbose) console.log(`🧮 ILP 修復: 残違反なし → skip`);
    return { feasible: true, applied: false };
  }

  // 影響範囲: 違反パターンにスキルマッチする全スタッフ
  const affectedStaff = new Set();
  const affectedPatterns = new Set();
  shortages.forEach((sh) => {
    affectedPatterns.add(sh.pattern.id);
    Object.values(state.perStaff).forEach((s) => {
      if (s.excludeFromAutoAssign) return;
      if (!matchesJobType(s, sh.pattern)) return;
      if (!matchesSkillGroup(state, s.employeeId, sh.pattern)) return;
      affectedStaff.add(s.employeeId);
    });
  });

  if (affectedStaff.size === 0) {
    if (verbose) console.warn(`🧮 ILP: スキルマッチするスタッフなし`);
    return;
  }

  const staffArr = [...affectedStaff];
  const datesArr = [...state.dates];
  const { monthlyMin, monthlyMax } = state.policy;

  // 取り得るマーク: OFF + スタッフ集合の誰かが配置可能な全パターン
  const allMarks = new Set([OFF_MARK]);
  state.patterns.forEach((p) => {
    if (p.isChainOnlyPattern || !p.displayMark) return;
    // affectedStaff の誰かにマッチするパターンのみ
    const someMatch = staffArr.some((empId) => {
      const s = state.perStaff[empId];
      return matchesJobType(s, p) && matchesSkillGroup(state, empId, p);
    });
    if (someMatch) allMarks.add(p.displayMark);
  });

  const isFrozenLike = (empId, date) => {
    const k = keyOf(empId, date);
    if (state.frozen.has(k)) return true;
    if (state.hopes && state.hopes[k]) return true;
    return false;
  };

  // モデル構築
  const model = {
    optimize: "objective",
    opType: "min",
    constraints: {},
    variables: {},
    ints: {},
  };

  // 変数: x_${empId}_${date}_${mark}
  staffArr.forEach((empId) => {
    const s = state.perStaff[empId];
    datesArr.forEach((date) => {
      const cellKey = `cell_${empId}_${date}`;
      model.constraints[cellKey] = { equal: 1 };

      if (isFrozenLike(empId, date)) {
        // 既存値を固定
        const curMark = state.matrix[keyOf(empId, date)] || OFF_MARK;
        const varName = `x_${empId}_${date}_${curMark}`;
        model.variables[varName] = { [cellKey]: 1, objective: 0 };
        model.ints[varName] = 1;
        // この cell では他のマークを取れないように単一変数のみ定義
        return;
      }

      // 自由セル: マッチするマーク変数を定義
      Array.from(allMarks).forEach((mark) => {
        if (mark !== OFF_MARK) {
          const pat = state.patternByMark[mark];
          if (!pat) return;
          if (!matchesJobType(s, pat)) return;
          if (!matchesSkillGroup(state, empId, pat)) return;
          // チェーンマークの単独配置を防ぐため、postShiftIds を持たない or 整合性が成立する場合のみ
          // (初期版では postShiftIds を持つマーク = 1 のみ。後続セルは別途処理)
        }
        const varName = `x_${empId}_${date}_${mark}`;
        const curMark = state.matrix[keyOf(empId, date)] || '';
        const cost = (mark === curMark) ? 0 : 1; // 最小変更
        model.variables[varName] = { [cellKey]: 1, objective: cost };
        model.ints[varName] = 1;
      });
    });
  });

  // 各日 × パターン: 不足側の制約のみ追加（その日にそのパターンが必要）
  shortages.forEach((sh) => {
    const constraintKey = `req_${sh.date}_${sh.pattern.displayMark}`;
    if (model.constraints[constraintKey]) return; // 同じパターン×日付の重複排除

    const isFixed = isFixedOnDate(sh.pattern, sh.date)
      || (state.dailyOverrides?.[sh.date]?.[sh.pattern.id] != null);

    // affectedStaff 外の固定配置を定数として計上
    let externalCount = 0;
    Object.values(state.perStaff).forEach((s2) => {
      if (affectedStaff.has(s2.employeeId)) return;
      if (state.matrix[keyOf(s2.employeeId, sh.date)] === sh.pattern.displayMark) externalCount += 1;
    });

    const adjustedRequired = sh.required - externalCount;

    // affected staff のこの (date, mark) 変数に係数 1 を加算
    staffArr.forEach((empId) => {
      const varName = `x_${empId}_${sh.date}_${sh.pattern.displayMark}`;
      if (model.variables[varName]) {
        model.variables[varName][constraintKey] = 1;
      }
    });

    if (isFixed) {
      model.constraints[constraintKey] = { equal: Math.max(0, adjustedRequired) };
    } else {
      model.constraints[constraintKey] = { min: Math.max(0, adjustedRequired) };
    }
  });

  // 月公休制約: monthlyMin ≤ Σ x[s,d,OFF] ≤ monthlyMax
  if (useMonthlyOff) {
    staffArr.forEach((empId) => {
      const ck = `off_${empId}`;
      let externalOff = 0;
      datesArr.forEach((date) => {
        if (isFrozenLike(empId, date)) {
          if (state.matrix[keyOf(empId, date)] === OFF_MARK) externalOff += 1;
        }
      });

      datesArr.forEach((date) => {
        if (isFrozenLike(empId, date)) return;
        const varName = `x_${empId}_${date}_${OFF_MARK}`;
        if (model.variables[varName]) {
          model.variables[varName][ck] = 1;
        }
      });
      model.constraints[ck] = {
        min: Math.max(0, monthlyMin - externalOff),
        max: Math.max(0, monthlyMax - externalOff),
      };
    });
  }

  // 🔥 連勤上限制約（スライディングウィンドウ）
  //   任意の N+1 日連続窓で Σ work[s,d+i] ≤ N
  //   work_marks = 寄与度 > 0 のマーク（休み系を除く）
  const maxConsec = Math.floor(state.policy.maxConsecutiveWorkDays || 6);
  const workMarks = new Set();
  state.patterns.forEach((p) => {
    if (!p.displayMark) return;
    const c = getConsecutiveContribution(p.displayMark, state.patterns, state.patternByMark);
    if (c > 0) workMarks.add(p.displayMark);
  });

  if (useConsec && maxConsec > 0 && workMarks.size > 0) {
    const windowSize = maxConsec + 1;
    staffArr.forEach((empId) => {
      for (let startIdx = 0; startIdx <= datesArr.length - windowSize; startIdx++) {
        const window = datesArr.slice(startIdx, startIdx + windowSize);
        const ck = `consec_${empId}_${startIdx}`;
        let externalWork = 0;
        let hasVar = false;
        window.forEach((d) => {
          if (isFrozenLike(empId, d)) {
            const cur = state.matrix[keyOf(empId, d)];
            if (cur && workMarks.has(cur)) externalWork += 1;
            return;
          }
          workMarks.forEach((m) => {
            const varName = `x_${empId}_${d}_${m}`;
            if (model.variables[varName]) {
              model.variables[varName][ck] = (model.variables[varName][ck] || 0) + 1;
              hasVar = true;
            }
          });
        });
        if (hasVar) {
          // 🛡️ infeasible 回避: 既存固定値が既に上限を超えていたら、
          //   既存値を上限としてその制約を緩和する（悪化はさせない）。
          const rawMax = maxConsec - externalWork;
          model.constraints[ck] = { max: Math.max(0, rawMax) };
        }
      }
    });
  }

  // 🔥 同マーク連続上限制約
  if (useSameConsec) {
    state.patterns.forEach((p) => {
      if (!p.displayMark || !p.maxSameConsecutive || p.maxSameConsecutive <= 0) return;
      const limit = p.maxSameConsecutive;
      const windowSize = limit + 1;
      staffArr.forEach((empId) => {
        for (let startIdx = 0; startIdx <= datesArr.length - windowSize; startIdx++) {
          const window = datesArr.slice(startIdx, startIdx + windowSize);
          const ck = `sameconsec_${empId}_${p.displayMark}_${startIdx}`;
          let externalSame = 0;
          let hasVar = false;
          window.forEach((d) => {
            if (isFrozenLike(empId, d)) {
              if (state.matrix[keyOf(empId, d)] === p.displayMark) externalSame += 1;
              return;
            }
            const varName = `x_${empId}_${d}_${p.displayMark}`;
            if (model.variables[varName]) {
              model.variables[varName][ck] = 1;
              hasVar = true;
            }
          });
          if (hasVar) {
            model.constraints[ck] = { max: Math.max(0, limit - externalSame) };
          }
        }
      });
    });
  }

  // 🔥 後続チェーン制約: x[s,d,X] - x[s,d+1,follow] ≤ 0
  if (useChain) {
    state.patterns.forEach((p) => {
      if (!p.displayMark) return;
      if (!p.postShiftIds || !p.postShiftIds.length) return;
      const ref = p.postShiftIds[0];
      const target = state.patternById[ref] || state.patternByMark[ref];
      const followMark = target?.displayMark;
      if (!followMark) return;

      staffArr.forEach((empId) => {
        datesArr.forEach((d, idx) => {
          if (idx === datesArr.length - 1) return;
          const nextD = datesArr[idx + 1];
          const varX = `x_${empId}_${d}_${p.displayMark}`;
          const varFollow = `x_${empId}_${nextD}_${followMark}`;
          const ck = `chain_${empId}_${d}_${p.displayMark}`;
          const xExists = !!model.variables[varX];
          const fExists = !!model.variables[varFollow];
          if (xExists && fExists) {
            model.variables[varX][ck] = (model.variables[varX][ck] || 0) + 1;
            model.variables[varFollow][ck] = (model.variables[varFollow][ck] || 0) - 1;
            model.constraints[ck] = { max: 0 };
          } else if (xExists && !fExists) {
            const nextCur = state.matrix[keyOf(empId, nextD)];
            if (nextCur !== followMark) {
              model.variables[varX][ck] = 1;
              model.constraints[ck] = { max: 0 };
            }
          }
        });
      });
    });
  }

  // 🔥 後続禁止制約
  if (useForbidden) {
    state.patterns.forEach((p) => {
      if (!p.displayMark) return;
      if (!p.forbiddenPostShiftIds || !p.forbiddenPostShiftIds.length) return;
      p.forbiddenPostShiftIds.forEach((ref) => {
        const target = state.patternById[ref] || state.patternByMark[ref];
        const fMark = target?.displayMark;
        if (!fMark) return;
        staffArr.forEach((empId) => {
          datesArr.forEach((d, idx) => {
            if (idx === datesArr.length - 1) return;
            const nextD = datesArr[idx + 1];
            const varX = `x_${empId}_${d}_${p.displayMark}`;
            const varF = `x_${empId}_${nextD}_${fMark}`;
            const ck = `forbid_${empId}_${d}_${p.displayMark}_${fMark}`;
            let externalSum = 0;
            if (isFrozenLike(empId, d) && state.matrix[keyOf(empId, d)] === p.displayMark) externalSum += 1;
            if (isFrozenLike(empId, nextD) && state.matrix[keyOf(empId, nextD)] === fMark) externalSum += 1;
            let added = false;
            if (model.variables[varX]) { model.variables[varX][ck] = 1; added = true; }
            if (model.variables[varF]) { model.variables[varF][ck] = 1; added = true; }
            if (added) {
              model.constraints[ck] = { max: Math.max(0, 1 - externalSum) };
            }
          });
        });
      });
    });
  }

  console.log(
    `🧮 ILP モデル: 変数=${Object.keys(model.variables).length}, 制約=${Object.keys(model.constraints).length}` +
    ` (スタッフ ${staffArr.length} × 日 ${datesArr.length} × マーク ${allMarks.size})`
  );

  let result;
  try {
    const t0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    result = solver.Solve(model);
    const t1 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    console.log(`🧮 ILP solve 完了: ${Math.round(t1 - t0)}ms, feasible=${result?.feasible}, result=${result?.result}`);
  } catch (e) {
    console.error(`🧮 ILP solver エラー:`, e?.message);
    return { feasible: false, applied: false };
  }

  if (!result || !result.feasible) {
    console.warn(`🧮 ILP: 実行可能解なし（制約矛盾）`);
    return { feasible: false, applied: false };
  }

  // 結果を matrix に適用
  let changed = 0;
  staffArr.forEach((empId) => {
    datesArr.forEach((date) => {
      if (isFrozenLike(empId, date)) return;
      const k = keyOf(empId, date);
      const curMark = state.matrix[k] || '';
      let chosen = null;
      for (const mark of allMarks) {
        const varName = `x_${empId}_${date}_${mark}`;
        if (result[varName] != null && result[varName] >= 0.5) {
          chosen = mark;
          break;
        }
      }
      if (chosen != null && chosen !== curMark) {
        state.matrix[k] = chosen;
        changed += 1;
      }
    });
  });

  const afterShortages = collectFixedShortages(state);
  console.log(
    `🧮 ILP 修復完了: ${changed} セル変更 / 不足 ${shortages.length}→${afterShortages.length}`
  );
  return { feasible: true, applied: true, changed };
};

const runFixedShortageRepair = async (state, opts = {}) => {
  const maxDepth = opts.maxDepth || 10;
  // 🔥 緩和: タイムアウトのみが効くように
  const maxPasses = opts.maxPasses || 2000;
  const timeoutMs = opts.timeoutMs || 60_000;
  const onProgress = opts.onProgress || (() => {});
  const progressStart = opts.progressStart ?? 93;
  const progressEnd = opts.progressEnd ?? 94;

  const startTime = (typeof performance !== 'undefined' ? performance.now() : Date.now());
  const initial = collectFixedShortages(state);
  if (initial.length === 0) {
    console.log('✅ 固定不足なし（修復スキップ）');
    return;
  }
  console.log(`📏 固定不足 ${initial.length} 件をクロスパターン交換で修復試行...`);

  let totalFixed = 0;
  let pass = 0;
  let noProgressStreak = 0;
  while (pass < maxPasses) {
    pass += 1;
    // 🔥 UI スレッドに制御を返す
    // eslint-disable-next-line no-await-in-loop
    await yieldToBrowser();
    const elapsed = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - startTime;
    if (elapsed > timeoutMs) {
      console.warn(`📏 固定不足修復: タイムアウト (${Math.round(elapsed)}ms)`);
      break;
    }
    onProgress(progressStart + Math.floor(Math.min(1, elapsed / timeoutMs) * (progressEnd - progressStart)));

    const shortages = collectFixedShortages(state);
    if (shortages.length === 0) break;

    // 🔥 個別違反の試行失敗回数を記録。3 回連続で失敗した違反は「物理的に不可能」と
    //    判定してこの修復セッション中はスキップ。これがないと永久ループになる。
    if (!state._fixedShortageFailCount) state._fixedShortageFailCount = new Map();

    let resolvedThisPass = 0;
    for (const s of shortages) {
      const violationKey = `${s.date}_${s.mark}`;
      // 過去 3 回以上失敗した違反はスキップ
      if ((state._fixedShortageFailCount.get(violationKey) || 0) >= 3) continue;

      const re = collectFixedShortages(state).find(
        (x) => x.date === s.date && x.mark === s.mark
      );
      if (!re) { resolvedThisPass += 1; continue; }

      const changes = [];
      const visited = new Set();
      if (tryRepairFixedShortage(state, re, 0, maxDepth, changes, visited)) {
        resolvedThisPass += 1;
        totalFixed += 1;
        // 成功 → 失敗カウンタリセット
        state._fixedShortageFailCount.delete(violationKey);
        if (pass <= 2) {
          console.log(`  ✅ ${re.date} ${re.mark}: 不足解消 (変更 ${changes.length} 件)`);
        }
      } else {
        // 失敗 revert + カウンタ加算
        for (let i = changes.length - 1; i >= 0; i--) {
          state.matrix[changes[i].key] = changes[i].before;
        }
        const cnt = (state._fixedShortageFailCount.get(violationKey) || 0) + 1;
        state._fixedShortageFailCount.set(violationKey, cnt);
        if (cnt === 3 && pass <= 5) {
          console.log(`  🚫 ${re.date} ${re.mark}: 3 回連続失敗 → 物理的に不可能と判定、スキップ`);
        }
      }
    }

    // 全違反が「諦め済み or 解消済み」なら即終了
    const remainingTryable = collectFixedShortages(state).filter(
      (x) => (state._fixedShortageFailCount.get(`${x.date}_${x.mark}`) || 0) < 3
    );
    if (remainingTryable.length === 0) {
      console.log(`📏 残り違反は全て「物理的に不可能」と判定 → 修復終了`);
      break;
    }

    if (resolvedThisPass === 0) {
      noProgressStreak += 1;
      // 進捗ゼロ pass が 5 回連続したら打ち切り（フィンガープリント実質変わらないため）
      if (noProgressStreak >= 5) {
        console.log(`📏 進捗ゼロ ${noProgressStreak} pass 連続 → 修復打ち切り`);
        break;
      }
    } else {
      noProgressStreak = 0;
    }
  }
  const remaining = collectFixedShortages(state);
  const impossible = remaining.filter(
    (x) => (state._fixedShortageFailCount?.get(`${x.date}_${x.mark}`) || 0) >= 3
  ).length;
  console.log(
    `📏 固定不足修復完了: ${totalFixed} 件解消 / 残り ${remaining.length} 件` +
    `（うち ${impossible} 件は物理的に不可能と判定） (${pass} passes)`
  );
};

// =============================================================================
// Phase 7.8: チェーン整合バリデーション
//   is_chain_only_pattern (明け など) のマークが「単独で」配置されている箇所を検出。
//   原因: 修復経路の玉突き / CSV import / 前月由来 / 過剰削減の副作用 で
//        「夜勤を消したのに明けが残る」状態が発生しうる。最終的に OFF に補正。
//   さらに、過足固定パターンの検出も行う（required を超えた配置を OFF に削る）。
//   ただし「希望由来」「frozen」は触らない。
// =============================================================================
const runChainConsistencyCheck = (state) => {
  // 🔥 「dock target マーク」(=どこかのパターンの post_shift_ids に登録されている
  //    マーク) を全部集める。is_chain_only_pattern フラグに依存せず、
  //    設定上「後続として登場するマーク」を全て対象にする。
  const dockTargetMarks = new Set();
  state.patterns.forEach((p) => {
    (p.postShiftIds || []).forEach((ref) => {
      const target = state.patternById[ref] || state.patternByMark[ref];
      if (target?.displayMark) dockTargetMarks.add(target.displayMark);
    });
  });

  let chainFixed = 0;
  let chainStartBroken = 0;
  const chainSamples = [];
  const startSamples = [];
  Object.values(state.perStaff).forEach((staff) => {
    if (staff.excludeFromAutoAssign) return;
    const empId = staff.employeeId;
    state.dates.forEach((date, idx) => {
      const k = keyOf(empId, date);
      const v = state.matrix[k];
      if (!v || v === OFF_MARK) return;

      // ケース1: 単独 follow（dock target が前日 chain start なしで残ってる）→ 休に倒す
      if (dockTargetMarks.has(v) && !isChainFollowMark(state, empId, date)) {
        if (state.frozen.has(k)) return;
        if (state.hopes && state.hopes[k]) return;
        state.matrix[k] = OFF_MARK;
        chainFixed += 1;
        if (chainSamples.length < 5) {
          chainSamples.push(`${empId} ${date}: ${v} → 公（前日に chain start なし）`);
        }
        return;
      }

      // ケース2: chain start なのに翌日が期待された follow ではない
      const pattern = state.patternByMark[v];
      if (!pattern?.postShiftIds?.length) return;
      if (idx + 1 >= state.dates.length) return; // 月末は翌月キューに任せる
      const nextDate = state.dates[idx + 1];
      const nextK = keyOf(empId, nextDate);
      const nextV = state.matrix[nextK];
      const expectedMarks = pattern.postShiftIds
        .map((ref) => {
          const t = state.patternById[ref] || state.patternByMark[ref];
          return t?.displayMark;
        })
        .filter(Boolean);
      if (expectedMarks.includes(nextV)) return; // 正常

      // 🔥 改善: 「chain start を倒す（=不足を作る）」より先に「翌日を follow に書き換える」を試す。
      //    これがないと runFixedShortageRepair が直したものを毎サイクル取り消し → 無限ループ。
      //    翌日が空 / OFF / 触れる柔軟セルなら follow に書き換えるだけで整合性を取れる。
      const expectedFollow = expectedMarks[0];
      const nextPat = nextV ? state.patternByMark[nextV] : null;
      const nextIsTouchable = !state.frozen.has(nextK)
        && !(state.hopes && state.hopes[nextK])
        && (!nextV
          || nextV === OFF_MARK
          || (nextPat && !isFixedOnDate(nextPat, nextDate)
              && !nextPat.postShiftIds?.length
              && !isChainFollowMark(state, empId, nextDate)));
      if (expectedFollow && nextIsTouchable) {
        state.matrix[nextK] = expectedFollow;
        chainFixed += 1;
        if (chainSamples.length < 5) {
          chainSamples.push(`${empId} ${nextDate}: ${nextV || '空'} → ${expectedFollow}（前日 ${date}=${v} の follow 補完）`);
        }
        return;
      }

      // 翌日が動かせない → やむを得ず chain start を倒す
      if (state.frozen.has(k)) return;
      if (state.hopes && state.hopes[k]) return;
      state.matrix[k] = OFF_MARK;
      chainStartBroken += 1;
      if (startSamples.length < 5) {
        startSamples.push(`${empId} ${date}: ${v} → 公（翌日 ${nextDate}=${nextV || '空'} が follow ではなく動かせない）`);
      }
    });
  });
  if (chainFixed > 0) {
    console.warn(`🔗 チェーン整合: 後続マーク単独残り ${chainFixed} 件を 休 化`, chainSamples);
  }
  if (chainStartBroken > 0) {
    console.warn(`🔗 チェーン整合: chain start 後 follow 不整合 ${chainStartBroken} 件を 休 化`, startSamples);
  }
  if (chainFixed === 0 && chainStartBroken === 0) {
    console.log('🔗 チェーン整合: 不整合なし');
  }

  // 固定パターンの過足検出 + 修正
  //   固定モードのパターンは required ぴったりに揃えるべき。
  //   default fill などで日勤マークが過剰に入った結果、固定 required を超えてる場合、
  //   超過分を OFF に削る。
  let overFixed = 0;
  const overSamples = [];
  state.patterns.forEach((pattern) => {
    if (pattern.isChainOnlyPattern) return;
    const mark = pattern.displayMark;
    if (!mark || mark === OFF_MARK) return;
    state.dates.forEach((date) => {
      if (!isFixedOnDate(pattern, date)) return;
      // 例外: dailyOverride がある日はその値が固定
      const ovr = state.dailyOverrides?.[date]?.[pattern.id];
      if (ovr === 0) return; // override 0 は別ロジックで処理
      const required = getRequiredCount(pattern, date, state.holidaySet, state.dailyOverrides, state);
      const current = countOnDate(state, date, mark);
      if (current <= required) return;
      // 超過 → 触れるセルから削る
      let surplus = current - required;
      const candidates = Object.keys(state.perStaff)
        .filter((empId) => {
          if (state.matrix[keyOf(empId, date)] !== mark) return false;
          if (state.frozen.has(keyOf(empId, date))) return false;
          if (state.perStaff[empId].excludeFromAutoAssign) return false;
          if (state.hopes && state.hopes[`${empId}_${date}`]) return false;
          if (pattern.postShiftIds?.length) return false;
          if (isChainFollowMark(state, empId, date)) return false;
          return true;
        });
      for (const empId of candidates) {
        if (surplus <= 0) break;
        state.matrix[keyOf(empId, date)] = OFF_MARK;
        surplus -= 1;
        overFixed += 1;
        if (overSamples.length < 5) {
          overSamples.push(`${empId} ${date}: ${mark} → 公（固定 ${required} 超過）`);
        }
      }
    });
  });
  if (overFixed > 0) {
    console.warn(`📏 固定パターン過足: ${overFixed} セルを 休 化`, overSamples);
  } else {
    console.log('📏 固定パターン過足: なし');
  }
};

// =============================================================================
// Phase 7: 負荷分散スワップ
// =============================================================================

// スワップ可能性チェック：(empId, date) を newMark に変えても制約 OK か
// （セルは既に値あり、内容を変えたい）
const canSwapTo = (state, empId, date, newMark) => {
  const pattern = state.patternByMark[newMark];
  if (!pattern) return false;
  const staff = state.perStaff[empId];
  if (!staff) return false;
  if (staff.excludeFromAutoAssign) return false;
  if (state.frozen.has(keyOf(empId, date))) return false;
  if (!matchesJobType(staff, pattern)) return false;

  // 一時的にセルをクリアして検査（状態変更を最小化）
  const orig = state.matrix[keyOf(empId, date)];
  state.matrix[keyOf(empId, date)] = "";

  let ok = true;
  // スキルグループ
  if (!matchesSkillGroup(state, empId, pattern)) ok = false;
  // 月間上限
  if (ok && !checkMonthlyLimits(state, empId, pattern)) ok = false;
  // 連勤: 寄与度ベース + 前後両方向で配置後の累計連勤値を予測
  //   canAssign と同じ式に揃える（以前は固定 +1 で寄与度・後方連結を無視していた）
  if (ok && newMark !== OFF_MARK) {
    const contribution = getConsecutiveContribution(newMark, state.patterns, state.patternByMark);
    if (contribution > 0) {
      const wouldBe = getWouldBeConsecutiveLength(state, empId, date, contribution);
      const limit = state.policy.maxConsecutiveWorkDays ?? 6;
      if (wouldBe > limit) ok = false;
    }
  }
  // 同一マーク連続
  if (ok && pattern.maxSameConsecutive > 0) {
    const sameRun = getSameMarkConsecutive(state, empId, date, newMark);
    if (sameRun + 1 > pattern.maxSameConsecutive) ok = false;
  }
  // 相性
  if (ok && violatesIncompatibility(state, empId, date, pattern)) ok = false;

  // 元に戻す
  state.matrix[keyOf(empId, date)] = orig;
  return ok;
};

const balancePattern = (state, pattern) => {
  if (pattern.flexibilityType === "fixed") return 0;
  if (pattern.isChainOnlyPattern) return 0;
  // 「日勤」系（assignmentModeEnabled）は対象外
  if (pattern.assignmentModeEnabled) return 0;

  let swaps = 0;
  const maxIter = 200;

  for (let iter = 0; iter < maxIter; iter++) {
    // パターン適合スタッフのみ対象
    const eligibleStaff = Object.keys(state.perStaff).filter((empId) => {
      const s = state.perStaff[empId];
      if (s.excludeFromAutoAssign) return false;
      return matchesJobType(s, pattern);
    });
    const counts = eligibleStaff.map((empId) => ({
      empId,
      n: countPerStaffPattern(state, empId, pattern.id),
    }));
    counts.sort((a, b) => b.n - a.n);
    if (counts.length < 2) return swaps;
    const top = counts[0];
    const bot = counts[counts.length - 1];
    if (top.n - bot.n <= 1) return swaps;

    // 同日スワップを優先で探す
    let swapped = false;
    for (const date of state.dates) {
      const topCell = state.matrix[keyOf(top.empId, date)];
      const botCell = state.matrix[keyOf(bot.empId, date)];
      if (topCell !== pattern.displayMark) continue;
      if (state.frozen.has(keyOf(top.empId, date))) continue;
      if (state.frozen.has(keyOf(bot.empId, date))) continue;
      // bot は「日勤系」マークでないと交換不可（自分の同種パターンや夜勤は触らない）
      const botPattern = state.patternByMark[botCell];
      if (!botPattern || !botPattern.assignmentModeEnabled) continue;

      const topDayMark = state.dayShiftMarkByStaff[top.empId] || botCell;

      // top: pattern → topDayMark, bot: botCell → pattern
      if (!canSwapTo(state, top.empId, date, topDayMark)) continue;
      if (!canSwapTo(state, bot.empId, date, pattern.displayMark)) continue;

      // 実行
      state.matrix[keyOf(top.empId, date)] = topDayMark;
      state.matrix[keyOf(bot.empId, date)] = pattern.displayMark;
      swaps += 1;
      swapped = true;
      break;
    }

    if (!swapped) return swaps;
  }
  return swaps;
};

const runLoadBalance = (state) => {
  let total = 0;
  state.patterns.forEach((pattern) => {
    const s = balancePattern(state, pattern);
    if (s > 0) console.log(`  ⚖️ ${pattern.name}: ${s} 件スワップ`);
    total += s;
  });
  state.stats.swaps = total;
  console.log(`  ⚖️ 負荷分散スワップ合計 ${total} 件`);
};

// =============================================================================
// 翌月セル DB 書き込み
// =============================================================================

const flushCrossMonthWrites = async (state) => {
  if (state.crossMonthWrites.size === 0) return;
  const rows = Array.from(state.crossMonthWrites.values());
  try {
    const { error } = await supabase
      .from("monthly_shifts")
      .upsert(rows, { onConflict: "organization_id,employee_id,shift_date" });
    if (error) throw error;
    state.stats.crossMonthWrites = rows.length;
    console.log(`✅ 翌月セル ${rows.length} 件を Supabase 書込`);
  } catch (e) {
    console.error("❌ 翌月書込エラー:", e);
  }
};

// =============================================================================
// 検証 → conflicts 構築
// =============================================================================

const runValidation = (state) => {
  const { monthlyMin, monthlyMax, maxConsecutiveWorkDays } = state.policy;
  const nightPatternMarks = new Set(
    state.patterns
      .filter(
        (p) =>
          p.displayMark === "/"
          || p.displayMark?.startsWith("/")
          || p.name?.includes("夜勤")
      )
      .map((p) => p.displayMark)
  );

  // 🔥 全パターンで required vs current の差分を再収集。
  //   UI 側の「3/5」表示と一致させるため、固定/柔軟関係なく required ベース。
  state.conflicts.fixedShortage = [];
  state.conflicts.flexShortage = [];
  state.patterns.forEach((pattern) => {
    if (pattern.isChainOnlyPattern) return;
    if (!pattern.displayMark) return;
    state.dates.forEach((date) => {
      const required = getRequiredCount(pattern, date, state.holidaySet, state.dailyOverrides, state);
      // 🔥 required 充足判定は countForRequirement（excludedCountGroups 除外）
      const current = countForRequirement(state, date, pattern.displayMark, pattern);
      const isFixed = isFixedOnDate(pattern, date);
      const hasOverride = state.dailyOverrides?.[date]?.[pattern.id] != null;
      const treatAsFixed = isFixed || hasOverride;
      if (treatAsFixed) {
        if (current !== required) {
          state.conflicts.fixedShortage.push({
            date,
            patternName: pattern.name,
            mark: pattern.displayMark,
            have: current,
            required,
            isOverride: hasOverride,
            diff: current - required,
          });
        }
      } else if (required > 0 && current < required) {
        // 柔軟でも required を満たさないなら不一致として記録
        state.conflicts.fixedShortage.push({
          date,
          patternName: pattern.name,
          mark: pattern.displayMark,
          have: current,
          required,
          isOverride: false,
          diff: current - required,
          isFlexible: true,
        });
      }
      // 柔軟の minRequired 未達も別途記録（既存ロジック維持）
      if (!treatAsFixed && current < (pattern.minRequired || 0)) {
        state.conflicts.flexShortage.push({
          date,
          patternName: pattern.name,
          mark: pattern.displayMark,
          have: current,
          minRequired: pattern.minRequired,
        });
      }
    });
  });
  if (state.conflicts.fixedShortage.length > 0) {
    console.warn(`📏 最終検証: パターン不一致 ${state.conflicts.fixedShortage.length} 件 (固定+柔軟required未達)`);
  }
  if (state.conflicts.flexShortage.length > 0) {
    console.warn(`📏 最終検証: 柔軟パターン最低未達 ${state.conflicts.flexShortage.length} 件`);
  }

  // 🔍 target_group 制限のあるパターンに、非メンバーが配置されていないかを検証
  //    配置経路（applyHopes / runChainPass / runSmartDefaultFill 等）に関わらず最終結果を確認。
  const skillGroupViolations = [];
  const patternsWithGroup = state.patterns.filter((p) => p.targetGroup);
  patternsWithGroup.forEach((p) => {
    const memberSet = state.skillGroupMembership?.byName?.[p.targetGroup];
    if (!memberSet) return; // matchesSkillGroup 側で警告済み
    Object.values(state.perStaff).forEach((staff) => {
      if (staff.excludeFromAutoAssign) return;
      state.dates.forEach((date) => {
        const v = state.matrix[keyOf(staff.employeeId, date)];
        if (v !== p.displayMark) return;
        const myStaffId = staff.staffId;
        if (!myStaffId || !memberSet.has(String(myStaffId))) {
          skillGroupViolations.push({
            empId: staff.employeeId,
            date,
            mark: p.displayMark,
            patternName: p.name,
            targetGroup: p.targetGroup,
          });
        }
      });
    });
  });
  if (skillGroupViolations.length > 0) {
    console.error(
      `❌ スキルグループ違反 ${skillGroupViolations.length} 件: ` +
      `target_group メンバー以外のスタッフが配置されています`
    );
    console.group('🔍 スキルグループ違反の詳細（先頭10件）');
    skillGroupViolations.slice(0, 10).forEach((v) => {
      console.error(
        `  ${v.empId} ${v.date} ${v.patternName}(${v.mark}) ← target_group="${v.targetGroup}" のメンバーではない`
      );
    });
    console.groupEnd();
    state.conflicts.skillGroupViolations = skillGroupViolations;
  }

  // 🔥 同一マーク最大連続違反の最終集計（緩和段階で無視された分も含めて検出）
  //    と 後続禁止違反 も同時に検出
  state.conflicts.sameConsecutive = [];
  state.conflicts.forbiddenPost = [];
  state.patterns.forEach((pattern) => {
    if (!pattern.displayMark) return;
    const limit = pattern.maxSameConsecutive;
    if (!limit || limit <= 0) return;
    Object.values(state.perStaff).forEach((staff) => {
      if (staff.excludeFromAutoAssign) return;
      const empId = staff.employeeId;
      let run = 0;
      let start = null;
      state.dates.forEach((date) => {
        const v = state.matrix[keyOf(empId, date)];
        if (v === pattern.displayMark) {
          if (run === 0) start = date;
          run += 1;
          if (run > limit) {
            state.conflicts.sameConsecutive.push({
              empId, mark: pattern.displayMark, patternName: pattern.name,
              start, end: date, length: run, limit,
            });
          }
        } else {
          run = 0;
          start = null;
        }
      });
    });
  });
  // 重複集約: 同一 (empId, start) は最長 run のみ残す
  if (state.conflicts.sameConsecutive.length > 0) {
    const m = new Map();
    state.conflicts.sameConsecutive.forEach((c) => {
      const k = `${c.empId}_${c.start}_${c.mark}`;
      if (!m.has(k) || m.get(k).length < c.length) m.set(k, c);
    });
    state.conflicts.sameConsecutive = Array.from(m.values());
    console.warn(`📏 同一マーク連続違反 ${state.conflicts.sameConsecutive.length} 件`);
  }

  // 後続禁止違反の検出: 各パターンの forbiddenPostShiftIds に基づき、
  // 「前日が p で 今日がその禁止先」のセルを違反として記録
  state.patterns.forEach((p) => {
    const forbidden = p.forbiddenPostShiftIds || [];
    if (forbidden.length === 0) return;
    const forbiddenMarks = forbidden
      .map((ref) => {
        const t = state.patternById[ref] || state.patternByMark[ref];
        return t?.displayMark;
      })
      .filter(Boolean);
    if (forbiddenMarks.length === 0) return;
    Object.values(state.perStaff).forEach((staff) => {
      if (staff.excludeFromAutoAssign) return;
      const empId = staff.employeeId;
      state.dates.forEach((date, idx) => {
        if (state.matrix[keyOf(empId, date)] !== p.displayMark) return;
        if (idx + 1 >= state.dates.length) return; // 月末は対象外（翌月情報は別途）
        const nextDate = state.dates[idx + 1];
        const nextMark = state.matrix[keyOf(empId, nextDate)];
        if (forbiddenMarks.includes(nextMark)) {
          state.conflicts.forbiddenPost.push({
            empId, date, mark: p.displayMark, patternName: p.name,
            nextDate, nextMark,
          });
        }
      });
    });
  });
  if (state.conflicts.forbiddenPost.length > 0) {
    console.warn(`🚫 後続禁止違反 ${state.conflicts.forbiddenPost.length} 件`);
  }

  Object.values(state.perStaff).forEach((staff) => {
    if (staff.excludeFromAutoAssign) return;
    let offCount = 0;
    let nightCount = 0;
    state.dates.forEach((date) => {
      const v = state.matrix[keyOf(staff.employeeId, date)];
      if (v === OFF_MARK) offCount += 1;
      if (v && nightPatternMarks.has(v)) nightCount += 1;
    });
    if (offCount < monthlyMin) {
      state.conflicts.off[staff.employeeId] = {
        current: offCount,
        target: monthlyMin,
        diff: monthlyMin - offCount,
      };
    } else if (offCount > monthlyMax) {
      state.conflicts.off[staff.employeeId] = {
        current: offCount,
        target: monthlyMax,
        diff: offCount - monthlyMax,
      };
    }
    state.conflicts.night[staff.employeeId] = { current: nightCount };

    // 連続勤務違反
    //   累計ベース: 各セルの consecutive_contribution を加算
    //   - 寄与度 0（休/有給/夏休み/明け等）は連勤を切る
    let run = 0;
    let start = null;
    state.dates.forEach((date) => {
      const v = state.matrix[keyOf(staff.employeeId, date)];
      const explicit = v ? hasExplicitContribution(v, state.patterns, state.patternByMark) : false;
      const isFollow = v && !explicit ? isChainFollowMark(state, staff.employeeId, date) : false;
      const c = v
        ? (isFollow ? 0 : getConsecutiveContribution(v, state.patterns, state.patternByMark))
        : 0;
      if (c > 0) {
        if (run === 0) start = date;
        run += c;
        if (run > maxConsecutiveWorkDays) {
          state.conflicts.consecutive.push({
            empId: staff.employeeId,
            start,
            end: date,
            length: run,
          });
        }
      } else {
        run = 0;
        start = null;
      }
    });
  });
};

// =============================================================================
// メインエントリ
// =============================================================================

// 🏷️ コードバージョンタグ: コードが更新されたら必ず値を変える。
//   毎実行この値がコンソールに出るので、ブラウザが古いキャッシュを使っていたら判別可能。
const SHIFT_AUTO_ASSIGN_VERSION = '2026-06-26-r52-chain-only-dynamic-and-event-display';

export const runFullAutoAssignment = async (
  year, // eslint-disable-line no-unused-vars
  month, // eslint-disable-line no-unused-vars
  staffList,
  hopes,
  shiftPatterns,
  holidays,
  dates,
  settings,
  onProgress = () => {},
  options = {}
) => {
  // 各実行で必ず違う ID。「同じ実行が使い回されている」疑惑を判別するため。
  const runId = Math.random().toString(36).slice(2, 10);
  const startMs = (typeof performance !== 'undefined' ? performance.now() : Date.now());
  console.log(`🚀 自動シフト作成 開始 [version=${SHIFT_AUTO_ASSIGN_VERSION}, runId=${runId}, ts=${new Date().toISOString()}]`);

  const organizationId =
    options.organizationId
    || settings?.organizationId
    || staffList?.[0]?.organizationId;
  if (!organizationId) throw new Error("organizationId is required");
  if (!Array.isArray(dates) || dates.length === 0)
    throw new Error("dates is required");

  // 職種フィルタ：指定された場合、その職種のスタッフと、その職種に適用可能なパターンだけに絞る
  //   - staffList: job_type_id がフィルタと一致するスタッフのみ
  //   - shiftPatterns: target_job_types が空（全職種共通）または フィルタを含むパターンのみ
  const jobTypeFilter = options.jobTypeFilter
    ? String(options.jobTypeFilter)
    : null;
  let filteredStaff = staffList || [];
  let filteredPatterns = shiftPatterns || [];
  if (jobTypeFilter) {
    const beforeStaff = filteredStaff.length;
    const beforePat = filteredPatterns.length;
    filteredStaff = filteredStaff.filter(
      (s) => String(s.job_type_id || s.jobTypeId || "") === jobTypeFilter
    );
    filteredPatterns = filteredPatterns.filter((p) => {
      const targets = (p.targetJobTypes || p.configuration?.target_job_types || []).map(String);
      return targets.length === 0 || targets.includes(jobTypeFilter);
    });
    console.log(
      `🎯 職種フィルタ ${jobTypeFilter}: staff ${beforeStaff}→${filteredStaff.length}, patterns ${beforePat}→${filteredPatterns.length}`
    );
  }

  const patterns = normalizePatterns(filteredPatterns);
  console.log(`📋 パターン ${patterns.length} 件`);
  onProgress(10);

  const firstDate = dates[0];
  const lastDate = dates[dates.length - 1];
  const [policy, incompatibilities, { prior, next }, skillGroupMembership, dailyOverrides, recurringEvents] = await Promise.all([
    fetchPolicy(organizationId),
    fetchIncompatibilities(organizationId),
    fetchContextMatrices(organizationId, firstDate, lastDate),
    fetchSkillGroupMembership(organizationId, filteredStaff),
    fetchDailyOverrides(organizationId, firstDate, lastDate),
    fetchRecurringEvents(organizationId, firstDate, lastDate),
  ]);
  console.log(`📋 policy:`, policy);
  console.log(`📋 相性ルール ${incompatibilities.length} 件 / 前14日 ${Object.keys(prior).length} 件 / 後14日 ${Object.keys(next).length} 件`);
  console.log(
    `📋 スキルグループ ${Object.keys(skillGroupMembership.byName).length} 種`,
    Object.fromEntries(
      Object.entries(skillGroupMembership.byName).map(([n, s]) => [n, s.size])
    )
  );
  console.log(`📋 特定日オーバーライド ${Object.keys(dailyOverrides).length} 日分`);
  console.log(`📋 当月イベント ${recurringEvents.length} 件`);
  onProgress(20);

  const state = buildState({
    staffList: filteredStaff,
    patterns,
    dates,
    holidays: holidays || [],
    policy,
    incompatibilities,
    priorMatrix: prior,
    nextMatrix: next,
    organizationId,
    staffGroups: options.staffGroups || {},
    skillGroupMembership,
    dailyOverrides,
  });
  console.log(`👥 処理対象スタッフ ${Object.keys(state.perStaff).length}名（フィルタ適用後）`);
  // スキルグループ統計（処理対象スタッフのうち、各パターンのグループに該当する人数）
  const groupedPatterns = patterns.filter((p) => p.targetGroup);
  console.log(
    `📋 スキルグループ制限あり ${groupedPatterns.length}件 / グループマスタ ${Object.keys(state.skillGroupMembership.byName).length} 種`
  );
  if (groupedPatterns.length > 0) {
    const unresolved = [];
    groupedPatterns.forEach((p) => {
      const memberSet = state.skillGroupMembership.byName[p.targetGroup];
      if (!memberSet) {
        unresolved.push(`${p.name}(${p.displayMark}) → "${p.targetGroup}"`);
        console.warn(`    ⚠️ ${p.name}(${p.displayMark}) → グループ "${p.targetGroup}" がマスタに存在しません`);
        return;
      }
      // 処理対象（filteredStaff）のうち、staffId がこのグループに含まれる人数を数える
      const n = Object.values(state.perStaff).filter(
        (s) => s.staffId && memberSet.has(String(s.staffId))
      ).length;
      console.log(
        `    - ${p.name}(${p.displayMark}) → グループ "${p.targetGroup}" 所属 ${n}人 / 処理対象 ${Object.keys(state.perStaff).length}人中`
      );
    });
    if (unresolved.length > 0) {
      console.warn(
        `⚠️ 未解決のスキルグループ ${unresolved.length}件 — このパターンは全員許可で扱われます: `,
        unresolved
      );
    }
  }

  // Phase 1: hopes / customShiftPattern
  const allHopes = { ...(hopes || {}), ...(options.adminHopes || {}) };
  // 🔥 連勤修復フェーズで「希望由来のセルは触らない」判定に使う
  state.hopes = allHopes;
  // 🔥 管理者希望のキー集合を state に保持。applyHopes 側で skill group check を
  //    skip して「管理者意思を最優先」に扱う（1件落ちる現象の対策）。
  state.adminHopeKeys = new Set(Object.keys(options.adminHopes || {}));
  console.log(`📥 受け取った希望 ${Object.keys(allHopes).length} 件 (うち管理者 ${state.adminHopeKeys.size} 件)`);
  applyHopes(state, allHopes);
  debugCountConsecutiveViolations(state, 'applyHopes 後');
  applyCustomShiftPatterns(state);
  debugCountConsecutiveViolations(state, 'applyCustomShiftPatterns 後');
  applyPriorMonthCarryover(state); // 前月末の後続シフトを当月初日に引き継ぐ
  debugCountConsecutiveViolations(state, 'applyPriorMonthCarryover 後');
  // 🔥 係活動などのイベント反映: 対象スキルグループ全員を指定勤務に強制配置
  //    希望 / custom よりも後に適用することで「管理者意思を最優先」にする。
  applyRecurringEvents(state, recurringEvents);
  debugCountConsecutiveViolations(state, 'applyRecurringEvents 後');
  console.log(`🔒 固定セル ${state.frozen.size} 件`);
  onProgress(35);

  // Phase 2: 事前検証
  preflightValidation(state);
  onProgress(40);

  // Phase 3〜4.5: グローバル再試行
  //   方針見直し: 100回繰り返しても精度はほぼ上がらない（≒同じ叩き台）→ 時間の無駄。
  //   叩き台を少数 (5) 通り作り、その中の最良を採用。その後の反復修復に時間を割く。
  const MAX_GLOBAL_RETRIES = 5;
  const baseSnapshot = {
    matrix: { ...state.matrix },
    frozen: new Set(state.frozen),
    crossMonthWrites: new Map(state.crossMonthWrites),
    nextMatrix: { ...state.nextMatrix },
  };
  let bestRetry = null;
  let bestRetryScore = Infinity;
  // eslint-disable-next-line no-await-in-loop
  for (let r = 0; r < MAX_GLOBAL_RETRIES; r++) {
    // 各試行で frozen 直後の状態にリセット
    state.matrix = { ...baseSnapshot.matrix };
    state.frozen = new Set(baseSnapshot.frozen);
    state.crossMonthWrites = new Map(baseSnapshot.crossMonthWrites);
    state.nextMatrix = { ...baseSnapshot.nextMatrix };
    state.conflicts = { off: {}, night: {}, hopeOverflow: [], fixedShortage: [], flexShortage: [], consecutive: [] };
    state.globalSeed = r;

    preScatterOff(state); // 公休を月内に均等散布
    if (r === 0) debugCountConsecutiveViolations(state, 'preScatterOff 後 (seed=0)');
    runIterativeFixedPass(state); // 固定→柔軟（ソフト制約 4 段階）
    if (r === 0) debugCountConsecutiveViolations(state, 'runIterativeFixedPass 後 (seed=0)');
    runRepairPass(state); // 修復パス（連勤切る／displace 等）
    if (r === 0) debugCountConsecutiveViolations(state, 'runRepairPass 後 (seed=0)');

    const fixedShort = state.conflicts.fixedShortage.length;
    const flexShort = state.conflicts.flexShortage.length;
    // 🔥 連勤違反もスコアに含める（これまで完全無視 → 連勤悪化解が "ベスト" 採用される
    //    バグの原因）。重み: fixed 不足 1000 > 連勤違反 100 > flex 不足 10。
    //    後段の修復で直しやすいので fixed が最優先、次に連勤、最後に flex。
    let consecCount = 0;
    Object.values(state.perStaff || {}).forEach((s) => {
      if (s.excludeFromAutoAssign) return;
      const v = detectMaxViolation(state, s.employeeId);
      if (v) consecCount += 1;
    });
    const score = fixedShort * 1000 + consecCount * 100 + flexShort * 10;
    console.log(
      `🔄 グローバル試行 ${r + 1}/${MAX_GLOBAL_RETRIES} (seed=${r}): ` +
      `fixed残=${fixedShort} 連勤違反=${consecCount} flex残=${flexShort} score=${score}`
    );

    if (score < bestRetryScore) {
      bestRetryScore = score;
      bestRetry = {
        matrix: { ...state.matrix },
        frozen: new Set(state.frozen),
        crossMonthWrites: new Map(state.crossMonthWrites),
        nextMatrix: { ...state.nextMatrix },
        conflicts: {
          off: { ...state.conflicts.off },
          night: { ...state.conflicts.night },
          hopeOverflow: [...state.conflicts.hopeOverflow],
          fixedShortage: [...state.conflicts.fixedShortage],
          flexShortage: [...state.conflicts.flexShortage],
          consecutive: [...state.conflicts.consecutive],
        },
      };
    }
    if (fixedShort === 0 && flexShort === 0 && consecCount === 0) {
      console.log(`✅ ${r + 1} 回目で全条件達成（連勤違反含む）、グローバル探索終了`);
      break;
    }
    // 🔥 グローバル探索の進捗を逐次更新（40 → 72 の範囲を等分）。
    onProgress(40 + Math.floor(((r + 1) / MAX_GLOBAL_RETRIES) * 32));
    // 🔥 UI スレッドに制御を返す（"ページ応答なし" 防止）
    // eslint-disable-next-line no-await-in-loop
    await yieldToBrowser();
  }
  // ベスト解を採用
  if (bestRetry) {
    state.matrix = bestRetry.matrix;
    state.frozen = bestRetry.frozen;
    state.crossMonthWrites = bestRetry.crossMonthWrites;
    state.nextMatrix = bestRetry.nextMatrix;
    state.conflicts = bestRetry.conflicts;
  }
  onProgress(72);

  debugCountConsecutiveViolations(state, 'グローバル探索完了後（ベスト解採用）');

  // Phase 4.7: 日勤のみスタッフの空欄埋め（default fill より先に処理）
  runDayShiftOnlyFill(state);
  debugCountConsecutiveViolations(state, 'runDayShiftOnlyFill 後');
  onProgress(76);

  // Phase 5: スマート default fill（連勤上限 + 公休 min/max を考慮）
  runSmartDefaultFill(state);
  debugCountConsecutiveViolations(state, 'runSmartDefaultFill 後');
  onProgress(80);

  // Phase 6: 連鎖
  runChainPass(state);
  debugCountConsecutiveViolations(state, 'runChainPass 後');
  onProgress(85);

  // Phase 7: 負荷分散
  runLoadBalance(state);
  debugCountConsecutiveViolations(state, 'runLoadBalance 後');
  onProgress(88);

  // Phase 7.4: 柔軟パターン過剰割り当ての削減
  //   設定値（required）を超えて配置された分を 休 に戻す。
  //   月公休カウントに転換 → 連勤修復の余地を増やす。
  runReduceOverAssignedFlex(state);
  debugCountConsecutiveViolations(state, 'runReduceOverAssignedFlex 後');
  onProgress(89);

  // 🔥 Phase 7.45: シンプル順次修復（1日目から月末まで素直に過不足を振替）
  //   複雑な玉突きを試す前に、最もシンプルな修正を 1 パス走らせる。
  //   - 不足パターン: 柔軟パターンの余裕分から振替 + OFF（希望休でない）から採用
  //   - 過剰パターン: 超過分を OFF に
  //   これで多くの違反が「揺さぶり等の大技なしに」直る。
  runSequentialRepair(state);
  debugCountConsecutiveViolations(state, 'runSequentialRepair 後');
  // 2 周目: 1 周目で振り替えた結果新しく生まれた不足/過剰も拾う
  runSequentialRepair(state);
  debugCountConsecutiveViolations(state, 'runSequentialRepair 2周目 後');
  onProgress(90);

  // Phase 7.5-7.8: 段階的緩和修復（ユーザー方針: ロジカルに分岐）
  //   揺さぶり/ランダム性を廃止。「触れる範囲」を段階的に広げて反復する。
  //
  //   Stage 1: 柔軟セルのみで修復を試みる（固定セルは絶対動かさない）
  //   Stage 2: 連勤違反者の固定セルも動かす（代替必須）
  //   Stage 3: 既存の RELAX_STAGES を再利用し、ソフト制約を緩めて穴埋め
  //
  //   各 Stage では「sequential 振替 → consec 修復 → fixed 不足修復」を
  //   違反が減らなくなるまで反復し、減らなくなったら次 Stage へ進む。

  const scoreOf2 = (s) => {
    const c = collectConsecutiveViolationsForRepair(s).length;
    const f = collectFixedShortages(s).length;
    return { consec: c, fixed: f, total: c * 100 + f * 10 };
  };

  const repairStartTime = (typeof performance !== 'undefined' ? performance.now() : Date.now());
  // 🔥 ユーザー方針: 時間は気にしない、ルール違反がなくなるまで。
  //   タイムアウト撤廃。終了条件は『全違反 0』または『物理的に動かしようがない（同じ違反セットが連続）』のみ。
  //   各内部修復関数のタイムアウトも実質無効化（非常に大きい値）。
  const HUGE_TIMEOUT = 3_600_000; // 1 時間（実質無制限。フェイルセーフとして）

  // 違反セットのフィンガープリント: 同じ違反が動かないことを検出
  const violationFingerprint = (s) => {
    const consec = collectConsecutiveViolationsForRepair(s)
      .map((v) => `c:${v.empId}_${v.start}_${v.end}_${Number(v.length).toFixed(1)}`)
      .sort()
      .join('|');
    const fixed = collectFixedShortages(s)
      .map((x) => `f:${x.date}_${x.mark}_${x.required - x.current}`)
      .sort()
      .join('|');
    return consec + '||' + fixed;
  };

  // 1 サイクル: sequential → consec → fixed → sequential
  const runOneRepairCycle = async (label, opts) => {
    // 🔥 毎 cycle 始まりで「諦め違反カウンタ」をリセット。
    //   前 cycle で時間軸 swap や consec 修復が違反を動かしたかもしれないので、
    //   このサイクルでは再挑戦の機会を与える。
    state._fixedShortageFailCount = new Map();

    runSequentialRepair(state);
    runTimeAxisSwapRepair(state);
    // 🔁 同パターン他日交換: D2 の X 持ちを D1 に移動 + D2 を OFF に
    // eslint-disable-next-line no-await-in-loop
    await runSameMarkOtherDateSwap(state);
    // 🪑 席空け連鎖: スキル候補が別パターンに居る → 別の人で席を埋めてスキル者を不足位置に
    // eslint-disable-next-line no-await-in-loop
    await runOpenSeatCascade(state);
    // eslint-disable-next-line no-await-in-loop
    await runConsecutiveRepairPass(state, {
      maxDepth: 12,
      maxPasses: 100_000,
      timeoutMs: HUGE_TIMEOUT,
      onProgress,
      progressStart: opts.progressStart ?? 90,
      progressEnd: opts.progressEnd ?? 91,
      noProgressLimit: 100_000,
    });
    // 🌳 深さ優先連鎖修復: 同日候補がなくても、別日 swap + 連鎖で解消を試みる
    // eslint-disable-next-line no-await-in-loop
    await runDeepChainRepair(state, { maxDepth: 6, timeoutMs: 45_000 });
    // eslint-disable-next-line no-await-in-loop
    await runFixedShortageRepair(state, {
      maxDepth: 12,
      maxPasses: 100_000,
      timeoutMs: HUGE_TIMEOUT,
      onProgress,
      progressStart: opts.progressStart ?? 91,
      progressEnd: opts.progressEnd ?? 92,
    });
    runSequentialRepair(state);
    runTimeAxisSwapRepair(state);
    // 🔁 同パターン他日交換と 🪑 席空け連鎖を 2 周目（残不足にも追従）
    // eslint-disable-next-line no-await-in-loop
    await runSameMarkOtherDateSwap(state);
    // eslint-disable-next-line no-await-in-loop
    await runOpenSeatCascade(state);
    // 深さ優先修復をもう一度（修復で生まれた新規不足にも追従）
    // eslint-disable-next-line no-await-in-loop
    await runDeepChainRepair(state, { maxDepth: 6, timeoutMs: 30_000 });

    // 🔥 (NEW) 同マーク連続違反の同日 swap 修復
    //   ユーザー指摘: 「同マーク連続違反は他勤務とのチェンジで解決できるはず」
    runSameMarkConsecSwap(state);

    // 🔥 (NEW) チェーン整合チェック - cycle 末で毎回実行
    //   ユーザー指摘: 「連鎖専用パターンが単体で出ているところがある」
    //   修復で chain start を変更しても、 follow が残ったままになる経路を毎 cycle で掃除する。
    //   修復ロジック側で都度フォローアップを書くのは煩雑なので、 cycle 末で一括整合化する設計。
    runChainConsistencyCheck(state);

    // 🔥 (NEW) 最後の保険: 連勤違反なし & 固定不足あり時のみ、monthlyMin を破って勤務化を試す
    //   ユーザー方針: 「絶対守るのは連勤違反のみ。人数不足なら公休より勤務優先」
    const scBefore = scoreOf2(state);
    if (scBefore.consec === 0 && scBefore.fixed > 0) {
      console.log(`  💪 [${label}] 公休 monthlyMin ソフト緩和を発動 (連勤違反なし & 固定不足 ${scBefore.fixed} 件)`);
      runTimeAxisSwapRepair(state, { allowOffBelowMin: true, verbose: false });
      const scAfter = scoreOf2(state);
      console.log(`    → 固定不足 ${scBefore.fixed} → ${scAfter.fixed} (連勤 ${scAfter.consec})`);
    }

    const sc = scoreOf2(state);
    console.log(`  [${label}] 連勤=${sc.consec} 固定不足=${sc.fixed}`);
    return sc;
  };

  // Stage を回す:
  //   終了条件は厳密に 2 つ:
  //     1. score === 0 (全違反解消)
  //     2. 違反フィンガープリントが連続 IDENTICAL_LIMIT 回同じ
  //        = どう試しても違反セットが 1 つも動かない = 物理的に不可能
  const IDENTICAL_LIMIT = 30; // 同じ違反セットが 30 cycle 連続 = これ以上は無理
  const runStage = async (stageName, stageOpts = {}) => {
    let lastFingerprint = '';
    let identicalStreak = 0;
    let cycle = 0;
    // eslint-disable-next-line no-await-in-loop
    while (true) {
      cycle += 1;
      const elapsed = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - repairStartTime;
      const sc = scoreOf2(state);
      if (sc.consec === 0 && sc.fixed === 0) return 'done';

      const fp = violationFingerprint(state);
      console.log(`🔁 ${stageName} cycle ${cycle}（経過 ${Math.round(elapsed / 1000)}s, score=${sc.total}, 同一違反連続 ${identicalStreak}）`);
      // eslint-disable-next-line no-await-in-loop
      const after = await runOneRepairCycle(`${stageName}-${cycle}`, stageOpts);
      if (after.total === 0) return 'done';

      const fpAfter = violationFingerprint(state);
      if (fpAfter === fp && fpAfter === lastFingerprint) {
        identicalStreak += 1;
        if (identicalStreak >= IDENTICAL_LIMIT) {
          console.log(`  ↪️ ${stageName}: 同じ違反セットが ${identicalStreak} cycle 連続 → 物理的に動かせないため次 stage へ`);
          return 'stuck';
        }
      } else {
        identicalStreak = 0;
      }
      lastFingerprint = fpAfter;
    }
  };

  // ===== Stage 1: 柔軟セルベースで修復 =====
  //   違反フィンガープリントが 30 cycle 連続で同じになるまで走り続ける。
  let stageResult = await runStage('Stage1', {
    progressStart: 90, progressEnd: 91,
  });
  onProgress(92);

  // ===== Stage 2: 既存の RELAX_STAGES を再利用 =====
  //   グローバル探索内で使った runIterativeFixedPass + runRepairPass を再走。
  //   その後 sequential + consec + fixed を回す。
  if (stageResult !== 'done') {
    console.log(`🔁 Stage2: 既存 runIterativeFixedPass + runRepairPass を再実行（緩和段階）`);
    try {
      runIterativeFixedPass(state);
      runRepairPass(state);
      const sc = scoreOf2(state);
      console.log(`  [Stage2-prep] 連勤=${sc.consec} 固定不足=${sc.fixed}`);
    } catch (e) {
      console.warn('Stage2 例外:', e?.message);
    }
    stageResult = await runStage('Stage2', {
      progressStart: 92, progressEnd: 93,
    });
  }
  onProgress(93);

  // ===== Stage 3: 最終駆け込み =====
  //   さらに同じサイクルを回す。揺さぶり的にもう一度。
  if (stageResult !== 'done') {
    await runStage('Stage3', {
      progressStart: 93, progressEnd: 94,
    });
  }

  // 🧮 Stage 4 (ILP): 段階的緩和 + 悪化時撤回
  //   既存配置に既に違反がある状態だと厳格 ILP は infeasible になる。
  //   制約を段階的に緩めて、解が出る最も厳しい制約レベルで採用する。
  const ilpBefore = scoreOf2(state);
  if (ilpBefore.fixed > 0) {
    console.log(`🧮 Stage4(ILP) 開始: 残不足 ${ilpBefore.fixed} 件 (連勤 ${ilpBefore.consec} 件)`);
    const ilpSnapshot = { matrix: { ...state.matrix } };
    let bestApplied = false;

    // 段階的緩和: 厳しい順に試して、最初に feasible になったものを採用
    //   level 0: 全制約
    //   level 1: 同マーク連続を外す
    //   level 2: + 後続禁止を外す
    //   level 3: + 後続チェーンを外す
    //   level 4: + 連勤上限を外す（最後の砦は固定人数 + 月公休 + 職種・スキル）
    //   level 5: + 月公休も外す（本当の最終手段）
    const levels = [
      { name: '全制約', opts: {} },
      { name: '同マーク連続 OFF', opts: { useSameConsec: false } },
      { name: '同マーク連続+後続禁止 OFF', opts: { useSameConsec: false, useForbidden: false } },
      { name: '同マーク連続+後続禁止+チェーン OFF', opts: { useSameConsec: false, useForbidden: false, useChain: false } },
      { name: '連勤も OFF', opts: { useSameConsec: false, useForbidden: false, useChain: false, useConsec: false } },
      { name: '月公休も OFF (最終)', opts: { useSameConsec: false, useForbidden: false, useChain: false, useConsec: false, useMonthlyOff: false } },
    ];

    for (let i = 0; i < levels.length; i++) {
      const { name, opts: ilpOpts } = levels[i];
      // 毎回スナップショットから再開
      state.matrix = { ...ilpSnapshot.matrix };
      console.log(`🧮 Stage4(ILP) Level ${i} [${name}] を試行`);
      let r;
      try {
        r = runILPRepair(state, ilpOpts);
      } catch (e) {
        console.warn(`🧮 Level ${i} 例外:`, e?.message);
        continue;
      }
      if (!r || !r.feasible) continue;
      // 適用後ルール再修復
      runSequentialRepair(state);
      runTimeAxisSwapRepair(state);
      const after = scoreOf2(state);
      const beforeTotal = ilpBefore.fixed * 10 + ilpBefore.consec * 100;
      const afterTotal = after.fixed * 10 + after.consec * 100;
      if (afterTotal < beforeTotal) {
        console.log(
          `✅ Stage4(ILP) Level ${i} [${name}] 採用: ` +
          `連勤 ${ilpBefore.consec}→${after.consec} / 固定不足 ${ilpBefore.fixed}→${after.fixed} ` +
          `(score ${beforeTotal}→${afterTotal})`
        );
        bestApplied = true;
        break;
      } else {
        console.log(`🛡️ Level ${i}: 解は得たが改善なし (${beforeTotal}→${afterTotal}) → 次レベル`);
      }
    }

    if (!bestApplied) {
      state.matrix = { ...ilpSnapshot.matrix };
      console.warn(`🧮 Stage4(ILP): 全レベルで改善できず → 撤回`);
    }
  }

  // 最後にチェーン整合（1 回だけ）
  runChainConsistencyCheck(state);

  // 🔥 修復完了後の最終ステップ: monthlyMin 未達スタッフに OFF を補充
  //   preScatterOff を縮小したぶん、ここで「影響少ない位置」だけに OFF を追加。
  //   固定不足を悪化させない場所のみ選ぶので、精度を壊さない。
  runEnsureMonthlyMinOff(state);

  const finalScore = scoreOf2(state);
  const finalElapsed = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - repairStartTime;
  console.log(`🏆 段階的修復完了: 残違反 連勤=${finalScore.consec} 固定不足=${finalScore.fixed} (${Math.round(finalElapsed / 1000)}s)`);
  onProgress(94);

  // Phase 8: 検証
  runValidation(state);
  onProgress(95);

  // Phase 9: 翌月書込
  await flushCrossMonthWrites(state);
  onProgress(100);

  console.log("📊 stats:", state.stats);
  console.log("📊 conflicts summary:", {
    hopeOverflow: state.conflicts.hopeOverflow.length,
    fixedShortage: state.conflicts.fixedShortage.length,
    flexShortage: state.conflicts.flexShortage.length,
    consecutive: state.conflicts.consecutive.length,
    offIssues: Object.keys(state.conflicts.off).length,
  });
  // 連勤違反のサンプル詳細（最大 10 件）
  if (state.conflicts.consecutive.length > 0) {
    console.group(`⚠️ 連勤違反 ${state.conflicts.consecutive.length} 件（max=${state.policy.maxConsecutiveWorkDays}日）`);
    state.conflicts.consecutive.slice(0, 10).forEach((c) => {
      // 該当スタッフの該当期間の matrix を取り出して表示
      const startDate = dayjs(c.start);
      const endDate = dayjs(c.end);
      const cells = [];
      let d = startDate.subtract(1, 'day');
      const lastTs = endDate.add(1, 'day').valueOf();
      while (d.valueOf() <= lastTs) {
        const ds = d.format('YYYY-MM-DD');
        const v = state.matrix[keyOf(c.empId, ds)] || (state.priorMatrix?.[keyOf(c.empId, ds)] || '?');
        cells.push(`${ds.slice(5)}:${v}`);
        d = d.add(1, 'day');
      }
      console.log(`  ${c.empId} ${c.start}〜${c.end} (${c.length}連勤): ${cells.join(' | ')}`);
    });
    console.groupEnd();
  }
  if (state.conflicts.hopeOverflow.length > 0) {
    console.warn("⚠️ 希望が固定人数を超えている日:");
    state.conflicts.hopeOverflow.slice(0, 5).forEach((c) => {
      console.warn(`  - ${c.date} ${c.patternName}(${c.mark}): 希望${c.frozenCount} > 必要${c.required}`);
    });
  }
  // ❗ 各 fixedShortage に「希望調整候補スタッフ」を付与
  //   不足日に休み系希望（off / paid / half / summer 等）を出していて、
  //   そのパターンの target_job_types にマッチするスタッフを抽出。
  //   ユーザーが「誰の希望を調整すべきか」一目で分かるリストになる。
  (() => {
    const staffByEmp = {};
    (staffList || []).forEach((s) => {
      staffByEmp[String(s.employeeId)] = s;
    });

    const restHopeLabel = (rawHope) => {
      if (!rawHope) return null;
      const v = String(rawHope).trim();
      const lower = v.toLowerCase();
      if (lower === 'off' || v === 'OFF' || v === '休') return '休み希望';
      // request_group / requestGroupLabel に一致する shift_patterns を探す
      const matched = state.patterns.find((p) => {
        const rg = p.requestGroup;
        const rgl = p.requestGroupLabel;
        return rg === v || rg === lower || rgl === v || rgl === lower;
      });
      if (!matched) return null;
      // 日勤・夜勤系は出勤を伴うため「調整対象」から除外。それ以外（有給/半休/夏休み等）は休み系
      const isDayShift =
        matched.assignmentModeEnabled === true ||
        matched.requestGroup === 'day' ||
        (matched.name?.includes('日勤') && !matched.name?.includes('夜勤'));
      const isNight =
        matched.requestGroup === 'night' ||
        matched.name?.includes('夜勤');
      if (isDayShift || isNight) return null;
      return matched.requestGroupLabel || `${matched.name || v}希望`;
    };

    state.conflicts.fixedShortage.forEach((sh) => {
      const pattern = state.patternByMark[sh.mark];
      const targetJobs = pattern?.targetJobTypes || [];
      const adjusters = [];

      // (1) 休み系の希望を出している人（既存）
      Object.entries(allHopes || {}).forEach(([key, rawValue]) => {
        const m = key.match(/^(.+)_(\d{4}-\d{2}-\d{2})$/);
        if (!m) return;
        const empId = m[1];
        const date = m[2];
        if (date !== sh.date) return;
        const rawHope = extractMark(rawValue);
        const hopeLabel = restHopeLabel(rawHope);
        if (!hopeLabel) return;
        const staff = staffByEmp[String(empId)];
        if (!staff) return;
        const jobId = String(staff.job_type_id || staff.jobTypeId || '');
        if (targetJobs.length > 0 && jobId && !targetJobs.includes(jobId)) return;
        const name = `${staff.lastName || staff.last_name || ''} ${staff.firstName || staff.first_name || ''}`.trim() || empId;
        adjusters.push({ empId, name, hopeType: rawHope, hopeLabel, category: 'hope' });
      });

      // 注: 「希望じゃない休みの人」は runTimeAxisSwapRepair が修復済み。
      //     ここで UI に出すと「直せるのに出してる」状態になるので追加しない。
      //     残る fixedShortage は本当に物理的に詰んでるケースのみ。

      // 同一スタッフが重複しないように emp で uniq
      const seen = new Set();
      sh.hopeAdjusters = adjusters.filter((a) => {
        if (seen.has(a.empId)) return false;
        seen.add(a.empId);
        return true;
      });
    });

    if (state.conflicts.fixedShortage.length > 0) {
      console.group('🛠 固定不足の希望調整候補リスト');
      state.conflicts.fixedShortage.forEach((sh) => {
        if (sh.hopeAdjusters?.length > 0) {
          console.log(
            `  ${sh.date} ${sh.patternName}(${sh.mark}) ${sh.have}/${sh.required}人 — 調整候補 ${sh.hopeAdjusters.length}名:`,
            sh.hopeAdjusters.map((a) => `${a.name}(${a.hopeLabel})`).join(', ')
          );
        } else {
          console.log(
            `  ${sh.date} ${sh.patternName}(${sh.mark}) ${sh.have}/${sh.required}人 — 調整候補なし（休み系希望者がいないため、必要人数の見直しが必要）`
          );
        }
      });
      console.groupEnd();
    }
  })();

  // ❗ 完成判定：固定人数が 100% 満たされていなければ「未完」
  const isComplete = state.conflicts.fixedShortage.length === 0;
  state.stats.isComplete = isComplete;
  state.stats.fixedShortageCount = state.conflicts.fixedShortage.length;
  state.conflicts.isComplete = isComplete;
  state.conflicts.fixedShortageCount = state.conflicts.fixedShortage.length;

  if (state.conflicts.fixedShortage.length > 0) {
    console.error(
      `❌ シフト作成は未完了：固定人数が ${state.conflicts.fixedShortage.length} 件未達`
    );
    state.conflicts.fixedShortage.forEach((c) => {
      console.error(
        `   - ${c.date} ${c.patternName}(${c.mark}): ${c.have}/${c.required}人`,
        c.reasons || ''
      );
    });
  } else {
    console.log("✅ シフト作成完了：固定人数 100% 達成");
  }

  const endMs = (typeof performance !== 'undefined' ? performance.now() : Date.now());
  const elapsedMs = Math.round(endMs - startMs);
  console.log(
    `🏁 自動シフト作成 終了 [version=${SHIFT_AUTO_ASSIGN_VERSION}, runId=${runId}, 実行時間=${elapsedMs}ms]`
  );

  return {
    matrix: state.matrix,
    conflicts: state.conflicts,
    stats: state.stats,
    _meta: { version: SHIFT_AUTO_ASSIGN_VERSION, runId, elapsedMs },
  };
};
